<template>
  <form
    class="bb-artefact-card"
    @submit.prevent="onSubmit"
  >
    <!-- ------------ -->
    <!--   DIALOGS    -->
    <!-- ------------ -->
    <confirm-dialog ref="confirmDialog"></confirm-dialog>
    <!-- ----------------- -->
    <!-- TOP ROW (HEADER)  -->
    <!-- ----------------- -->
    <div class="row h-40">
      <p class="bb-page-title mt-3 mb-1">ARTEFACT DETAILS</p>
    </div>
    <!-- -------------------------------------------------------------->
    <!-- MAIN CONTAINER -->
    <!-- -------------- -->
    <div
      v-if="isReady"
      class="row m-2 mt-0 bb-artefact-card__body"
    >
      <!-- ------------------------------------------------------------>
      <!--    LEFT COL (BUTTONS)   -->
      <!-- ----------------------- -->
      <div class="col-3">
        <!-- ---------- -->
        <!-- BUTTONS    -->
        <!-- ---------- -->
        <div class="row mt-4">
          <div
            class="btn-group-vertical btn-group-lg"
            role="group"
          >
            <!-- ------------ -->
            <!--    cancel    -->
            <!-- ------------ -->
            <button
              class="btn btn-lg btn-warning"
              type="button"
              @click="manageCancel()"
            >
              Cancel
            </button>
            <button
              v-if="!isNewArtefact && !this.delete"
              class="btn btn-primary btn-lg"
              type="button"
              @click="confirmDeletion()"
            >
              Delete
            </button>
            <!-- ------------ -->
            <!--     add      -->
            <!-- ------------ -->
            <button
              v-if="isNewArtefact && hasArtefactName"
              :disabled="artefactTypeMissingForNew"
              class="btn btn-primary btn-lg"
              type="submit"
            >
              Add
            </button>
            <!-- ------------ -->
            <!--      ok      -->
            <!-- ------------ -->
            <button
              v-if="!isNewArtefact"
              :disabled="!isChanged || isError"
              class="btn btn-primary btn-lg"
              type="submit"
            >
              OK
            </button>
            <button
              v-if="documentViewerUrl"
              class="btn btn-primary btn-lg"
              type="button"
              @click="openDocument()"
            >
              Document
            </button>
          </div>
        </div>
      </div>
      <!-- ------------------------------------------------------------>
      <!--    RIGHT COL (DATA)   -->
      <!-- --------------------- -->
      <div class="col m-3 bb-tooltip-wrapper">
        <!-- ---------- -->
        <!--    type    -->
        <!-- ---------- -->
        <p class="bb-hdg-lg mb-3 mt-2 no-wrap">{{ artefactHeading }}</p>
        <!-- ------------ -->
        <!--    divider   -->
        <!-- ------------ -->
        <hr
          v-if="hasArtefactName"
          class="mt-1 mb-3 bb-horizontal-divider"
        />
        <!-- -------------------- -->
        <!--   DATA TAB NAV       -->
        <!-- -------------------- -->
        <div v-if="hasArtefactName">
          <ul class="nav nav-pills mb-3 bb-item-tabs">
            <li class="nav-item">
              <button
                type="button"
                class="nav-link me-1"
                :class="{ active: activeTab === 'artefact' }"
                @click="setActiveTab('artefact')"
              >
                Basic
              </button>
            </li>
            <li class="nav-item">
              <button
                type="button"
                class="nav-link me-1"
                :class="{ active: activeTab === 'descriptions' }"
                @click="setActiveTab('descriptions')"
              >
                Descriptions
              </button>
            </li>
            <li class="nav-item">
              <button
                type="button"
                class="nav-link me-1"
                :class="{ active: activeTab === 'switches' }"
                @click="setActiveTab('switches')"
              >
                Switches
              </button>
            </li>
            <li
              class="nav-item"
              v-if="showCountTab"
            >
              <button
                type="button"
                class="nav-link me-1"
                :class="{ active: activeTab === 'count' }"
                @click="setActiveTab('count')"
              >
                Quantities
              </button>
            </li>
            <li
              class="nav-item"
              v-if="showArmourTab"
            >
              <button
                type="button"
                class="nav-link me-1"
                :class="{ active: activeTab === 'armour' }"
                @click="setActiveTab('armour')"
              >
                Armour
              </button>
            </li>
            <li
              class="nav-item"
              v-if="showWeaponTab"
            >
              <button
                type="button"
                class="nav-link me-1"
                :class="{ active: activeTab === 'weapon' }"
                @click="setActiveTab('weapon')"
              >
                Weapon
              </button>
            </li>
            <li
              class="nav-item"
              v-if="showHealingTab"
            >
              <button
                type="button"
                class="nav-link me-1"
                :class="{ active: activeTab === 'healing' }"
                @click="setActiveTab('healing')"
              >
                Healing
              </button>
            </li>
            <li
              class="nav-item"
              v-if="showPoisonTab"
            >
              <button
                type="button"
                class="nav-link me-1"
                :class="{ active: activeTab === 'poison' }"
                @click="setActiveTab('poison')"
              >
                Poison
              </button>
            </li>
            <li
              class="nav-item"
              v-if="showSpecialistTab"
            >
              <button
                type="button"
                class="nav-link me-1"
                :class="{ active: activeTab === 'specialist' }"
                @click="setActiveTab('specialist')"
              >
                Specialist
              </button>
            </li>
            <li
              class="nav-item"
              v-if="showAdjsTab"
            >
              <button
                type="button"
                class="nav-link me-1"
                :class="{ active: activeTab === 'adjs' }"
                @click="setActiveTab('adjs')"
              >
                Adjs
              </button>
            </li>
            <li
              class="nav-item"
              v-if="showMagicTab"
            >
              <button
                type="button"
                class="nav-link me-1"
                :class="{ active: activeTab === 'magic' }"
                @click="setActiveTab('magic')"
              >
                Magic
              </button>
            </li>
            <li
              class="nav-item"
              v-if="showArtefactTraitsTab"
            >
              <button
                type="button"
                class="nav-link me-1"
                :class="{ active: activeTab === 'traits' }"
                @click="setActiveTab('traits')"
              >
                Traits
              </button>
            </li>
            <li
              class="nav-item"
              v-if="showImageTab"
            >
              <button
                type="button"
                class="nav-link me-1"
                :class="{ active: activeTab === 'image' }"
                @click="setActiveTab('image')"
              >
                Image
              </button>
            </li>
          </ul>
        </div>
        <!-- ------------ -->
        <!--    divider   -->
        <!-- ------------ -->
        <hr
          v-if="hasArtefactName"
          class="mt-3 mb-1"
          style="border-top: 1.5px solid grey; opacity: 0.75"
        />

        <!-- ======================= -->
        <!-- TAB 2: ARTEFACT         -->
        <!-- ======================= -->
        <ItemArtefactTab
          v-if="activeTab === 'artefact'"
          :active-item="activeArtefact"
          :local-item="local_artefact"
          :details-heading="artefactDetailsHeading"
          :force-campaign-layout="true"
          :show-campaign-ref="
            !isNewArtefact && (activeArtefact?.id ?? artefact?.id ?? 0) !== 0
          "
          :campaign-ref-value="
            activeArtefact?.campaign_ref ?? artefact?.campaign_ref ?? ''
          "
          :artefact-types="filteredArtefactTypes"
          :artefact-base-items="artefactBaseItemsForSelection"
          :item-sizes="item_sizes"
          :container-sizes="container_sizes"
          :item-name="artefactName"
          :materials="materials"
          :qualities="qualities"
          :ages="ages"
          :races="races"
          :currencies="currencies"
          :error-fields="errorFields"
          :display-value-lower="displayValueLower"
          :display-value-upper="displayValueUpper"
          :readonly-race-name="readonlyRaceName"
          :got-value="gotValue"
          :is-editable="isEditable"
          :is-new-item="isNewArtefact"
          :on-key-down="onKeyDown"
          :clear-input-error="clearInputError"
          :on-artefact-field-change="onArtefactFieldChange"
          :on-name-input="onNameInput"
          :on-description-input="onDescriptionInput"
          :on-notes-input="onNotesInput"
          :get-artefact-base-item-label="getArtefactBaseItemLabel"
          :get-item-size-label="getItemSizeLabel"
          :get-container-size-label="getContainerSizeLabel"
          :get-race-label="getRaceLabel"
          :get-currency-label="getCurrencyLabel"
          :reduce-object="reduceObject"
          :handle-numeric-input="handleNumericInput"
          :on-estimated-value-toggle="onEstimatedValueToggle"
          :delayed-emit-update="delayedEmitUpdate"
          :on-capitalise-first-letter="onCapitaliseFirstLetter"
        />

        <ItemDescriptionTab
          v-if="activeTab === 'descriptions'"
          :active-item="activeArtefact"
          :error-fields="errorFields"
          :on-capitalise-first-letter="onCapitaliseFirstLetter"
          :on-description-input="onDescriptionInput"
          :on-dm-notes-input="onDmNotesInput"
        />
        <!-- ------------------------------------ -->
        <!--   WHERE ITEM NAME PRESENT/SELECTED   -->
        <!-- ------------------------------------ -->
        <div v-if="hasArtefactName">
          <!-- ====================== -->
          <!-- TAB 3 : COUNT         -->
          <!-- ====================== -->
          <ArtefactQuantityTab
            v-if="activeTab === 'count' && showCountTab"
            :active-item="activeArtefact"
            :local-item="local_artefact"
            :tally-qty-min="1"
            :force-enc-editable="true"
            :force-enc-two-dp="true"
            :units="units"
            :error-fields="errorFields"
            :format-enc="formatEnc"
            :calc-qty-tot-enc="calcQtyTotEnc"
            :on-counter-changed="onCounterChanged"
            :clear-input-error="clearInputError"
            :on-enc-input="onEncInput"
            :on-enc-blur="onEncBlur"
            :on-key-down="onKeyDown"
            :on-unit-select="onUnitSelect"
            :is-new-item="isNewArtefact"
          />

          <!-- ======================= -->
          <!-- TAB 6: ARMOUR           -->
          <!-- ======================= -->
          <ArtefactArmourTab
            v-if="activeTab === 'armour' && showArmourTab"
            :show-worn-switch="false"
            :show-state-field="false"
            :show-pwr-storage="false"
            :active-item="activeArtefact"
            :local-item="local_artefact"
            :armour-locations="armour_locations"
            :error-fields="errorFields"
            :max-ap="safeMaxAp"
            :max-hp="safeMaxHp"
            :compute-item-state="computeArtefactstate"
            :dex-pen-display="armourDexPenDisplay"
            :reduce-object="reduceObject"
            :on-key-down="onKeyDown"
            :on-armour-location-select="onArmourLocationSelect"
            :on-equipped-toggle="onEquippedToggle"
            :on-counter-changed="onCounterChanged"
            :on-artefact-field-change="onArtefactFieldChange"
          />

          <!-- ======================= -->
          <!-- TAB 7: WEAPON           -->
          <!-- ======================= -->
          <ArtefactWeaponTab
            v-if="activeTab === 'weapon' && showWeaponTab"
            :show-worn-switch="false"
            :show-state-field="false"
            :show-character-stats="false"
            :show-pwr-storage="false"
            :active-item="activeArtefact"
            :local-item="local_artefact"
            :effective-item-type="resolvedItemType"
            :worn-locations="[]"
            :error-fields="errorFields"
            :max-ap="safeMaxAp"
            :max-hp="safeMaxHp"
            :is-weapon-or-armour="isWeaponOrArmour"
            :compute-item-state="computeArtefactstate"
            :character="null"
            :weapon-skill-rows="weaponSkillRows"
            :reduce-object="reduceObject"
            :on-key-down="onKeyDown"
            :on-worn-location-select="onWornLocationSelect"
            :on-counter-changed="onCounterChanged"
            :on-artefact-field-change="onArtefactFieldChange"
            :clear-input-error="clearInputError"
            :on-worn-toggle="onWornToggle"
            :on-weapon-skill-damage-dice-change="onWeaponSkillDamageDiceChange"
          />

          <!-- ======================= -->
          <!-- TAB 8: HEALING          -->
          <!-- ======================= -->
          <ArtefactHealingTab
            v-if="activeTab === 'healing' && showHealingTab"
            :active-item="activeArtefact"
            :local-item="local_artefact"
            :is-new-item="true"
            :healing-types="healing_types"
            :error-fields="errorFields"
            :on-field-changed="onHealingFieldChanged"
          />

          <ArtefactPoisonTab
            v-if="activeTab === 'poison' && showPoisonTab"
            :active-item="activeArtefact"
            :local-item="local_artefact"
            :poison-types="poison_types"
            :error-fields="errorFields"
            :on-field-changed="onHealingFieldChanged"
          />

          <ArtefactSpecialistTab
            v-if="activeTab === 'specialist' && showSpecialistTab"
            :active-item="activeArtefact"
            :local-item="local_artefact"
            :on-counter-changed="onCounterChanged"
            :show-cur-unit-count="false"
          />

          <ItemAdjustmentsTab
            v-if="activeTab === 'adjs' && showAdjsTab"
            :item_characteristic_adj_reasons="
              artefactItemCharacteristicAdjReasons
            "
            :item_ability_adj_reasons="artefactItemAbilityAdjReasons"
            :item_characteristic_skill_adj_reasons="
              artefactItemCharacteristicSkillAdjReasons
            "
            :item_skill_adj_reasons_applied="artefactItemSkillAdjReasonsApplied"
            :item_skill_adj_reasons_optional="
              artefactItemSkillAdjReasonsOptional
            "
          />

          <ItemMagicTab
            v-if="activeTab === 'magic' && showMagicTab"
            :has-item-name="hasArtefactName"
            :item_runes_integrated="artefactItemRunesIntegrated"
            :item_runes_unintegrated="artefactItemRunesUnintegrated"
            :item_spells_rune="artefactItemSpellsRune"
            :item_spells_sorcery="artefactItemSpellsSorcery"
            :item_spells_divine="artefactItemSpellsDivine"
          />

          <!-- ========================= -->
          <!-- TAB X: TRAITS             -->
          <!-- ========================= -->
          <ArtefactTraitsTab
            v-if="activeTab === 'traits' && showArtefactTraitsTab"
            :traits="artefactTraitRows"
            :all-traits="traits"
            :selected-trait-id="selectedArtefactTraitId"
            :allow-trait-selection="canSelectArtefactTrait"
            :on-trait-select="onTraitSelect"
          />

          <!-- ========================= -->
          <!-- TAB 12: IMAGE             -->
          <!-- ========================= -->
          <ArtefactImageTab
            v-if="activeTab === 'image' && showImageTab"
            :has-item-name="hasArtefactName"
            :image="image"
            :show-change-image="showChangeImageLink"
            :show-clear-image="showClearImageLink"
            :on-preview-image="previewImage"
            :on-clear-image="clearImage"
          />

          <!-- ========================= -->
          <!-- TAB: SWITCHES             -->
          <!-- ========================= -->
          <ArtefactSwitchesTab
            v-if="activeTab === 'switches'"
            :active-item="activeArtefact"
            :local-item="local_artefact"
            :on-flag-change="onArtefactFieldChange"
            :disable-active="!!activeArtefact?.character_name"
          />
          <div
            v-if="activeTab === 'switches' && showInactiveStatusDropdown"
            class="row mt-2 mb-2"
          >
            <div class="col-12">
              <p class="bb-hdg mt-2 mb-3">Status</p>
            </div>
            <div class="col-8 form-group">
              <div class="form-floating">
                <v-select
                  :options="inactiveStatusDropdownOptions"
                  class="form-control"
                  placeholder="select..."
                  :modelValue="selectedInactiveStatus"
                  :disabled="!!activeArtefact?.character_name"
                  @keydown="onKeyDown($event)"
                  :class="{ 'is-invalid': errorFields.includes('status') }"
                  @click="clearInputError('status')"
                  @update:modelValue="onArtefactFieldChange('status', $event)"
                  id="status"
                >
                  <template #search="{ attributes, events }">
                    <input
                      class="vs__search"
                      v-bind="attributes"
                      v-on="events"
                    />
                  </template>
                </v-select>
                <label
                  class="bb-float-label"
                  for="status"
                  >Status</label
                >
              </div>
            </div>
          </div>
        </div>
        <!-- ============ -->
        <!--    ERRORS    -->
        <!-- ============ -->
        <div v-if="errors.length > 0">
          <div class="row mt-2 mb-3">
            <div class="form-group">
              <div class="row">
                <div class="col text-end me-3">
                  <div
                    v-for="err in errors"
                    :key="err"
                    class="bb-error-wrap"
                    style="white-space: pre-line"
                  >
                    <div class="row mb-3">
                      {{ err }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import ConfirmDialog from './ConfirmDialog.vue'
import ItemArtefactTab from './ItemArtefactTab.vue'
import ArtefactQuantityTab from './ArtefactQuantityTab.vue'
import ArtefactArmourTab from './ArtefactArmourTab.vue'
import ArtefactWeaponTab from './ArtefactWeaponTab.vue'
import ArtefactHealingTab from './ArtefactHealingTab.vue'
import ArtefactPoisonTab from './ArtefactPoisonTab.vue'
import ArtefactSpecialistTab from './ArtefactSpecialistTab.vue'
import ArtefactTraitsTab from './ArtefactTraitsTab.vue'
import ArtefactImageTab from './ArtefactImageTab.vue'
import ItemDescriptionTab from './ItemDescriptionTab.vue'
import ArtefactSwitchesTab from './ArtefactSwitchesTab.vue'
import ItemAdjustmentsTab from './ItemAdjustmentsTab.vue'
import ItemMagicTab from './ItemMagicTab.vue'

import defaultImage from '../assets/images/default-item-image.png'

import calcContainerAvailability from '../helpers/calcContainerAvailability'
import getStringAfterLastSlash from '../helpers/getStringAfterLastSlash'
import capitaliseFirstLetter from '../helpers/capitaliseFirstLetter'
import normaliseLeadingLetterText from '../helpers/normaliseLeadingLetterText'
import sanitisePositiveInt from '../helpers/sanitisePositiveInt'
import isObjectChanged from '../helpers/isObjectChanged'
import copyObjectNoRef from '../helpers/copyObjectNoRef'
import isAlphanumeric from '../helpers/isAlphanumeric'
import setItemValues from '../helpers/setItemValues'
import createTempId from '../helpers/createTempId'
import isEmpty from '../helpers/isEmpty'
import buildAdjReasonsString from '../helpers/buildAdjReasonsString'
import buildRunesString from '../helpers/buildRunesString'
import {
  getEffectiveItemType,
  getItemPropertySource,
  getNormalisedEquipmentType,
} from '../helpers/getEffectiveItemType'

import { evaluateWeaponUsability } from '../helpers/weaponUsabilityCore'

import { inject } from 'vue'

export default {
  name: 'ArtefactCard',
  emits: [
    'add-campaign-artefact',
    'update-campaign-artefact',
    'delete-campaign-artefact',
    'close-artefact-panel',
    'close-campaign-artefact-panel',
  ],
  //========================================================
  // components
  //------------
  components: {
    ConfirmDialog,
    ItemArtefactTab,
    ArtefactQuantityTab,
    ArtefactArmourTab,
    ArtefactWeaponTab,
    ArtefactHealingTab,
    ArtefactPoisonTab,
    ArtefactSpecialistTab,
    ArtefactTraitsTab,
    ArtefactImageTab,
    ItemDescriptionTab,
    ArtefactSwitchesTab,
    ItemAdjustmentsTab,
    ItemMagicTab,
  },
  //========================================================
  // setup
  //-------
  setup() {
    const global = inject('global')
    return { global }
  },
  //========================================================
  //-------
  props: {
    artefact: {
      type: Object,
      default: null,
    },
    artefactNames: {
      type: Array,
      default: () => [],
    },
  },
  //========================================================
  // data
  //------
  data() {
    return {
      defaultImage,
      original_artefact: {
        tally_qty: 1,
        dm_notes: '',
        short_description: '',
        container: null,
        worn_location: null,
        attach_location: null,
        armour_location: null,
        default_currency: null,
        ap: 0,
        hp: 0,
        enc: 0.01,
        worn: false,
        held: false,
        equipped: false,
        save_item_id: null,
        runes: [],
        spells: [],
        max_unit_count: null,
        value_lower: null,
        value_upper: null,
        estimated_value: false,
        artefact_type: '',
        artefact_base_item: null,
        trait: null,
        material: '',
        quality: '',
        age: '',
        pwr_storage: 0,
        image: null,
        artefact: null,
        currency: null,
        item_size: null,
        container_size: null,
        container_capacity: null,
        container_size_short: '',
        container_size_long: '',
        status: 'Active',
      },
      local_artefact: {},
      save_item_id: null,
      // last valid container selection (for reverting v-select when
      // capacity validation fails)
      previousContainerId: null,
      pendingTimers: {}, // per-field debounce timers (key -> timeout id)
      submitCommitted: false,
      //
      errors: [],
      errorFields: [],
      lookupsLoaded: false,
      artefacts: [],
      item_names: [],
      containers: [],
      artefact_types: [],
      artefact_type_records: [],
      item_sizes: [],
      container_sizes: [],
      traits: [],
      qualities: [],
      ages: [],
      races: [],
      currencies: [],
      materials: [],
      statuses: [],
      units: [],
      poison_types: [],
      healing_types: [],
      activeTab: 'artefact',

      item_runes_integrated: '',
      item_runes_unintegrated: '',
      item_spells_sorcery: '',
      item_spells_divine: '',
      //
      delete: false,
      // baseline armour stats for dex-pen recalculation
      armour_base_ap: 0,
      armour_base_dex_pen: 0,
      armour_locations: [],
      max_hp: 0,
      max_ap: 0,
    }
  },
  //==========================================================
  // watch
  //-------
  watch: {
    //========================================================
    // item: {
    //   handler() {
    //   },
    //   immediate: true,
    // },
    //========================================================
    // character: {
    //   handler() {
    //     console.log('=====================================')
    //     console.log('WATCH  IN ITEM CARD....')
    //     //------------------------------------------------------------------------------------------
    //     // get drop down values (artefacts, containers, worn_locations (item/armour), attach_locations)
    //     //------------------------------------------------------------------------------------------
    //     this.getDropDownValues()
    //   },
    //   immediate: true,
    // },
    //========================================================
    artefactNameParts: {
      immediate: true,
      handler(newParts) {
        const banned = ['unknown', 'other', 'n/a']

        // For EXISTING artefacts, require a valid artefact_type
        // (selected and not banned) before auto-constructing a
        // name. For NEW artefacts, allow the name to be built
        // from whichever parts are currently set so that clearing
        // artefact_type (or any other part) immediately updates
        // the heading.
        const rawType = (this.local_artefact?.artefact_type || '').trim()
        const isExisting = !this.isNewArtefact
        if (
          isExisting &&
          (!rawType || banned.includes(rawType.toLowerCase()))
        ) {
          return
        }

        const parts = (newParts || [])
          .map((v) => (v || '').trim())
          .filter((v) => v && !banned.includes(v.toLowerCase()))

        if (!parts.length) {
          // For new artefacts, when all parts are cleared out,
          // reset the working name so the heading falls back
          // to the base type label (e.g. just "Artefact").
          if (this.isNewArtefact) {
            this.local_artefact.name = ''
          }
          return
        }

        const name = parts.join(' ')

        // NEW artefacts: always auto-construct the name from parts
        if (this.isNewArtefact) {
          const uniqueName = this.getUniqueArtefactName(name)
          this.local_artefact.name = uniqueName
          return
        }

        // EXISTING artefacts: leave the name as-is.
      },
    },

    //========================================================
    showCountTab: {
      immediate: true,
      handler(newVal) {
        if (!newVal && this.activeTab === 'count') {
          this.activeTab = 'artefact'
        }
      },
    },
    artefactSubtypeForTabs: {
      immediate: true,
      handler() {
        const dependentTabs = [
          'count',
          'armour',
          'weapon',
          'healing',
          'poison',
          'specialist',
          'adjs',
          'magic',
          'traits',
          'image',
        ]

        if (
          dependentTabs.includes(this.activeTab) &&
          !this.isTabAvailable(this.activeTab)
        ) {
          this.activeTab = 'artefact'
        }
      },
    },
    hasArtefactTraits: {
      immediate: true,
      handler(newVal) {
        if (!newVal && this.activeTab === 'traits') {
          this.activeTab = 'artefact'
        }
      },
    },
    //========================================================
    'activeArtefact.container': {
      handler(newVal) {
        // console.log('------------------------------------------------------')
        console.log('WATCH 1. container: newVal = ', newVal)
        if (!this.activeArtefact) return

        // Any change of container invalidates the previous tally-vs-container
        // capacity check, so clear that error and let future tally/container
        // actions recalculate against the current location.
        this.clearInputError('tally_qty')
      },
    },
    'activeArtefact.active': {
      handler() {
        this.syncInactiveStatusWithActiveState()
      },
    },
  },
  computed: {
    item() {
      return this.artefact ?? null
    },
    item_type() {
      return (
        this.activeArtefact?.actual_type ||
        this.activeArtefact?.type ||
        this.effectiveStaticItemType ||
        'Artefact'
      )
    },
    itemType() {
      return this.item_type
    },
    // Optional base static item used only for derived/default values.
    // The edited artefact record itself remains the primary source of truth.
    baseStaticItem() {
      const staticItemId =
        this.local_artefact?.artefact_base_item ??
        this.activeArtefact?.artefact_base_item ??
        this.artefact?.artefact_base_item ??
        this.save_item_id ??
        null

      const getStaticItemById =
        this.$store.getters['characters/getStaticItemById']

      if (typeof getStaticItemById === 'function' && staticItemId) {
        const staticItem = getStaticItemById(staticItemId)
        if (staticItem) {
          return staticItem
        }
      }

      return null
    },
    activeArtefact() {
      const hasLocal =
        this.local_artefact && Object.keys(this.local_artefact).length > 0
      return hasLocal ? this.local_artefact : (this.item ?? {})
    },
    selectedInactiveStatus() {
      return this.getNormalisedInactiveStatusValue()
    },
    inactiveStatusDropdownOptions() {
      const values = Array.isArray(this.statuses) ? this.statuses : []
      const activeValue = this.getInactiveStatusNaValue()
      const destroyedValue = this.getDestroyedStatusValue()
      const isActive = this.toBooleanFlag(
        this.local_artefact?.active ?? this.activeArtefact?.active ?? true,
      )

      if (isActive) {
        return values.filter((status) => status === activeValue)
      }

      return values.filter(
        (status) =>
          status && status !== activeValue && status !== destroyedValue,
      )
    },
    showInactiveStatusDropdown() {
      if (this.activeArtefact?.character_name) {
        return false
      }
      return true
    },
    isNewArtefact() {
      const i = this.activeArtefact ?? null
      const id = i?.id ?? null
      return !this.activeArtefact || id === 0 || id == null
    },
    rowNumber() {
      return (this.activeArtefact?.originalIndex ?? 0) + 1
    },
    artefactName() {
      // Prefer the working name from local_artefact so that headings
      // immediately reflect unsaved edits (including auto-built
      // artefact names from quality/age/material/type parts).
      const localName = this.local_artefact?.name || ''
      if (localName) return localName

      const i = this.activeArtefact || {}
      return i.display_name || i.name || ''
    },
    artefactDetailsHeading() {
      return 'Artefact Details'
    },
    hasArtefactName() {
      const name = this.artefactName || ''
      return name !== ''
    },
    // ArtefactCard has no character context — these return empty arrays
    // so that weapon/armour computeds that reference character data degrade safely
    relatedSkills() {
      return []
    },
    relatedCharacteristics() {
      return []
    },
    resolvedItemType() {
      return (
        this.activeArtefact?.actual_type ||
        this.activeArtefact?.effective_type ||
        this.activeArtefact?.type ||
        this.effectiveStaticItemType ||
        'Artefact'
      )
    },
    artefactSubtypeForTabs() {
      if (this.isNewArtefact && this.artefactTypeMissingForNew) {
        return ''
      }

      return this.resolvedItemType
    },
    hasArtefactSubtypeSelection() {
      return this.artefactSubtypeForTabs !== ''
    },
    isArtefactItem() {
      return true
    },
    showCountTab() {
      return true
    },
    artefactTraitRows() {
      const traitSources = [
        this.activeArtefact?.item_traits,
        this.basePropertyItem?.item_traits,
      ]
      const rawTraits = traitSources.find(
        (traits) => Array.isArray(traits) && traits.length > 0,
      )

      if (!Array.isArray(rawTraits)) {
        return []
      }

      const seen = new Set()
      return rawTraits
        .filter((trait) => trait && (trait.name || trait.description))
        .filter((trait) => {
          const key = `${trait.id ?? ''}:${trait.name ?? ''}:${trait.description ?? ''}`
          if (seen.has(key)) {
            return false
          }
          seen.add(key)
          return true
        })
        .map((trait) => ({
          id: trait.id ?? null,
          name: trait.name ?? '',
          description: trait.description ?? '',
        }))
    },
    hasArtefactTraits() {
      return this.artefactTraitRows.length > 0
    },
    canSelectArtefactTrait() {
      return true
    },
    showArtefactTraitsTab() {
      return (
        this.hasArtefactSubtypeSelection &&
        (this.canSelectArtefactTrait || this.hasArtefactTraits)
      )
    },
    hasBaseItemSelected() {
      return !!(
        this.local_artefact?.artefact_base_item ||
        this.activeArtefact?.artefact_base_item
      )
    },
    showArmourTab() {
      return (
        this.artefactSubtypeForTabs === 'Armour' && this.hasBaseItemSelected
      )
    },
    showWeaponTab() {
      return (
        (this.artefactSubtypeForTabs === 'Melee Weapon' ||
          this.artefactSubtypeForTabs === 'Ranged Weapon') &&
        this.hasBaseItemSelected
      )
    },
    showHealingTab() {
      return this.artefactSubtypeForTabs === 'Healing'
    },
    showPoisonTab() {
      return this.artefactSubtypeForTabs === 'Poison'
    },
    showSpecialistTab() {
      return this.artefactSubtypeForTabs === 'Specialist'
    },
    hasLinkedCharacterOwner() {
      return !!(
        this.activeArtefact?.character_name || this.activeArtefact?.player_name
      )
    },
    hasArtefactAdjustmentData() {
      return !!(
        (Array.isArray(this.activeArtefact?.item_characteristic_adjustments) &&
          this.activeArtefact.item_characteristic_adjustments.length > 0) ||
        (Array.isArray(this.activeArtefact?.item_ability_adjustments) &&
          this.activeArtefact.item_ability_adjustments.length > 0) ||
        (Array.isArray(this.activeArtefact?.item_characteristic_skill_adjustments) &&
          this.activeArtefact.item_characteristic_skill_adjustments.length > 0) ||
        (Array.isArray(this.activeArtefact?.item_skill_adjustments) &&
          this.activeArtefact.item_skill_adjustments.length > 0)
      )
    },
    hasArtefactAdjustmentFields() {
      const sources = [this.activeArtefact, this.local_artefact, this.item]
      return sources.some(
        (source) =>
          source &&
          (
            Object.prototype.hasOwnProperty.call(
              source,
              'item_characteristic_adjustments',
            ) ||
            Object.prototype.hasOwnProperty.call(
              source,
              'item_ability_adjustments',
            ) ||
            Object.prototype.hasOwnProperty.call(
              source,
              'item_characteristic_skill_adjustments',
            ) ||
            Object.prototype.hasOwnProperty.call(
              source,
              'item_skill_adjustments',
            )
          ),
      )
    },
    hasArtefactMagicData() {
      return !!(
        (Array.isArray(this.activeArtefact?.runes) &&
          this.activeArtefact.runes.length > 0) ||
        (Array.isArray(this.activeArtefact?.non_rune_spells) &&
          this.activeArtefact.non_rune_spells.length > 0) ||
        (Array.isArray(this.activeArtefact?.spells) &&
          this.activeArtefact.spells.length > 0) ||
        Number(this.activeArtefact?.pwr_storage ?? 0) > 0
      )
    },
    hasArtefactMagicFields() {
      const sources = [this.activeArtefact, this.local_artefact, this.item]
      return sources.some(
        (source) =>
          source &&
          (
            Object.prototype.hasOwnProperty.call(source, 'runes') ||
            Object.prototype.hasOwnProperty.call(source, 'non_rune_spells') ||
            Object.prototype.hasOwnProperty.call(source, 'spells') ||
            Object.prototype.hasOwnProperty.call(source, 'pwr_storage')
          ),
      )
    },
    isRecoverableArtefactContext() {
      if (this.isNewArtefact) {
        return false
      }

      return !this.toBooleanFlag(
        this.activeArtefact?.active ?? this.local_artefact?.active ?? true,
      )
    },
    showAdjsTab() {
      return (
        this.hasLinkedCharacterOwner ||
        this.isRecoverableArtefactContext ||
        this.hasArtefactAdjustmentData ||
        this.hasArtefactAdjustmentFields
      )
    },
    showMagicTab() {
      return (
        this.hasLinkedCharacterOwner ||
        this.isRecoverableArtefactContext ||
        this.hasArtefactMagicData ||
        this.hasArtefactMagicFields
      )
    },
    showImageTab() {
      return this.hasArtefactSubtypeSelection
    },
    safeMaxAp() {
      const configuredMax = Number(this.max_ap ?? 0)
      const currentAp = Number(
        this.local_artefact?.ap ?? this.activeArtefact?.ap ?? 0,
      )

      return Math.max(
        Number.isFinite(configuredMax) ? configuredMax : 0,
        Number.isFinite(currentAp) ? currentAp : 0,
      )
    },
    safeMaxHp() {
      const configuredMax = Number(this.max_hp ?? 0)
      const currentHp = Number(
        this.local_artefact?.hp ?? this.activeArtefact?.hp ?? 0,
      )

      return Math.max(
        Number.isFinite(configuredMax) ? configuredMax : 0,
        Number.isFinite(currentHp) ? currentHp : 0,
      )
    },
    artefactItemCharacteristicAdjReasons() {
      return buildAdjReasonsString(
        Array.isArray(this.activeArtefact?.item_characteristic_adjustments)
          ? this.activeArtefact.item_characteristic_adjustments
          : [],
      )
    },
    artefactItemAbilityAdjReasons() {
      return buildAdjReasonsString(
        Array.isArray(this.activeArtefact?.item_ability_adjustments)
          ? this.activeArtefact.item_ability_adjustments
          : [],
      )
    },
    artefactItemCharacteristicSkillAdjReasons() {
      return buildAdjReasonsString(
        Array.isArray(
          this.activeArtefact?.item_characteristic_skill_adjustments,
        )
          ? this.activeArtefact.item_characteristic_skill_adjustments
          : [],
      )
    },
    artefactItemSkillAdjReasonsApplied() {
      return buildAdjReasonsString(
        Array.isArray(this.activeArtefact?.item_skill_adjustments)
          ? this.activeArtefact.item_skill_adjustments.filter(
              (adj) => adj?.applied == true,
            )
          : [],
      )
    },
    artefactItemSkillAdjReasonsOptional() {
      return buildAdjReasonsString(
        Array.isArray(this.activeArtefact?.item_skill_adjustments)
          ? this.activeArtefact.item_skill_adjustments.filter(
              (adj) => adj?.applied == false,
            )
          : [],
      )
    },
    artefactItemRunesIntegrated() {
      const runes = Array.isArray(this.activeArtefact?.runes)
        ? this.activeArtefact.runes
        : []
      return buildRunesString(
        runes
          .filter((rune) => this.toBooleanFlag(rune?.integrated))
          .map((rune) => ({
            ...rune,
            name: rune?.display_name || rune?.name || '',
          })),
      )
    },
    artefactItemRunesUnintegrated() {
      const runes = Array.isArray(this.activeArtefact?.runes)
        ? this.activeArtefact.runes
        : []
      return buildRunesString(
        runes
          .filter((rune) => !this.toBooleanFlag(rune?.integrated))
          .map((rune) => ({
            ...rune,
            name: rune?.display_name || rune?.name || '',
          })),
      )
    },
    artefactItemSpellsRune() {
      const runes = Array.isArray(this.activeArtefact?.runes)
        ? this.activeArtefact.runes
        : []
      const lines = runes
        .map((rune) => {
          const runeName = String(rune?.display_name || rune?.name || '').trim()
          const spells = Array.isArray(rune?.spells) ? rune.spells : []
          if (!runeName || spells.length === 0) {
            return ''
          }

          const spellText = spells
            .map((spell) => {
              const spellName = String(
                spell?.display_name || spell?.name || '',
              ).trim()
              if (!spellName) {
                return ''
              }

              const mag =
                spell?.mag !== undefined && spell?.mag !== null
                  ? ` (Mag ${spell.mag})`
                  : ''
              return `${spellName}${mag}`
            })
            .filter(Boolean)
            .join('. ')

          return spellText ? `${runeName}: ${spellText}` : ''
        })
        .filter(Boolean)

      return lines.join('\n')
    },
    artefactItemSpellsSorcery() {
      return this.buildArtefactSpellStringByType('Sorcery')
    },
    artefactItemSpellsDivine() {
      return this.buildArtefactSpellStringByType('Divine')
    },

    selectedArtefactTraitId() {
      const getTraitIdFromSource = (source) => {
        const sourceTraits = Array.isArray(source?.item_traits)
          ? source.item_traits
          : []

        if (sourceTraits.length > 0) {
          const firstTrait = sourceTraits[0]
          return typeof firstTrait === 'object'
            ? (firstTrait.id ?? null)
            : firstTrait
        }

        const rawTrait = source?.trait ?? null
        return rawTrait && typeof rawTrait === 'object'
          ? (rawTrait.id ?? null)
          : rawTrait
      }

      const candidateTrait =
        getTraitIdFromSource(this.local_artefact) ??
        getTraitIdFromSource(this.activeArtefact) ??
        getTraitIdFromSource(this.basePropertyItem) ??
        null

      if (candidateTrait) {
        return candidateTrait
      }

      if (this.canSelectArtefactTrait && this.artefactTraitRows.length === 1) {
        return this.artefactTraitRows[0]?.id ?? null
      }

      return null
    },
    image() {
      const currentArtefact = this.activeArtefact ?? null
      const currentId = currentArtefact?.id ?? null
      const isNewArtefactDraft =
        typeof this.isNewArtefact === 'boolean'
          ? this.isNewArtefact
          : !currentArtefact || currentId === 0 || currentId == null

      const candidateImages = isNewArtefactDraft
        ? [this.local_artefact?.image, this.activeArtefact?.image]
        : [
            this.local_artefact?.image,
            this.activeArtefact?.image,
            this.artefact?.image,
          ]

      const resolvedImage = candidateImages.find((candidateImage) => {
        if (typeof candidateImage === 'string') {
          return candidateImage.trim() !== ''
        }

        return candidateImage != null
      })

      return resolvedImage != null
        ? this.buildAbsoluteUrl(resolvedImage)
        : this.defaultImage
    },
    showChangeImageLink() {
      return true
    },
    showClearImageLink() {
      const artefactImage =
        this.local_artefact?.image ?? this.activeArtefact?.image ?? null

      return typeof artefactImage === 'string'
        ? artefactImage.trim() !== ''
        : !!artefactImage
    },
    readonlyRaceName() {
      const raw = this.activeArtefact?.race

      if (typeof raw === 'string' && raw && !raw.includes('-')) {
        return raw
      }

      if (raw && typeof raw === 'object' && raw.name) {
        return raw.name
      }

      const id = typeof raw === 'string' || typeof raw === 'number' ? raw : null
      const getRaceById = this.$store.getters['races/getRaceById']
      const raceObj =
        typeof getRaceById === 'function' && id ? getRaceById(id) : null

      return raceObj?.name || 'Unknown'
    },
    // display-only artefact name for read-only artefact view
    readonlyArtefactName() {
      const raw = this.activeArtefact?.artefact

      // If backend already sent a name string, use it directly
      if (typeof raw === 'string' && raw && !raw.includes('-')) {
        return raw
      }

      // If we have an object with a name, use that
      if (raw && typeof raw === 'object' && raw.name) {
        return raw.name
      }

      // Otherwise treat raw as an id and resolve via store
      const id = typeof raw === 'string' || typeof raw === 'number' ? raw : null
      const getArtefactById = this.$store.getters['artefacts/getArtefactById']
      const artefactObj =
        typeof getArtefactById === 'function' && id ? getArtefactById(id) : null

      return artefactObj?.name || 'Unknown'
    },
    artefactNameParts() {
      // order: quality, age, material, artefact type
      return [
        this.local_artefact.quality,
        this.local_artefact.age,
        this.local_artefact.material,
        this.local_artefact.artefact_type,
      ]
    },
    gotValue() {
      if (!this.activeArtefact) return false

      const rawLower = this.activeArtefact.value_lower
      const rawUpper = this.activeArtefact.value_upper

      const lowerNum =
        rawLower === '' || rawLower === null || rawLower === undefined
          ? null
          : Number(rawLower)
      const upperNum =
        rawUpper === '' || rawUpper === null || rawUpper === undefined
          ? null
          : Number(rawUpper)

      const hasLower =
        typeof lowerNum === 'number' &&
        !Number.isNaN(lowerNum) &&
        lowerNum !== 0
      const hasUpper =
        typeof upperNum === 'number' &&
        !Number.isNaN(upperNum) &&
        upperNum !== 0

      return hasLower || hasUpper
    },
    computeArtefactstate() {
      console.log(
        'computeArtefactstate: activeArtefact = ',
        this.activeArtefact,
      )
      const itemType = this.resolvedItemType

      if (
        !this.activeArtefact ||
        (itemType !== 'Melee Weapon' &&
          itemType !== 'Ranged Weapon' &&
          itemType !== 'Armour')
      ) {
        return
      }
      if (this.activeArtefact.broken) {
        return 'Broken'
      }
      return 'Functional'
    },
    armourDexPenDisplay() {
      const item = this.activeArtefact || {}
      const itemId = item.id ?? null
      const rawItemDexPen = this.local_artefact?.dex_pen ?? item.dex_pen ?? 0
      const itemDexPen = Number(rawItemDexPen)

      if (
        !item.equipped ||
        itemId == null ||
        !Array.isArray(this.relatedArmourLocations)
      ) {
        return Number.isNaN(itemDexPen) ? 0 : itemDexPen
      }

      let matchedTotal = 0
      let foundMatch = false

      this.relatedArmourLocations.forEach((loc) => {
        if (!loc || !Array.isArray(loc.equipped_armour)) return

        loc.equipped_armour.forEach((entry) => {
          if (!entry || entry.armour !== itemId) return

          const entryDexPen = Number(entry.dex_pen)
          if (!Number.isNaN(entryDexPen)) {
            matchedTotal += entryDexPen
            foundMatch = true
          }
        })
      })

      if (foundMatch) {
        return matchedTotal
      }

      return Number.isNaN(itemDexPen) ? 0 : itemDexPen
    },
    isWeaponOrArmour() {
      const itemType = this.resolvedItemType

      return (
        this.activeArtefact &&
        (itemType === 'Melee Weapon' ||
          itemType === 'Ranged Weapon' ||
          itemType === 'Armour')
      )
    },
    calcQtyTotEnc() {
      const encPerItem = parseFloat(this.activeArtefact.enc || 0)
      const qty = parseInt(this.activeArtefact.tally_qty || 0, 10)

      if (Number.isNaN(encPerItem) || Number.isNaN(qty)) {
        console.log(
          'calcQtyTotEnc: invalid inputs enc=',
          this.activeArtefact.enc,
          ' qty=',
          this.activeArtefact.tally_qty,
        )
        return 0
      }

      // multiply and round to 2 decimal places to avoid
      // floating point tails like 0.12999999999999998
      const rawTotal = encPerItem * qty
      const roundedTotal = Math.round((rawTotal + Number.EPSILON) * 100) / 100

      console.log('calcQtyTotEnc = ', this.formatEnc(roundedTotal))
      return roundedTotal
    },
    displayCapacityEnc() {
      return `How much enc this container can hold`
    },
    displayTotalCnt() {
      return `Count of ALL artefacts in this container (incl. nested artefacts)`
    },
    // displayAvailableEnc() {
    //   return `Container Capacity (${this.formatEnc(this.container.container_capacity)}) - Contents Enc (${this.formatEnc(this.overallContentsEnc)})`;
    // },
    displayTotEnc() {
      return `Container Enc (${this.formatEnc(this.activeArtefact?.enc)}) + Contents Enc (${this.formatEnc(this.activeArtefact?.contents_enc)})`
    },
    // generic numeric-display helpers (render 0/null as blank string)
    displayValueLower: {
      get() {
        return this.getDisplayNumericField('value_lower')
      },
      set(v) {
        this.setDisplayNumericField('value_lower', v)
      },
    },
    displayValueUpper: {
      get() {
        return this.getDisplayNumericField('value_upper')
      },
      set(v) {
        this.setDisplayNumericField('value_upper', v)
      },
    },
    itemHeading() {
      // If there is no effective item name (including the
      // auto-built artefact name), fall back to the base
      // type label with no trailing hyphen.
      if (isEmpty(this.artefactName)) {
        return 'Artefact'
      }
      return `${this.resolvedItemType || 'Artefact'} - ${this.artefactName}`
    },
    artefactHeading() {
      if (!this.hasArtefactSubtypeSelection) {
        return this.itemHeading
      }

      const inactiveStatus = this.getNormalisedInactiveStatusValue()
      const normalisedInactiveStatus =
        typeof inactiveStatus === 'string' ? inactiveStatus.trim() : ''
      const activeStatus = this.getInactiveStatusNaValue()
      const notApplicableStatus = this.getStatusNotApplicableValue()
      const isActive = this.toBooleanFlag(
        this.local_artefact?.active ?? this.activeArtefact?.active ?? true,
      )
      const isNotApplicableInactiveStatus =
        !!normalisedInactiveStatus &&
        typeof notApplicableStatus === 'string' &&
        normalisedInactiveStatus.toLowerCase() ===
          notApplicableStatus.trim().toLowerCase()

      if (
        isActive &&
        (!normalisedInactiveStatus || normalisedInactiveStatus === activeStatus)
      ) {
        return this.itemHeading
      }

      if (
        (!isActive && !normalisedInactiveStatus) ||
        (!isActive && isNotApplicableInactiveStatus) ||
        normalisedInactiveStatus === activeStatus
      ) {
        return `${this.itemHeading} (Inactive)`
      }

      return `${this.itemHeading} (${normalisedInactiveStatus})`
    },
    isReady() {
      return !isEmpty(this.activeArtefact) && !isEmpty(this.local_artefact)
    },
    documentViewerUrl() {
      return (
        this.activeArtefact?.reference_document_viewer_url ??
        this.activeArtefact?.reference_document_url ??
        this.activeArtefact?.reference_document ??
        this.basePropertyItem?.reference_document_viewer_url ??
        this.basePropertyItem?.reference_document_url ??
        this.basePropertyItem?.reference_document ??
        ''
      )
    },
    shortDescriptionReadonly() {
      const staticShortDescription =
        this.activeArtefact?.short_description ??
        this.basePropertyItem?.short_description
      return (
        typeof staticShortDescription === 'string' &&
        staticShortDescription.trim() !== ''
      )
    },
    shortDescriptionRequired() {
      const requiredTypes = ['Healing', 'Poison', 'Specialist']
      const candidateTypes = [
        this.activeArtefact?.actual_type,
        this.activeArtefact?.type,
        this.resolvedItemType,
      ]

      return candidateTypes.some((type) => requiredTypes.includes(type))
    },
    effectiveStaticItemType() {
      const getStaticItemById =
        this.$store.getters['characters/getStaticItemById']
      return getEffectiveItemType(this.baseStaticItem, getStaticItemById)
    },
    basePropertyItem() {
      const getStaticItemById =
        this.$store.getters['characters/getStaticItemById']
      return getItemPropertySource(this.baseStaticItem, getStaticItemById)
    },
    // total ENC for this item, using global one-decimal formatter
    encDisplay() {
      const tot_enc = this.activeArtefact?.tot_enc
      return this.formatEnc(tot_enc)
    },
    // weapon usability details derived from shared helper
    weaponUsabilityDetails() {
      const type = this.resolvedItemType

      if (type !== 'Melee Weapon' && type !== 'Ranged Weapon') {
        return null
      }

      const staticSkills = Array.isArray(this.local_artefact?.skills)
        ? this.local_artefact.skills
        : []

      if (!staticSkills.length) return null

      const characterSkills = this.relatedSkills

      // always use the character's current Str/Dex rather than
      // any snapshot stored on the item so that mins-met and
      // usability stay in sync when characteristics change
      const strCur =
        this.relatedCharacteristics.find((c) => c.name === 'str')?.cur ?? null
      const dexCur =
        this.relatedCharacteristics.find((c) => c.name === 'dex')?.cur ?? null

      return evaluateWeaponUsability({
        staticSkills,
        characterSkills,
        itemType: type,
        strCur,
        dexCur,
      })
    },
    // rows for the weapon skills table (name, got skill, mins met)
    weaponSkillRows() {
      const details = this.weaponUsabilityDetails
      if (!details || !Array.isArray(details.perSkill)) return []
      const staticSkills = Array.isArray(this.local_artefact?.skills)
        ? this.local_artefact.skills
        : []

      return details.perSkill.map((ps) => ({
        ...(() => {
          const sourceSkill = staticSkills.find(
            (skill) => skill?.name === ps.name,
          )
          const skillId =
            sourceSkill?.skill ??
            sourceSkill?.skill_id ??
            sourceSkill?.id ??
            ps.name

          return {
            name: ps.name,
            skill_id: skillId,
            skill: ps.hasSkill ? 'Yes' : 'No',
            min_str: ps.min_str,
            min_dex: ps.min_dex,
            damage_dice: sourceSkill?.damage_dice ?? '',
            // in the UI, "Mins Met" should answer only
            // "do current Str/Dex meet the numeric mins?",
            // independent of whether the character has the skill
            met_req: ps.statsOk ? 'Yes' : 'No',
          }
        })(),
      }))
    },
    weaponUnusableReasons() {
      const details = this.weaponUsabilityDetails
      return details?.unusable_reasons || ''
    },
    isError() {
      return this.errors.length > 0
    },
    filteredArtefactTypes() {
      const artefactTypes = Array.isArray(this.artefact_type_records)
        ? this.artefact_type_records
        : []

      return [...artefactTypes]
        .sort((left, right) => {
          const leftSeq = Number(left?.seq ?? 0)
          const rightSeq = Number(right?.seq ?? 0)

          if (leftSeq !== rightSeq) {
            return leftSeq - rightSeq
          }

          const leftName = String(left?.name ?? '').toLowerCase()
          const rightName = String(right?.name ?? '').toLowerCase()

          if (leftName < rightName) return -1
          if (leftName > rightName) return 1
          return 0
        })
        .map((type) => type?.name || '')
        .filter((type) => type !== '')
    },
    artefactBaseItemsForSelection() {
      return this.getAvailableArtefactBaseItems(
        this.local_artefact?.artefact_type ??
          this.activeArtefact?.artefact_type,
      )
    },
    // for new artefact artefacts, require an artefact type selection
    // before enabling the Add button
    artefactTypeMissingForNew() {
      if (!this.isNewArtefact) return false

      const raw =
        (this.local_artefact && this.local_artefact.artefact_type) ||
        this.activeArtefact?.artefact_type ||
        ''
      const value = (raw || '').trim()

      // Treat only a blank value as "missing"; special
      // lookup entries like "Unknown" / "Other"
      // are still valid explicit choices.
      return !value
    },
    isChanged() {
      // new artefacts...
      if (this.isNewArtefact) {
        return isObjectChanged(this.original_artefact, this.activeArtefact)
      }
      return this.buildExistingArtefactPatchChanges().length > 0
    },
  },
  //========================================================
  // methods
  //---------
  methods: {
    // expose imported helper for template usage
    isEmpty,
    buildArtefactSpellStringByType(spellType) {
      const rawSpells = Array.isArray(this.activeArtefact?.non_rune_spells)
        ? this.activeArtefact.non_rune_spells
        : Array.isArray(this.activeArtefact?.spells)
          ? this.activeArtefact.spells
          : []

      const filteredSpells = rawSpells.filter((spell) => {
        const type = String(spell?.type || '')
          .trim()
          .toLowerCase()
        return (
          type ===
          String(spellType || '')
            .trim()
            .toLowerCase()
        )
      })

      return filteredSpells
        .map((spell) => {
          const spellName = String(
            spell?.display_name || spell?.name || '',
          ).trim()
          if (!spellName) {
            return ''
          }

          const mag =
            spell?.mag !== undefined && spell?.mag !== null
              ? ` (Mag ${spell.mag})`
              : ''
          return `${spellName}${mag}`
        })
        .filter(Boolean)
        .join('. ')
    },
    buildAbsoluteUrl(path) {
      if (!path) {
        return ''
      }

      if (/^(https?:|data:|blob:)/i.test(path)) {
        return path
      }

      const normalizedPath = path.startsWith('/') ? path : `/${path}`
      const baseUrl = import.meta.env.VITE_API_URL || window.location.origin

      try {
        return new URL(normalizedPath, baseUrl).toString()
      } catch {
        return normalizedPath
      }
    },
    setActiveTab(tab) {
      if (!tab || !this.isTabAvailable(tab)) {
        this.activeTab = 'artefact'
        return
      }

      this.activeTab = tab
    },
    navigateToFirstErrorTab() {
      // Ordered by tab priority so the first visible tab with an error is shown,
      // regardless of the order errors were pushed during validation.
      const tabPriority = [
        {
          tab: 'artefact',
          fields: [
            'artefact_type',
            'artefact_base_item',
            'material',
            'quality',
            'age',
            'artefact',
            'currency',
            'value_lower',
            'value_upper',
            'worn_location',
          ],
        },
        { tab: 'descriptions', fields: ['short_description'] },
        { tab: 'switches', fields: ['status'] },
        { tab: 'count', fields: ['unit_name', 'max_unit_count'] },
        { tab: 'armour', fields: ['armour_location', 'equipped'] },
        { tab: 'healing', fields: ['healing_type'] },
        {
          tab: 'poison',
          fields: [
            'poison_type',
            'pot',
            'effect',
            'delay',
            'damage_dice',
            'duration',
            'resist',
          ],
        },
      ]
      for (const { tab, fields } of tabPriority) {
        if (
          fields.some((f) => this.errorFields.includes(f)) &&
          this.isTabAvailable(tab)
        ) {
          this.setActiveTab(tab)
          return
        }
      }
    },
    buildHealingShortDescription(recovers) {
      const value = typeof recovers === 'string' ? recovers.trim() : ''
      const maxLength = 100

      if (!value) {
        return ''
      }

      const description = `Recovers ${value}`
      if (description.length <= maxLength) {
        return description
      }

      return description.slice(0, maxLength).trimEnd()
    },
    buildPoisonShortDescription(effect, damageDice, duration) {
      const effectValue = typeof effect === 'string' ? effect.trim() : ''
      const damageValue =
        typeof damageDice === 'string' ? damageDice.trim() : ''
      const durationValue = typeof duration === 'string' ? duration.trim() : ''
      const maxLength = 100

      if (!effectValue) {
        return ''
      }

      const hasDamageValue =
        damageValue && !['n/a', 'na'].includes(damageValue.toLowerCase())
      const hasDurationValue =
        durationValue && !['n/a', 'na'].includes(durationValue.toLowerCase())
      const description = hasDamageValue
        ? `${effectValue} (${damageValue})`
        : hasDurationValue
          ? `${effectValue} (${durationValue})`
          : effectValue

      if (description.length <= maxLength) {
        return description
      }

      return description.slice(0, maxLength).trimEnd()
    },
    openDocument() {
      if (!this.documentViewerUrl) {
        return
      }

      window.open(this.documentViewerUrl, '_blank', 'noopener,noreferrer')
    },
    toBooleanFlag(value) {
      if (typeof value === 'string') {
        const normalised = value.trim().toLowerCase()
        if (normalised === 'true' || normalised === '1') return true
        if (normalised === 'false' || normalised === '0' || normalised === '')
          return false
      }

      if (typeof value === 'number') {
        return value === 1
      }

      return value === true
    },
    findLookupStringValue(options, desiredValue) {
      if (typeof desiredValue !== 'string' || !desiredValue.trim()) {
        return desiredValue
      }

      const desiredNormalised = desiredValue.trim().toLowerCase()
      const values = Array.isArray(options) ? options : []
      const match = values.find(
        (option) =>
          typeof option === 'string' &&
          option.trim().toLowerCase() === desiredNormalised,
      )

      return match ?? desiredValue
    },
    getInactiveStatusNaValue() {
      const lookups =
        this.$store.getters['characters/getItemLookups']?.status ??
        this.statuses
      return this.findLookupStringValue(lookups, 'Active')
    },
    getDiscardedStatusValue() {
      const lookups =
        this.$store.getters['characters/getItemLookups']?.status ??
        this.statuses
      return this.findLookupStringValue(lookups, 'Discarded')
    },
    getStatusNotApplicableValue() {
      const lookups =
        this.$store.getters['characters/getItemLookups']?.status ??
        this.statuses
      return this.findLookupStringValue(lookups, 'n/a')
    },
    getDestroyedStatusValue() {
      const lookups =
        this.$store.getters['characters/getItemLookups']?.status ??
        this.statuses
      return this.findLookupStringValue(lookups, 'Destroyed')
    },
    getNormalisedInactiveStatusValue() {
      const isActive = this.toBooleanFlag(
        this.local_artefact?.active ?? this.activeArtefact?.active ?? true,
      )
      const currentStatus =
        this.local_artefact?.status ?? this.activeArtefact?.status ?? null

      if (isActive) {
        return this.getInactiveStatusNaValue()
      }

      if (!currentStatus) {
        return null
      }

      const activeStatus = this.getInactiveStatusNaValue()
      const normalisedCurrentStatus =
        typeof currentStatus === 'string'
          ? currentStatus.trim().toLowerCase()
          : ''

      if (normalisedCurrentStatus === activeStatus.trim().toLowerCase()) {
        return null
      }

      return currentStatus
    },
    syncInactiveStatusWithActiveState() {
      const isActive = this.toBooleanFlag(
        this.local_artefact?.active ?? this.activeArtefact?.active ?? true,
      )
      const activeStatus = this.getInactiveStatusNaValue()

      const rememberInactiveStatus = (statusValue) => {
        const normalisedStatusValue =
          typeof statusValue === 'string' ? statusValue.trim() : ''

        if (
          !normalisedStatusValue ||
          normalisedStatusValue.toLowerCase() ===
            activeStatus.trim().toLowerCase()
        ) {
          return
        }

        this.local_artefact._last_inactive_status = normalisedStatusValue

        if (this.activeArtefact) {
          this.activeArtefact._last_inactive_status = normalisedStatusValue
        }
      }

      this.local_artefact = this.local_artefact || {}

      if (!isActive) {
        const currentStatus = this.getNormalisedInactiveStatusValue()
        const restoredStatus =
          currentStatus ??
          this.local_artefact?._last_inactive_status ??
          this.activeArtefact?._last_inactive_status ??
          null

        this.local_artefact.status = restoredStatus

        if (this.activeArtefact) {
          this.activeArtefact.status = restoredStatus
        }

        rememberInactiveStatus(restoredStatus)
        return restoredStatus
      }

      rememberInactiveStatus(this.getNormalisedInactiveStatusValue())
      this.local_artefact.status = activeStatus

      if (this.activeArtefact) {
        this.activeArtefact.status = activeStatus
      }

      return activeStatus
    },
    getCharacterArtefactId() {
      const artefactName = (this.relatedArtefactName || '').trim()

      if (!artefactName) {
        return null
      }

      const getArtefactByName =
        this.$store.getters['artefacts/getArtefactByName']
      const artefact =
        typeof getArtefactByName === 'function'
          ? getArtefactByName(artefactName)
          : null

      return artefact?.id ?? null
    },
    applyArtefactTypeDefaults(artefactType, previousArtefactType = null) {
      const normalisedArtefactType =
        typeof artefactType === 'string'
          ? artefactType.trim().toLowerCase()
          : ''
      const normalisedPreviousArtefactType =
        typeof previousArtefactType === 'string'
          ? previousArtefactType.trim().toLowerCase()
          : ''

      const descriptorNaArtefactTypes = new Set(['runestone', 'tattoo'])
      const defaultsByArtefactType = {
        runestone: {
          material: this.findLookupStringValue(this.materials, 'Stone'),
          quality: this.findLookupStringValue(this.qualities, 'n/a'),
          age: this.findLookupStringValue(this.ages, 'n/a'),
          artefact: this.getCharacterArtefactId(),
        },
        tattoo: {
          material: this.findLookupStringValue(this.materials, 'Skin'),
          quality: this.findLookupStringValue(this.qualities, 'n/a'),
          age: this.findLookupStringValue(this.ages, 'n/a'),
          artefact: this.getCharacterArtefactId(),
        },
      }

      const defaults = defaultsByArtefactType[normalisedArtefactType]
      if (!defaults) {
        if (
          descriptorNaArtefactTypes.has(normalisedPreviousArtefactType) &&
          !descriptorNaArtefactTypes.has(normalisedArtefactType)
        ) {
          const unknownQuality = this.findLookupStringValue(
            this.qualities,
            'Unknown',
          )
          const unknownAge = this.findLookupStringValue(this.ages, 'Unknown')

          this.local_artefact.quality = unknownQuality
          this.local_artefact.age = unknownAge
          this.local_artefact._static_quality_unknown = true
          this.local_artefact._static_age_unknown = true
          this.local_artefact._session_quality_editable = true
          this.local_artefact._session_age_editable = true

          if (this.activeArtefact) {
            this.activeArtefact.quality = unknownQuality
            this.activeArtefact.age = unknownAge
            this.activeArtefact._static_quality_unknown = true
            this.activeArtefact._static_age_unknown = true
            this.activeArtefact._session_quality_editable = true
            this.activeArtefact._session_age_editable = true
          }

          if (!this.isNewArtefact) {
            this.emitUpdate('quality', unknownQuality)
            this.emitUpdate('age', unknownAge)
          }
        }

        return
      }

      Object.entries(defaults).forEach(([key, defaultValue]) => {
        this.local_artefact[key] = defaultValue

        if (this.activeArtefact) {
          this.activeArtefact[key] = defaultValue
        }

        if (!this.isNewArtefact) {
          this.emitUpdate(key, defaultValue)
        }
      })
    },
    //========================================================
    // is editable
    //-------------
    isEditable(prop) {
      // get item id
      const id = this.activeArtefact && this.activeArtefact.id

      // only allow editing of existing item (artefact) where value unknown
      if (prop === 'Unknown' || isEmpty(prop)) {
        // isempty for artefact
        return true
      }
      // where new item = editable
      if (this.isNewArtefact) return true

      // where tempid = editable
      if (typeof id === 'string' && id.includes('TMP')) return true

      return false
    },
    //========================================================
    // preview image
    //---------------
    previewImage(event) {
      console.log('---------------------')
      console.log('IN previewImage')
      const file = event.target.files[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = (e) => {
        // store preview on the local item so the computed
        // image getter (using local_artefact.image) will update
        this.local_artefact = this.local_artefact || {}
        this.local_artefact.image = e.target.result

        // keep the upload blob on the local item so that
        // add/submit payloads can carry image_file directly

        // create image file for uploading
        const formData = new FormData()
        formData.append('image', file)

        this.local_artefact.image_file = formData
        this.local_artefact.image_cleared = false
        if (this.activeArtefact) {
          this.activeArtefact.image_file = formData
          this.activeArtefact.image_cleared = false
        }

        // emit both the preview data URL and the formdata so
        // the parent/component handling saves can persist it
        this.emitUpdate('image', e.target.result)
        this.emitUpdate('image_file', formData)
        console.log(
          'Image preview updated and file prepared for upload.',
          formData,
        )
      }
      reader.readAsDataURL(file)
    },
    clearImage() {
      this.local_artefact = this.local_artefact || {}
      this.local_artefact.image = null
      this.local_artefact.image_file = null
      this.local_artefact.image_cleared = true

      if (this.activeArtefact) {
        this.activeArtefact.image = null
        this.activeArtefact.image_file = null
        this.activeArtefact.image_cleared = true
      }

      this.emitUpdate('image', null)
      this.emitUpdate('image_file', null)
    },
    //========================================================
    // artefact name input handler
    //-----------------------------
    onArtefactNameInput(event) {
      const rawValue = event && event.target ? (event.target.value ?? '') : ''
      const value = rawValue.replace(
        /(^|[\s\-_])([a-z])/g,
        (match, prefix, char) => {
          return `${prefix}${char.toUpperCase()}`
        },
      )
      const selectionStart = event?.target?.selectionStart
      const selectionEnd = event?.target?.selectionEnd

      if (this.activeArtefact) {
        this.activeArtefact.name = value
      }

      if (this.local_artefact) {
        this.local_artefact.name = value
      }

      if (event && event.target && event.target.value !== value) {
        event.target.value = value

        if (
          typeof selectionStart === 'number' &&
          typeof selectionEnd === 'number' &&
          typeof event.target.setSelectionRange === 'function'
        ) {
          event.target.setSelectionRange(selectionStart, selectionEnd)
        }
      }

      if (!this.isNewArtefact) {
        this.delayedEmitUpdate('name', value)
      }
    },
    //========================================================
    // artefact description input handler
    //------------------------------------
    onArtefactDescriptionInput(event) {
      const raw = event && event.target ? (event.target.value ?? '') : ''
      const value = normaliseLeadingLetterText(raw)

      if (event && event.target) {
        event.target.value = value
      }

      if (this.activeArtefact) {
        this.activeArtefact.description = value
      }

      if (this.local_artefact) {
        this.local_artefact.description = value
      }

      this.delayedEmitUpdate('description', value)
    },
    //========================================================
    // generic name input handler for child tabs
    //-------------------------------------------
    onNameInput(event) {
      this.onArtefactNameInput(event)
    },
    //========================================================
    // get unique artefact name for non-campaign artefacts
    //-----------------------------------------------------
    getUniqueArtefactName(rawName) {
      const trimmedName = typeof rawName === 'string' ? rawName.trim() : ''
      const baseName = trimmedName ? capitaliseFirstLetter(trimmedName) : ''

      if (!baseName) {
        return ''
      }

      const existingArtefacts = Array.isArray(this.artefactNames)
        ? this.artefactNames
        : []

      const existingNames = new Set()

      existingArtefacts.forEach((item) => {
        const existingName =
          typeof item === 'string'
            ? item
            : (item?.display_name || item?.name || '').toString().trim()

        if (existingName) {
          existingNames.add(existingName.toLowerCase())
        }
      })

      if (!existingNames.has(baseName.toLowerCase())) {
        return baseName
      }

      const match = baseName.match(/^(.*?)(?:\s+(\d+))?$/)
      const unsuffixedBase = (match?.[1] || baseName).trim() || baseName

      let suffix = 2
      let candidate = `${unsuffixedBase} ${suffix}`

      while (existingNames.has(candidate.toLowerCase())) {
        suffix += 1
        candidate = `${unsuffixedBase} ${suffix}`
      }

      return candidate
    },
    //========================================================
    // normalise artefact descriptor payload value
    //---------------------------------------------
    normaliseArtefactDescriptorForSave(field, rawValue) {
      const fieldsDefaultingToUnknown = ['material', 'quality', 'age']

      if (!fieldsDefaultingToUnknown.includes(field)) {
        return rawValue
      }

      if (rawValue === null || rawValue === undefined) {
        return 'Unknown'
      }

      if (typeof rawValue === 'string') {
        const trimmed = rawValue.trim()
        return trimmed === '' ? 'Unknown' : trimmed
      }

      return rawValue
    },
    //========================================================
    // generic description input handler for child tabs
    //--------------------------------------------------
    onDescriptionInput(event) {
      this.onArtefactDescriptionInput(event)
    },
    //========================================================
    // generic dm-notes input handler
    //-------------------------------
    onDmNotesInput(event) {
      const raw = event && event.target ? (event.target.value ?? '') : ''
      const value = normaliseLeadingLetterText(raw)

      if (event && event.target) {
        event.target.value = value
      }

      if (this.activeArtefact) {
        this.activeArtefact.dm_notes = value
      }

      this.delayedEmitUpdate('dm_notes', value)
    },
    //========================================================
    // backwards-compatible notes handler alias
    //------------------------------------------
    onNotesInput(event) {
      this.onDmNotesInput(event)
    },
    //========================================================
    // preload image
    //---------------
    preloadImage(image) {
      let image_name = getStringAfterLastSlash(image)
      console.log('-------------------------')
      console.log('IN preloadImage')
      //-----------------------------
      // get blob from src url fetch
      //-----------------------------
      fetch(image)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }
          return response.blob()
        })
        .then((blob) => {
          //---------------------------
          // create FormData with blob
          //---------------------------
          const formData = new FormData()
          formData.append('image', blob, image_name)

          // create an object URL for preview and store it
          // on the local item so the computed image getter
          // (local_artefact.image) can render it
          const url = URL.createObjectURL(blob)
          this.local_artefact = this.local_artefact || {}
          this.local_artefact.image = url
          this.local_artefact.image_cleared = false

          // keep the upload blob on the local item so that
          // add/submit payloads can carry image_file directly
          this.local_artefact.image_file = formData

          if (this.activeArtefact) {
            this.activeArtefact.image_file = formData
            this.activeArtefact.image_cleared = false
          }

          // emit both preview and upload blob to parent
          this.emitUpdate('image', url)
          this.emitUpdate('image_file', formData)
        })
        .catch((error) => {
          console.error('Failed to fetch and convert artefact image:', error)
        })
    },
    //========================================================
    // generic ENC formatter (two decimals)
    //-------------------------------------
    formatEnc(value) {
      const n = Number(value)
      if (Number.isNaN(n)) return ''
      if (n === 0) return '0.00'
      if (this.global && typeof this.global.twoDecimal === 'function') {
        return this.global.twoDecimal(n)
      }
      return n.toFixed(2)
    },
    //========================================================
    // estimated value toggle (artefact tab)
    //---------------------------------------
    onEstimatedValueToggle(value) {
      // keep active item in sync with toggle
      if (this.activeArtefact) {
        this.activeArtefact.estimated_value = !!value
      }

      // existing artefacts: emit realtime update
      if (!this.isNewArtefact) {
        this.emitUpdate('estimated_value', !!value)
      }
    },
    //========================================================
    // artefact countable toggle (tally tab)
    //--------------------------------------
    onCountableToggle(value) {
      console.log('onCountableToggle value = ', value)

      this.local_artefact = this.local_artefact || {}
      const boolVal = !!value

      this.local_artefact.countable = boolVal
      if (this.activeArtefact) {
        this.activeArtefact.countable = boolVal
      }

      if (!this.isNewArtefact) {
        this.emitUpdate('countable', boolVal)
      }

      this.enforceCountAndTallyRules({ emitUpdates: !this.isNewArtefact })
    },
    enforceCountAndTallyRules({ emitUpdates = false } = {}) {
      this.local_artefact = this.local_artefact || {}

      const countable = this.toBooleanFlag(
        this.local_artefact.countable ?? this.activeArtefact?.countable,
      )
      const tallyable = this.toBooleanFlag(
        this.local_artefact.tallyable ?? this.activeArtefact?.tallyable,
      )

      const updatedFields = []
      const applyField = (key, value) => {
        const currentValue = this.local_artefact[key]
        if (currentValue !== value) {
          this.local_artefact[key] = value
          updatedFields.push({ key, value })
        }

        if (this.activeArtefact) {
          this.activeArtefact[key] = value
        }
      }

      if (!countable) {
        applyField('unit_name', null)
        applyField('max_unit_count', 0)
      } else {
        const maxUnits = Number(this.local_artefact.max_unit_count)
        if (!Number.isFinite(maxUnits) || maxUnits < 1) {
          applyField('max_unit_count', 1)
        }
      }

      const parsedTallyQty = Number.parseInt(this.local_artefact.tally_qty, 10)
      const nextTallyQty =
        !tallyable || !Number.isFinite(parsedTallyQty) || parsedTallyQty < 1
          ? 1
          : parsedTallyQty
      applyField('tally_qty', nextTallyQty)

      if (emitUpdates && !this.isNewArtefact) {
        updatedFields.forEach(({ key, value }) => {
          this.emitUpdate(key, value)
        })
      }
    },
    getInitialMaxUnitCount(unitName, candidateMax) {
      if (!unitName) {
        return 0
      }

      const parsedMax = Number(candidateMax)
      if (!Number.isNaN(parsedMax) && parsedMax > 0) {
        return parsedMax
      }

      return this.isNewArtefact ? 1 : 0
    },
    //========================================================
    // artefact unit-name select (tally tab)
    //---------------------------------------
    onUnitSelect(unit) {
      console.log('onUnitSelect unit = ', unit)
      this.local_artefact = this.local_artefact || {}

      // If cleared, treat as "no unit" and zero counts.
      if (!unit) {
        this.local_artefact.unit_name = null
        this.local_artefact.max_unit_count = 0

        if (this.activeArtefact) {
          this.activeArtefact.unit_name = null
          this.activeArtefact.max_unit_count = 0
        }

        if (!this.isNewArtefact) {
          this.emitUpdate('unit_name', null)
          this.emitUpdate('max_unit_count', 0)
        }
        return
      }

      let value = unit
      if (typeof unit === 'object' && unit !== null) {
        value = unit.short_name || unit.name || unit.value || unit.id || null
      }

      this.local_artefact.unit_name = value
      const initialMax = this.getInitialMaxUnitCount(
        value,
        this.local_artefact.max_unit_count,
      )
      this.local_artefact.max_unit_count = initialMax

      if (this.activeArtefact) {
        this.activeArtefact.unit_name = value
        this.activeArtefact.max_unit_count = initialMax
      }

      if (!this.isNewArtefact) {
        this.emitUpdate('unit_name', value)
        this.emitUpdate('max_unit_count', initialMax)
      }
    },
    //========================================================
    // artefact trait select
    //----------------------
    onTraitSelect(traitId) {
      this.local_artefact = this.local_artefact || {}

      const selectedTraitId = traitId || null
      const selectedTrait =
        this.traits.find((trait) => trait?.id === selectedTraitId) ?? null
      const selectedTraitRows = selectedTrait
        ? [
            {
              id: selectedTrait.id ?? null,
              name: selectedTrait.name ?? '',
              description: selectedTrait.description ?? '',
            },
          ]
        : []

      this.local_artefact.trait = selectedTraitId
      this.local_artefact.item_traits = selectedTraitRows

      if (this.activeArtefact) {
        this.activeArtefact.trait = selectedTraitId
        this.activeArtefact.item_traits = selectedTraitRows
      }

      if (!this.isNewArtefact) {
        this.emitUpdate('trait', selectedTraitId)
      }
    },
    //========================================================
    // artefact lookup field change
    //--------------------------------
    onArtefactFieldChange(field, value) {
      // keep local_artefact in sync with the select
      this.local_artefact = this.local_artefact || {}

      const booleanFields = [
        'active',
        'starter',
        'tallyable',
        'wearable',
        'storeable',
        'storage',
        'attachable',
        'attach_point',
        'countable',
      ]

      const normaliseBooleanFieldValue = (raw) => {
        if (typeof raw === 'boolean') {
          return raw
        }

        if (typeof raw === 'string') {
          const lowered = raw.trim().toLowerCase()
          if (['true', '1', 'yes', 'on'].includes(lowered)) {
            return true
          }
          if (['false', '0', 'no', 'off', ''].includes(lowered)) {
            return false
          }
        }

        if (raw && typeof raw === 'object') {
          if (typeof raw.target?.checked === 'boolean') {
            return raw.target.checked
          }
          if (typeof raw.checked === 'boolean') {
            return raw.checked
          }
          if (Object.prototype.hasOwnProperty.call(raw, 'value')) {
            return normaliseBooleanFieldValue(raw.value)
          }
          if (Object.prototype.hasOwnProperty.call(raw, 'modelValue')) {
            return normaliseBooleanFieldValue(raw.modelValue)
          }
        }

        return !!raw
      }

      const descriptorFields = ['material', 'quality', 'age', 'artefact']
      const isUnknownDescriptorValue = (rawValue) => {
        if (rawValue === null || rawValue === undefined) {
          return true
        }

        if (typeof rawValue === 'string') {
          const trimmed = rawValue.trim()
          return trimmed === '' || trimmed === 'Unknown'
        }

        if (typeof rawValue === 'object') {
          if (typeof rawValue.name === 'string') {
            const trimmedName = rawValue.name.trim()
            if (trimmedName === '' || trimmedName === 'Unknown') {
              return true
            }
          }

          return rawValue.id === null || rawValue.id === undefined
        }

        return false
      }

      // Latch descriptor editability for the rest of the session if the
      // field was unknown when the user began editing it. This prevents the
      // control from immediately relocking when the newly selected value is
      // no longer Unknown.
      if (descriptorFields.includes(field)) {
        const currentValue =
          this.local_artefact[field] ?? this.activeArtefact?.[field]
        if (isUnknownDescriptorValue(currentValue)) {
          this.local_artefact[`_session_${field}_editable`] = true
        }
      }

      const previousArtefactType =
        field === 'artefact_type'
          ? this.normaliseArtefactTypeName(
              this.local_artefact[field] ?? this.activeArtefact?.[field],
            )
          : null

      // For id-based lookups (artefact, currency, etc.), treat "no
      // selection" as null so the v-select placeholder ("Select...")
      // can show correctly instead of an empty string.
      let normalisedValue = value === undefined || value === '' ? null : value

      if (booleanFields.includes(field)) {
        normalisedValue = normaliseBooleanFieldValue(value)
      }

      if (
        field === 'trait' ||
        field === 'race' ||
        field === 'currency' ||
        field === 'item_size' ||
        field === 'container_size' ||
        field === 'artefact_base_item'
      ) {
        normalisedValue = this.normaliseSelectionId(value)
      }

      if (field === 'artefact_type') {
        const artefactTypeName = this.normaliseArtefactTypeName(value)
        normalisedValue = artefactTypeName || null
      }

      if (field === 'status') {
        normalisedValue =
          value === undefined || value === null ? null : String(value)
      }

      this.local_artefact[field] = normalisedValue

      // for both new and existing artefacts, mirror onto activeArtefact so
      // isChanged comparisons and submit payloads see the latest value
      if (this.activeArtefact) {
        this.activeArtefact[field] = normalisedValue
      }

      let syncedInactiveStatus = null

      if (field === 'status') {
        const activeStatus = this.getInactiveStatusNaValue()
        const normalisedStatusValue =
          typeof normalisedValue === 'string'
            ? normalisedValue.trim().toLowerCase()
            : ''

        if (
          normalisedStatusValue &&
          normalisedStatusValue !== activeStatus.trim().toLowerCase()
        ) {
          this.local_artefact._last_inactive_status = normalisedValue
          if (this.activeArtefact) {
            this.activeArtefact._last_inactive_status = normalisedValue
          }
        }

        if (
          normalisedStatusValue &&
          normalisedStatusValue === activeStatus.trim().toLowerCase()
        ) {
          this.local_artefact.active = true
          if (this.activeArtefact) {
            this.activeArtefact.active = true
          }
        } else if (normalisedStatusValue) {
          this.local_artefact.active = false
          if (this.activeArtefact) {
            this.activeArtefact.active = false
          }
        }

        syncedInactiveStatus = normalisedValue
      } else {
        syncedInactiveStatus = this.syncInactiveStatusWithActiveState()
      }

      if (field === 'active' && normalisedValue === true) {
        this.clearInputError('status')
      }

      if (field === 'status') {
        const normalisedInactiveStatus =
          typeof normalisedValue === 'string'
            ? normalisedValue.trim().toLowerCase()
            : ''
        if (normalisedInactiveStatus) {
          this.clearInputError('status')
        }
      }

      if (field === 'artefact_type') {
        this.applyArtefactSubtypeFromType(normalisedValue)
        if (this.isNewArtefact) {
          this.applyArtefactTypeFlags(normalisedValue)
          // Seed potency default for new poison artefacts
          if (
            typeof normalisedValue === 'string' &&
            normalisedValue.trim().toLowerCase() === 'poison' &&
            !this.local_artefact.pot
          ) {
            this.local_artefact.pot = 10
          }
        }
        this.applyArtefactTypeDefaults(normalisedValue, previousArtefactType)
        this.applyArtefactTypeItemSize(normalisedValue, this.isNewArtefact)

        const currentEnc = Number(
          this.local_artefact?.enc ?? this.activeArtefact?.enc ?? null,
        )
        const hasCurrentEnc = Number.isFinite(currentEnc) && currentEnc > 0
        if (this.isNewArtefact || !hasCurrentEnc) {
          this.applyArtefactTypeEnc(normalisedValue)
        }

        const currentBaseItemId = this.normaliseSelectionId(
          this.local_artefact?.artefact_base_item ??
            this.activeArtefact?.artefact_base_item ??
            null,
        )
        const getStaticItemById =
          this.$store.getters['characters/getStaticItemById']
        const currentBaseItem =
          typeof getStaticItemById === 'function' && currentBaseItemId
            ? getStaticItemById(currentBaseItemId)
            : null

        if (
          !this.requiresArtefactBaseItem(normalisedValue) ||
          (currentBaseItem &&
            !this.isArtefactBaseItemAllowedForType(
              currentBaseItem,
              normalisedValue,
            ))
        ) {
          this.clearArtefactBaseItemSelection()
        }

        if (!this.requiresContainerSize(normalisedValue)) {
          this.applyContainerSizeSelection(null)
        }
      }

      if (field === 'item_size') {
        this.applySelectedItemSize(normalisedValue)
      }

      if (field === 'container_size') {
        this.applyContainerSizeSelection(normalisedValue)
      }

      if (field === 'artefact_base_item') {
        this.applyArtefactBaseItemSelection(normalisedValue)
      }

      // existing artefacts: emit realtime update so backend stays in sync
      if (!this.isNewArtefact) {
        if (field === 'status') {
          this.emitUpdate('active', this.local_artefact.active)
          this.emitUpdate('status', syncedInactiveStatus)
        } else {
          this.emitUpdate(field, normalisedValue)
          if (field === 'active') {
            this.emitUpdate('status', syncedInactiveStatus)
          }
        }
      }

      if (field === 'countable' || field === 'tallyable') {
        this.enforceCountAndTallyRules({ emitUpdates: !this.isNewArtefact })
      }

      // keep value/currency validation in sync whenever the
      // artefact currency field changes
      if (field === 'currency') {
        this.validateValueRange()
      }

      if (field === 'damage_dice') {
        const trimmedDamageDice =
          typeof normalisedValue === 'string' ? normalisedValue.trim() : ''

        if (this.isValidWeaponDamageDiceOverride(trimmedDamageDice)) {
          this.clearInputError('damage_dice')
        }
      }
    },
    //========================================================
    // artefact value / currency validation
    //--------------------------------------
    validateValueRange() {
      // clear any existing errors for the affected fields before
      // re-evaluating the current combination
      this.clearInputError('value_lower')
      this.clearInputError('value_upper')
      this.clearInputError('currency')

      const item = this.local_artefact || this.activeArtefact || {}

      // use the display* computed values so that validation
      // follows exactly what the user sees ("not empty" means
      // the input shows something other than blank)
      const displayLower = this.displayValueLower || ''
      const displayUpper = this.displayValueUpper || ''

      const rawCurrency = item.currency

      const hasLower = displayLower !== ''
      const hasUpper = displayUpper !== ''

      const lowerNum = hasLower ? Number(displayLower) : null
      const upperNum = hasUpper ? Number(displayUpper) : null

      // 1) If value upper provided, value lower must be present
      if (hasUpper && !hasLower) {
        const msg = window.bbUiErrorMsg(
          'Value lower required when value upper is set',
        )
        if (!this.errors.includes(msg)) {
          this.errors.push(msg)
        }
        if (!this.errorFields.includes('value_lower')) {
          this.errorFields.push('value_lower')
        }
      }

      // 2) If value upper provided, it must be >= value lower
      if (
        hasLower &&
        hasUpper &&
        !Number.isNaN(lowerNum) &&
        !Number.isNaN(upperNum) &&
        upperNum < lowerNum
      ) {
        const msg = window.bbUiErrorMsg(
          'Value upper must be greater than or equal to value lower',
        )
        if (!this.errors.includes(msg)) {
          this.errors.push(msg)
        }
        if (!this.errorFields.includes('value_upper')) {
          this.errorFields.push('value_upper')
        }
      }

      // 3) If either value lower or upper is set, currency must be selected
      const hasCurrency =
        rawCurrency !== null &&
        rawCurrency !== undefined &&
        String(rawCurrency) !== ''
      if ((hasLower || hasUpper) && !hasCurrency) {
        const msg = window.bbUiErrorMsg(
          'Currency required when value lower or upper is set',
        )
        if (!this.errors.includes(msg)) {
          this.errors.push(msg)
        }
        if (!this.errorFields.includes('currency')) {
          this.errorFields.push('currency')
        }
      }
    },
    validatePoisonArtefactFields() {
      let hasErrors = false
      const poisonTypeValue =
        this.local_artefact?.poison_type ??
        this.activeArtefact?.poison_type ??
        null
      const poisonTypeText =
        typeof poisonTypeValue === 'string'
          ? poisonTypeValue.trim().toLowerCase()
          : String(poisonTypeValue ?? '')
              .trim()
              .toLowerCase()
      const isInvalidPoisonType =
        poisonTypeValue === null ||
        poisonTypeValue === undefined ||
        poisonTypeText === '' ||
        ['n/a', 'na', 'none', 'unknown'].includes(poisonTypeText)

      const requiredPoisonFields = [
        {
          field: 'poison_type',
          message: 'Poison type is required for poison artefacts',
        },
        { field: 'pot', message: 'Potency is required for poison artefacts' },
        {
          field: 'effect',
          message: 'Effect is required for poison artefacts',
        },
        { field: 'delay', message: 'Delay is required for poison artefacts' },
        {
          field: 'damage_dice',
          message: 'Damage dice is required for poison artefacts',
        },
        {
          field: 'duration',
          message: 'Duration is required for poison artefacts',
        },
        {
          field: 'resist',
          message: 'Resist is required for poison artefacts',
        },
      ]

      for (const { field, message } of requiredPoisonFields) {
        const value =
          this.local_artefact?.[field] ?? this.activeArtefact?.[field] ?? null
        const isMissing =
          field === 'poison_type'
            ? isInvalidPoisonType
            : value === null ||
              value === undefined ||
              (typeof value === 'string' && value.trim() === '')

        if (isMissing) {
          const msg = window.bbUiErrorMsg(message)
          if (!this.errors.includes(msg)) {
            this.errors.push(msg)
          }
          if (!this.errorFields.includes(field)) {
            this.errorFields.push(field)
          }
          hasErrors = true
        }
      }

      const potencyRaw =
        this.local_artefact?.pot ?? this.activeArtefact?.pot ?? null
      const hasPotencyValue =
        potencyRaw !== null &&
        potencyRaw !== undefined &&
        (typeof potencyRaw !== 'string' || potencyRaw.trim() !== '')

      if (hasPotencyValue) {
        const potency = Number(potencyRaw)
        const isValidPotency =
          Number.isFinite(potency) && potency >= 1 && potency <= 99

        if (!isValidPotency) {
          const msg = window.bbUiErrorMsg(
            'Potency must be a numeric value between 1 and 99',
          )
          if (!this.errors.includes(msg)) {
            this.errors.push(msg)
          }
          if (!this.errorFields.includes('pot')) {
            this.errorFields.push('pot')
          }
          hasErrors = true
        }
      }

      return !hasErrors
    },
    validateRequiredArtefactBaseItem() {
      this.clearInputError('artefact_base_item')

      const selectedArtefactType =
        this.local_artefact?.artefact_type ??
        this.activeArtefact?.artefact_type ??
        ''

      if (!this.requiresArtefactBaseItem(selectedArtefactType)) {
        return true
      }

      const selectedBaseItemId = this.normaliseSelectionId(
        this.local_artefact?.artefact_base_item ??
          this.activeArtefact?.artefact_base_item ??
          null,
      )

      if (selectedBaseItemId) {
        return true
      }

      const msg = window.bbUiErrorMsg(
        'Base item must be selected for this artefact type',
      )

      if (!this.errors.includes(msg)) {
        this.errors.push(msg)
      }
      if (!this.errorFields.includes('artefact_base_item')) {
        this.errorFields.push('artefact_base_item')
      }

      return false
    },
    validateInactiveStatusRequired() {
      this.clearInputError('status')

      const isActive =
        this.local_artefact?.active ?? this.activeArtefact?.active ?? true

      if (isActive) {
        return true
      }

      const inactiveStatus =
        this.local_artefact?.status ?? this.activeArtefact?.status ?? null
      const activeStatus = this.getInactiveStatusNaValue()
      const normalisedInactiveStatus =
        typeof inactiveStatus === 'string'
          ? inactiveStatus.trim().toLowerCase()
          : ''
      const hasInactiveStatus =
        typeof inactiveStatus === 'string'
          ? normalisedInactiveStatus !== '' &&
            normalisedInactiveStatus !== activeStatus.trim().toLowerCase()
          : inactiveStatus !== null && inactiveStatus !== undefined

      if (hasInactiveStatus) {
        return true
      }

      const msg = window.bbUiErrorMsg(
        'Status is required when active is set to false',
      )

      if (!this.errors.includes(msg)) {
        this.errors.push(msg)
      }
      if (!this.errorFields.includes('status')) {
        this.errorFields.push('status')
      }

      return false
    },
    //========================================================
    // generic helpers for numeric display fields that should
    // show 0/null as blank but store the raw value on local_artefact
    //----------------------------------------------------------
    getDisplayNumericField(field) {
      const val = this.activeArtefact ? this.activeArtefact[field] : undefined
      if (val === 0 || val === '0' || val === null || val === undefined)
        return ''
      return String(val)
    },
    setDisplayNumericField(field, v) {
      this.local_artefact = this.local_artefact || {}
      this.local_artefact[field] = v === '' ? '' : v
    },
    //========================================================
    // reduce object
    //----------------
    reduceObject(option) {
      if (!option) return null
      if (option.id === '__unknown_artefact__') return null
      return option.id ?? null
    },
    //========================================================
    // get container label
    //--------------------
    getContainerLabel(option) {
      if (!option) return ''
      return option.display_name || option.name || ''
    },
    //========================================================
    // get artefact label
    //----------------
    getArtefactLabel(option) {
      if (!option) return ''
      return option.name || ''
    },
    //========================================================
    // get race label
    //----------------
    getRaceLabel(option) {
      if (!option) return ''
      return option.name || ''
    },
    //========================================================
    // get currency label
    //--------------------
    getCurrencyLabel(option) {
      if (!option) return ''
      return option.short_name || ''
    },
    getArtefactBaseItemLabel(option) {
      if (!option) return ''

      const optionType = option.type || ''
      const optionName = option.name || ''

      if (
        optionType === 'Equipment' &&
        getNormalisedEquipmentType(option.equipment_type) === 'Container'
      ) {
        return `Container - ${optionName}`
      }

      return optionType ? `${optionType} - ${optionName}` : optionName
    },
    getItemSizeRecord(raw) {
      if (raw === null || raw === undefined || raw === '') {
        return null
      }

      const itemSizes = Array.isArray(this.item_sizes) ? this.item_sizes : []
      const rawId = typeof raw === 'object' ? (raw.id ?? null) : raw

      return itemSizes.find((itemSize) => itemSize?.id === rawId) ?? null
    },
    getItemSizeLabel(option) {
      if (!option) return ''

      const sizeShort = option.size_short || ''
      const enc = option.enc

      if (enc === null || enc === undefined || Number.isNaN(Number(enc))) {
        return `Size: ${sizeShort}`
      }

      return `Size: ${sizeShort} - enc: ${this.formatEnc(enc)}`
    },
    getContainerSizeRecord(raw) {
      if (raw === null || raw === undefined || raw === '') {
        return null
      }

      const containerSizes = Array.isArray(this.container_sizes)
        ? this.container_sizes
        : []
      const rawId = typeof raw === 'object' ? (raw.id ?? null) : raw

      return (
        containerSizes.find((containerSize) => containerSize?.id === rawId) ??
        null
      )
    },
    getContainerSizeLabel(option) {
      if (!option) return ''

      const sizeShort = option.size_short || ''
      const sizeName = option.size || ''
      const capacity = option.capacity

      if (
        capacity === null ||
        capacity === undefined ||
        Number.isNaN(Number(capacity))
      ) {
        return `${sizeShort} - ${sizeName}`
      }

      return `${sizeShort} - ${sizeName} (${this.formatEnc(capacity)} ENC)`
    },
    getSelectedSkillIds(skills) {
      if (!Array.isArray(skills) || skills.length === 0) {
        return []
      }

      const seen = new Set()

      return skills
        .map((skill) =>
          this.normaliseSelectionId(
            skill?.skill ?? skill?.skill_id ?? skill?.id ?? null,
          ),
        )
        .filter((skillId) => {
          if (!skillId || seen.has(skillId)) {
            return false
          }

          seen.add(skillId)
          return true
        })
    },
    normaliseSelectionId(raw) {
      if (raw === null || raw === undefined || raw === '') {
        return null
      }

      if (typeof raw === 'object') {
        return raw.id ?? null
      }

      return raw
    },
    getArtefactSubtypeName(rawArtefactType) {
      const record = this.getArtefactTypeRecord(rawArtefactType)
      const artefactTypeName = (record?.name || rawArtefactType || '').trim()
      const canonicalTypeNameByNormalisedValue = {
        armour: 'Armour',
        ammunition: 'Ammunition',
        'melee weapon': 'Melee Weapon',
        'ranged weapon': 'Ranged Weapon',
        container: 'Container',
        healing: 'Healing',
        poison: 'Poison',
        specialist: 'Specialist',
      }
      const normalisedTypeName = artefactTypeName.toLowerCase()

      return (
        canonicalTypeNameByNormalisedValue[normalisedTypeName] ||
        artefactTypeName ||
        'Artefact'
      )
    },
    requiresArtefactBaseItem(rawArtefactType) {
      return new Set([
        'Armour',
        'Ammunition',
        'Melee Weapon',
        'Ranged Weapon',
        'Container',
      ]).has(this.getArtefactSubtypeName(rawArtefactType))
    },
    requiresContainerSize(rawArtefactType) {
      return this.getArtefactSubtypeName(rawArtefactType) === 'Container'
    },
    getArtefactBaseItemFilterName(item) {
      if (!item) return ''

      if (item.type === 'Ammunition') return 'Ammunition'
      if (item.type === 'Armour') return 'Armour'
      if (item.type === 'Melee Weapon') return 'Melee Weapon'
      if (item.type === 'Ranged Weapon') return 'Ranged Weapon'
      if (
        item.type === 'Equipment' &&
        getNormalisedEquipmentType(item.equipment_type) === 'Container'
      ) {
        return 'Container'
      }

      return ''
    },
    isArtefactBaseItemAllowedForType(item, rawArtefactType) {
      const expectedType = this.getArtefactSubtypeName(rawArtefactType)
      return this.getArtefactBaseItemFilterName(item) === expectedType
    },
    getAvailableArtefactBaseItems(rawArtefactType) {
      if (!this.requiresArtefactBaseItem(rawArtefactType)) {
        return []
      }

      const artefactSubtype = this.getArtefactSubtypeName(rawArtefactType)
      const getArtefactBaseItemsByType =
        this.$store.getters['characters/getArtefactBaseItemsByType']
      const allItems =
        typeof getArtefactBaseItemsByType === 'function'
          ? getArtefactBaseItemsByType(artefactSubtype)
          : []

      // Load only the supported base-item candidates for the selected
      // artefact subtype, rather than the full static item catalogue.
      if (!Array.isArray(allItems) || allItems.length === 0) {
        this.$store
          .dispatch('characters/loadArtefactBaseItems', artefactSubtype)
          .catch(() => {})
        return []
      }

      return allItems
        .filter(
          (item) =>
            item &&
            this.isArtefactBaseItemAllowedForType(item, rawArtefactType),
        )
        .sort((left, right) => {
          const leftLabel = this.getArtefactBaseItemLabel(left).toLowerCase()
          const rightLabel = this.getArtefactBaseItemLabel(right).toLowerCase()
          if (leftLabel < rightLabel) return -1
          if (leftLabel > rightLabel) return 1
          return 0
        })
    },
    applyArtefactSubtypeFromType(rawArtefactType) {
      const artefactSubtype = this.getArtefactSubtypeName(rawArtefactType)

      this.local_artefact = this.local_artefact || {}
      this.local_artefact.type = artefactSubtype
      this.local_artefact.actual_type = artefactSubtype
      this.local_artefact.effective_type = artefactSubtype

      if (this.activeArtefact) {
        this.activeArtefact.type = artefactSubtype
        this.activeArtefact.actual_type = artefactSubtype
        this.activeArtefact.effective_type = artefactSubtype
      }
    },
    applyArtefactTypeItemSize(rawArtefactType, forceSelection = false) {
      const record = this.getArtefactTypeRecord(rawArtefactType)
      const defaultItemSizeId = record?.item_size_id ?? null

      if (!defaultItemSizeId) {
        return
      }

      const currentItemSizeId = this.normaliseSelectionId(
        this.local_artefact?.item_size ??
          this.activeArtefact?.item_size ??
          null,
      )

      if (!forceSelection && currentItemSizeId) {
        return
      }

      this.local_artefact = this.local_artefact || {}
      this.local_artefact.item_size = defaultItemSizeId

      if (this.activeArtefact) {
        this.activeArtefact.item_size = defaultItemSizeId
      }

      const itemSize = this.getItemSizeRecord(defaultItemSizeId)
      const defaultEnc = Number(itemSize?.enc ?? record?.item_size_enc ?? null)

      if (this.isNewArtefact && !Number.isNaN(defaultEnc) && defaultEnc > 0) {
        this.local_artefact.enc = defaultEnc
        if (this.activeArtefact) {
          this.activeArtefact.enc = defaultEnc
        }
      }
    },
    applySelectedItemSize(itemSizeId) {
      const itemSize = this.getItemSizeRecord(itemSizeId)
      if (!itemSize) {
        return
      }

      const enc = Number(itemSize.enc ?? null)
      if (this.isNewArtefact && !Number.isNaN(enc) && enc > 0) {
        this.local_artefact.enc = enc
        if (this.activeArtefact) {
          this.activeArtefact.enc = enc
        }
      }
    },
    applyContainerSizeSelection(containerSizeId) {
      const containerSize = this.getContainerSizeRecord(containerSizeId)

      this.local_artefact = this.local_artefact || {}
      this.local_artefact.container_size = containerSize?.id ?? null
      this.local_artefact.container_capacity = containerSize?.capacity ?? null
      this.local_artefact.container_size_short = containerSize?.size_short ?? ''
      this.local_artefact.container_size_long = containerSize?.size ?? ''

      if (this.activeArtefact) {
        this.activeArtefact.container_size = containerSize?.id ?? null
        this.activeArtefact.container_capacity = containerSize?.capacity ?? null
        this.activeArtefact.container_size_short =
          containerSize?.size_short ?? ''
        this.activeArtefact.container_size_long = containerSize?.size ?? ''
      }
    },
    clearArtefactBaseItemSelection() {
      this.local_artefact = this.local_artefact || {}
      this.local_artefact.artefact_base_item = null
      this.local_artefact.skills = []
      this.local_artefact.coverage = ''
      this.local_artefact.coverage_locations = []
      this.local_artefact.dex_pen = 0
      this.local_artefact.layer = 0
      this.local_artefact.ap = 0
      this.local_artefact.hp = 0
      this.local_artefact.damage_dice = null
      this.local_artefact.impale = null
      this.local_artefact.set_charge = null
      this.local_artefact.load = 0
      this.local_artefact.range = 0
      this.save_item_id = null
      this.applyArtefactSubtypeFromType(this.local_artefact?.artefact_type)

      if (this.activeArtefact) {
        this.activeArtefact.artefact_base_item = null
        this.activeArtefact.skills = []
        this.activeArtefact.coverage = ''
        this.activeArtefact.coverage_locations = []
        this.activeArtefact.dex_pen = 0
        this.activeArtefact.layer = 0
        this.activeArtefact.ap = 0
        this.activeArtefact.hp = 0
        this.activeArtefact.damage_dice = null
        this.activeArtefact.impale = null
        this.activeArtefact.set_charge = null
        this.activeArtefact.load = 0
        this.activeArtefact.range = 0
      }
    },
    applyArtefactBaseItemSelection(baseItemId, options = {}) {
      const { preserveExisting = false } = options
      const selectedBaseItemId = this.normaliseSelectionId(baseItemId)
      const getStaticItemById =
        this.$store.getters['characters/getStaticItemById']
      const baseItem =
        typeof getStaticItemById === 'function' && selectedBaseItemId
          ? getStaticItemById(selectedBaseItemId)
          : null

      this.local_artefact = this.local_artefact || {}
      this.local_artefact.artefact_base_item = selectedBaseItemId

      if (this.activeArtefact) {
        this.activeArtefact.artefact_base_item = selectedBaseItemId
      }

      this.save_item_id = selectedBaseItemId

      if (!baseItem) {
        this.applyArtefactSubtypeFromType(this.local_artefact?.artefact_type)
        return
      }

      const propertyItem = getItemPropertySource(baseItem, getStaticItemById)
      const effectiveType = getEffectiveItemType(baseItem, getStaticItemById)
      const artefactSubtype = effectiveType || baseItem.type || 'Artefact'

      this.local_artefact.type = artefactSubtype
      this.local_artefact.actual_type = artefactSubtype
      this.local_artefact.effective_type = artefactSubtype
      const existingSkills = Array.isArray(this.local_artefact.skills)
        ? this.local_artefact.skills
        : []
      const baseSkills = propertyItem?.skills ?? baseItem.skills ?? []
      const resolvedSkills =
        preserveExisting && existingSkills.length > 0
          ? existingSkills
          : baseSkills
      this.local_artefact.skills = resolvedSkills

      if (this.activeArtefact) {
        this.activeArtefact.type = artefactSubtype
        this.activeArtefact.actual_type = artefactSubtype
        this.activeArtefact.effective_type = artefactSubtype
        this.activeArtefact.skills = resolvedSkills
      }

      if (artefactSubtype === 'Armour' && propertyItem?.armour) {
        const materialAp = propertyItem.armour.armour_material?.ap ?? 0
        const materialHp = propertyItem.armour.armour_material?.hp ?? 0
        const existingAp = this.local_artefact?.ap
        const existingHp = this.local_artefact?.hp
        const resolvedAp =
          preserveExisting && existingAp !== null && existingAp !== undefined
            ? existingAp
            : materialAp
        const resolvedHp =
          preserveExisting && existingHp !== null && existingHp !== undefined
            ? existingHp
            : materialHp

        this.local_artefact.coverage = propertyItem.armour.coverage ?? ''
        this.local_artefact.dex_pen = propertyItem.armour.dex_pen ?? 0
        this.local_artefact.layer =
          propertyItem.armour.armour_material?.layer ?? 0
        this.local_artefact.ap = resolvedAp
        this.local_artefact.hp = resolvedHp
        this.local_artefact.coverage_locations =
          propertyItem.armour.coverage_locations ?? []
        this.max_ap = Math.max(materialAp, resolvedAp)
        this.max_hp = Math.max(materialHp, resolvedHp)
        this.armour_base_ap = materialAp
        this.armour_base_dex_pen = propertyItem.armour.dex_pen ?? 0

        const allCandidateLocs = Array.isArray(
          propertyItem.armour.coverage_locations,
        )
          ? propertyItem.armour.coverage_locations.filter(
              (location) => location && location.fixed == false,
            )
          : []
        this.armour_locations = allCandidateLocs

        if (this.activeArtefact) {
          this.activeArtefact.coverage = this.local_artefact.coverage
          this.activeArtefact.dex_pen = this.local_artefact.dex_pen
          this.activeArtefact.layer = this.local_artefact.layer
          this.activeArtefact.ap = this.local_artefact.ap
          this.activeArtefact.hp = this.local_artefact.hp
          this.activeArtefact.coverage_locations =
            this.local_artefact.coverage_locations
        }
      }

      if (
        artefactSubtype === 'Melee Weapon' ||
        artefactSubtype === 'Ranged Weapon'
      ) {
        this.applyWeaponArtefactFieldsFromSource(propertyItem ?? baseItem, {
          preserveExisting,
        })
      }

      if (
        this.getArtefactSubtypeName(this.local_artefact?.artefact_type) ===
          'Container' &&
        baseItem?.container_size
      ) {
        this.applyContainerSizeSelection(baseItem.container_size)
      }
    },
    //========================================================
    // normalise artefact type to lookup name
    //----------------------------------------
    normaliseArtefactTypeName(raw) {
      if (raw === null || raw === undefined || raw === '') return ''

      const record = this.getArtefactTypeRecord(raw)
      if (record?.name) return record.name

      if (typeof raw === 'object') {
        return raw.name ?? ''
      }

      return String(raw).trim()
    },
    applyWeaponArtefactFieldsFromSource(source, options = {}) {
      const { preserveExisting = false } = options
      const existingDamageDice = this.local_artefact?.damage_dice
      const existingAp = this.local_artefact?.ap
      const existingHp = this.local_artefact?.hp
      const weaponAp = source?.ap ?? 0
      const weaponHp = source?.hp ?? 0

      this.local_artefact.damage_dice =
        preserveExisting && existingDamageDice
          ? existingDamageDice
          : this.formatWeaponDamageDiceSummary(source?.skills ?? [])
      this.local_artefact.ap =
        preserveExisting && existingAp !== null && existingAp !== undefined
          ? existingAp
          : weaponAp
      this.local_artefact.hp =
        preserveExisting && existingHp !== null && existingHp !== undefined
          ? existingHp
          : weaponHp
      this.local_artefact.impale = source?.impale ? 'Yes' : 'No'
      this.local_artefact.set_charge = source?.set_charge ? 'Yes' : 'No'
      this.local_artefact.load = source?.load ?? 0
      this.local_artefact.range = source?.range ?? 0
      this.max_ap = Math.max(weaponAp, this.local_artefact.ap ?? weaponAp)
      this.max_hp = Math.max(weaponHp, this.local_artefact.hp ?? weaponHp)

      if (this.activeArtefact) {
        this.activeArtefact.damage_dice = this.local_artefact.damage_dice
        this.activeArtefact.ap = this.local_artefact.ap
        this.activeArtefact.hp = this.local_artefact.hp
        this.activeArtefact.impale = this.local_artefact.impale
        this.activeArtefact.set_charge = this.local_artefact.set_charge
        this.activeArtefact.load = this.local_artefact.load
        this.activeArtefact.range = this.local_artefact.range
      }
    },
    getWeaponSkillLabel(skillName) {
      if (!skillName || typeof skillName !== 'string') {
        return 'Skill'
      }

      if (/\b1h\b/i.test(skillName)) return '1H'
      if (/\b2h\b/i.test(skillName)) return '2H'

      return skillName
    },
    formatWeaponDamageDiceSummary(skills) {
      const linkedSkills = Array.isArray(skills)
        ? skills.filter(
            (skill) =>
              skill &&
              typeof skill.damage_dice === 'string' &&
              skill.damage_dice.trim() !== '',
          )
        : []

      if (!linkedSkills.length) {
        return ''
      }

      const uniqueDamageDice = [
        ...new Set(linkedSkills.map((skill) => skill.damage_dice.trim())),
      ]

      if (linkedSkills.length === 1 || uniqueDamageDice.length === 1) {
        return uniqueDamageDice[0]
      }

      return linkedSkills
        .map(
          (skill) =>
            `${this.getWeaponSkillLabel(skill.name)} (${skill.damage_dice.trim()})`,
        )
        .join(' / ')
    },
    hydrateWeaponSkillDamageDiceFromPersistedSummary() {
      const persistedSummary =
        typeof this.local_artefact?.damage_dice === 'string'
          ? this.local_artefact.damage_dice.trim()
          : ''
      const skills = Array.isArray(this.local_artefact?.skills)
        ? [...this.local_artefact.skills]
        : []

      if (!persistedSummary || skills.length !== 1) {
        return false
      }

      const currentDamageDice =
        typeof skills[0]?.damage_dice === 'string'
          ? skills[0].damage_dice.trim()
          : ''

      if (currentDamageDice === persistedSummary) {
        return false
      }

      skills[0] = {
        ...skills[0],
        damage_dice: persistedSummary,
      }
      this.local_artefact.skills = skills

      if (this.activeArtefact) {
        this.activeArtefact.skills = skills
      }

      return true
    },
    syncWeaponDamageDiceSummary() {
      const summary = this.formatWeaponDamageDiceSummary(
        this.local_artefact?.skills ?? [],
      )

      this.local_artefact.damage_dice = summary
      if (this.activeArtefact) {
        this.activeArtefact.damage_dice = summary
      }

      return summary
    },
    onWeaponSkillDamageDiceChange(row, value) {
      const skills = Array.isArray(this.local_artefact?.skills)
        ? [...this.local_artefact.skills]
        : []
      const normalisedValue = typeof value === 'string' ? value.trim() : ''
      const rowSkillId = row?.skill_id ?? row?.skill ?? row?.id ?? null
      const skillIdx = skills.findIndex((skill) => {
        const skillId = skill?.skill ?? skill?.skill_id ?? skill?.id ?? null
        return skillId === rowSkillId || skill?.name === row?.name
      })

      if (skillIdx < 0) {
        return
      }

      skills[skillIdx] = {
        ...skills[skillIdx],
        damage_dice: normalisedValue,
      }
      this.local_artefact.skills = skills

      if (this.activeArtefact) {
        this.activeArtefact.skills = skills
      }

      const summary = this.syncWeaponDamageDiceSummary()

      if (this.isValidWeaponDamageDiceOverride(normalisedValue)) {
        this.clearInputError(`skill_damage_dice-${rowSkillId}`)
      }

      if (!this.isNewArtefact) {
        this.emitUpdate('skills', skills)
        this.emitUpdate('damage_dice', summary)
      }
    },
    isValidWeaponDamageDiceOverride(value) {
      if (typeof value !== 'string') {
        return false
      }

      const damageDicePattern = '(?:D|[2-6]D)(1[0-2]|[1-9])(?:[+-][1-8])?'
      const handedDamageDicePattern = `(?:1H \\(${damageDicePattern}\\)|2H \\(${damageDicePattern}\\))`
      const dualHandedDamageDicePattern = `(?:1H \\(${damageDicePattern}\\)\\s*/\\s*2H \\(${damageDicePattern}\\)|2H \\(${damageDicePattern}\\)\\s*/\\s*1H \\(${damageDicePattern}\\))`
      const validPattern = new RegExp(
        `^(?:${damageDicePattern}|${handedDamageDicePattern}|${dualHandedDamageDicePattern})$`,
      )

      return validPattern.test(value.trim())
    },
    //========================================================
    // get artefact type record
    //-------------------------
    getArtefactTypeRecord(raw) {
      const records = Array.isArray(this.artefact_type_records)
        ? this.artefact_type_records
        : []

      if (!records.length || raw === null || raw === undefined || raw === '') {
        return null
      }

      const rawId = typeof raw === 'object' ? (raw.id ?? null) : null
      if (rawId) {
        const byId = records.find((record) => record?.id === rawId)
        if (byId) return byId
      }

      const rawName =
        typeof raw === 'object' ? (raw.name ?? '') : String(raw).trim()
      const normalisedName = rawName.toLowerCase()

      return (
        records.find(
          (record) =>
            typeof record?.name === 'string' &&
            record.name.trim().toLowerCase() === normalisedName,
        ) ?? null
      )
    },
    //========================================================
    // apply artefact type flag values
    //--------------------------------
    applyArtefactTypeFlags(rawArtefactType) {
      const record = this.getArtefactTypeRecord(rawArtefactType)
      const flagFields = [
        'tallyable',
        'wearable',
        'storeable',
        'storage',
        'attachable',
        'attach_point',
        'countable',
      ]

      this.local_artefact = this.local_artefact || {}

      flagFields.forEach((field) => {
        const fieldValue = record ? !!record[field] : false
        this.local_artefact[field] = fieldValue
        if (this.activeArtefact) {
          this.activeArtefact[field] = fieldValue
        }
      })

      if (!this.local_artefact.countable) {
        this.local_artefact.unit_name = null
        this.local_artefact.max_unit_count = 0

        if (this.activeArtefact) {
          this.activeArtefact.unit_name = null
          this.activeArtefact.max_unit_count = 0
        }
      }

      this.enforceCountAndTallyRules({ emitUpdates: false })
    },
    //========================================================
    // derive ENC from selected artefact type
    //----------------------------------------
    getArtefactTypeDerivedEnc(rawArtefactType) {
      const record = this.getArtefactTypeRecord(rawArtefactType)
      if (!record) return null

      const directEnc = Number(record?.item_size_enc)
      if (!Number.isNaN(directEnc) && directEnc > 0) {
        return directEnc
      }

      const nestedEnc = Number(record?.item_size?.enc)
      if (!Number.isNaN(nestedEnc) && nestedEnc > 0) {
        return nestedEnc
      }

      return null
    },
    //========================================================
    // apply artefact type ENC value
    //--------------------------------
    applyArtefactTypeEnc(rawArtefactType) {
      if (!this.isNewArtefact) {
        return
      }

      const derivedEnc = this.getArtefactTypeDerivedEnc(rawArtefactType)
      if (derivedEnc === null) return

      this.local_artefact = this.local_artefact || {}
      this.local_artefact.enc = derivedEnc

      if (this.activeArtefact) {
        this.activeArtefact.enc = derivedEnc
      }
    },
    //========================================================
    // normalise currency value to id
    //--------------------------------
    normaliseCurrencyId(raw) {
      if (raw === null || raw === undefined || raw === '') return null

      if (typeof raw === 'object') {
        return raw.id ?? null
      }

      const list = Array.isArray(this.currencies) ? this.currencies : []
      const byId = list.find((currency) => currency && currency.id === raw)
      if (byId) return byId.id

      const byCode = list.find(
        (currency) =>
          currency && (currency.short_name === raw || currency.name === raw),
      )
      if (byCode) return byCode.id

      return null
    },
    //========================================================
    // sync artefact currency selection
    //----------------------------------
    syncArtefactCurrencySelection() {
      if (!this.local_artefact) {
        return
      }

      const staticItem = this.activeArtefact ?? this.baseStaticItem
      const characterCurrencyId = this.normaliseCurrencyId(this.item?.currency)
      const staticCurrencyId = this.normaliseCurrencyId(staticItem?.currency)
      const localCurrencyId = this.normaliseCurrencyId(
        this.local_artefact.currency,
      )

      this.local_artefact.currency =
        characterCurrencyId ??
        staticCurrencyId ??
        localCurrencyId ??
        this.default_currency?.id ??
        null
    },
    //========================================================
    // get attach location label
    //---------------------------
    getAttachLocationLabel(option) {
      if (!option) return ''
      return option.display_name || option.name || ''
    },
    //========================================================
    // on key down
    //-------------
    onKeyDown(e) {
      const tagName = e?.target?.tagName?.toLowerCase?.() || ''
      const inputType = e?.target?.type?.toLowerCase?.() || ''

      if (
        tagName === 'textarea' ||
        (tagName === 'input' &&
          ['text', 'search', 'email', 'url', 'tel', 'password'].includes(
            inputType,
          ))
      ) {
        return
      }

      if (!isAlphanumeric(e)) e.preventDefault()
    },
    //========================================================
    // prevent negative sign in number fields
    //----------------------------------------
    preventNegative(e) {
      // block minus, plus, decimal, and exponent keys
      const key = e.key
      if (
        key === '-' ||
        key === '+' ||
        key === 'Minus' ||
        key === 'Add' ||
        key === 'Subtract' ||
        key === '.' ||
        key === 'e' ||
        key === 'E'
      ) {
        e.preventDefault()
      }
    },
    //========================================================
    // capitalise first letter of text inputs
    //----------------------------------------
    onCapitaliseFirstLetter(field, event) {
      if (!event || !event.target) return
      const raw = event.target.value ?? ''
      const capitalised = normaliseLeadingLetterText(raw)
      const selectionStart = event.target.selectionStart
      const selectionEnd = event.target.selectionEnd

      // update the underlying item fields
      if (this.activeArtefact && field in this.activeArtefact) {
        this.activeArtefact[field] = capitalised
      }

      if (this.local_artefact && field in this.local_artefact) {
        this.local_artefact[field] = capitalised
      }

      // keep the input value in sync only when the transform changed it,
      // otherwise let the browser preserve the typed text as-is.
      if (capitalised !== raw) {
        event.target.value = capitalised

        if (
          typeof selectionStart === 'number' &&
          typeof selectionEnd === 'number' &&
          typeof event.target.setSelectionRange === 'function'
        ) {
          event.target.setSelectionRange(selectionStart, selectionEnd)
        }
      }

      // emit update
      this.delayedEmitUpdate(field, capitalised)
    },
    //========================================================
    // generic numeric input handler for display* fields
    //---------------------------------------------------
    handleNumericInput({
      displayProp,
      emitField,
      event,
      min = 0,
      max = 9999,
      zeroIfBlank = false,
    }) {
      if (!event || !event.target || !displayProp) return

      const raw = event.target.value ?? ''
      const cleaned = sanitisePositiveInt(raw, min, max)

      // update the corresponding display* computed, which in turn
      // writes through to local_artefact via its setter
      this[displayProp] = cleaned

      // optionally emit realtime updates for existing artefacts
      if (emitField && !this.isNewArtefact) {
        let num
        if (cleaned === '') {
          num = zeroIfBlank ? 0 : null
        } else {
          num = parseInt(cleaned, 10)
        }
        this.delayedEmitUpdate(emitField, num)
      }

      // keep artefact value/currency validation in sync whenever
      // either value_lower or value_upper is edited
      if (
        displayProp === 'displayValueLower' ||
        displayProp === 'displayValueUpper'
      ) {
        this.validateValueRange()
      }
    },
    //========================================================
    // armour overlap validation (layer/location)
    //--------------------------------------------
    checkArmourLayerConflict() {
      // only applies to armour artefacts with a character + armour locations
      if (this.resolvedItemType !== 'Armour') return null
      if (!Array.isArray(this.relatedArmourLocations)) return null

      const item = this.activeArtefact || {}
      const layer = Number(item.layer)
      if (Number.isNaN(layer)) return null

      const coverageLocs = Array.isArray(item.coverage_locations)
        ? item.coverage_locations
        : []

      // determine which armour-location names this item will cover when equipped
      let targetNames = []
      if (item.armour_location) {
        const match = coverageLocs.find(
          (loc) => String(loc.id) === String(item.armour_location),
        )
        if (match && match.name) {
          targetNames.push(match.name)
        }
      } else {
        targetNames = coverageLocs
          .filter((loc) => loc && loc.fixed && loc.name)
          .map((loc) => loc.name)
      }

      if (!targetNames.length) return null

      const ignoreId = item.id ?? null

      for (const cal of this.relatedArmourLocations) {
        if (!cal || !targetNames.includes(cal.armour_location_name)) continue

        const equippedList = Array.isArray(cal.equipped_armour)
          ? cal.equipped_armour
          : []

        for (const ea of equippedList) {
          if (!ea) continue
          const existingLayer = Number(ea.layer)
          if (Number.isNaN(existingLayer)) continue
          // ignore the same item when editing
          if (ignoreId && ea.armour === ignoreId) continue

          if (existingLayer === layer) {
            const existingName = ea.armour_name || 'Existing armour'
            return {
              locationName: cal.armour_location_name,
              existingArmourName: existingName,
              message: window.bbUiErrorMsg(
                `Cannot equip armour: layer ${layer} already used at ${cal.armour_location_name} by ${existingName}`,
              ),
            }
          }
        }
      }

      return null
    },
    //========================================================
    // get parent container chain (immediate -> top)
    //-----------------------------------------------
    getParentContainerChain(containerId) {
      const chain = []
      if (!containerId || !Array.isArray(this.relatedArtefacts)) {
        return chain
      }

      let current =
        this.relatedArtefacts.find((c) => c.id === containerId) || null
      while (current && current.container) {
        const parentId = current.container
        const parent =
          this.relatedArtefacts.find((c) => c.id === parentId) || null
        if (
          !parent ||
          !parent.container_size ||
          typeof parent.container_size.capacity !== 'number'
        ) {
          break
        }

        const capacity = parent.container_size.capacity
        const contentsEnc = parent.contents_enc ?? 0
        const rawAvailable = capacity - contentsEnc
        const availability =
          Math.round((rawAvailable + Number.EPSILON) * 100) / 100

        chain.push({
          id: parent.id,
          name: parent.display_name || parent.name || '',
          capacity,
          contents_enc: contentsEnc,
          availability,
        })

        current = parent
      }

      return chain
    },
    // is container availability on tally change
    //-------------------------------------------
    isContainerAvailabilityOnTallyChange({ ref, count, prevCount }) {
      let availability = true

      console.log(
        'onCounterChanged: tally_qty change > 0 with container id = ',
        this.activeArtefact.container,
      )

      // get enc/capacity details for container
      const container = calcContainerAvailability({
        container_id: this.activeArtefact.container,
        character: { artefacts: this.relatedArtefacts },
      })
      // guard error
      if (isEmpty(container)) {
        console.error(
          'Unable to calculate container availability for container id ',
          this.activeArtefact.container,
        )
      } else {
        const encPerItem = parseFloat(this.activeArtefact.enc || 0)
        if (Number.isNaN(encPerItem)) {
          console.warn(
            'isContainerAvailabilityOnTallyChange: invalid encPerItem = ',
            this.activeArtefact.enc,
          )
          return true
        }

        const capacity = container.capacity
        const contentsEnc = container.contents_enc

        console.log(
          'onCounterChanged: capacity = ',
          capacity,
          ' contentsEnc = ',
          contentsEnc,
          ' prevCount = ',
          prevCount,
          ' new count = ',
          count,
          ' isNewArtefact = ',
          this.isNewArtefact,
        )

        // ENC this item currently occupies and will occupy after the change
        const prevEncForThisItemRaw = encPerItem * prevCount
        const prevEncForThisItem =
          Math.round((prevEncForThisItemRaw + Number.EPSILON) * 100) / 100

        const newTotalEncForThisItemRaw = encPerItem * count
        const newTotalEncForThisItem =
          Math.round((newTotalEncForThisItemRaw + Number.EPSILON) * 100) / 100

        // incremental ENC change for this item
        const deltaEncRaw = newTotalEncForThisItem - prevEncForThisItem
        const deltaEnc = Math.round((deltaEncRaw + Number.EPSILON) * 100) / 100

        console.log(
          'onCounterChanged: prevEncForThisItem = ',
          prevEncForThisItem,
          ' newTotalEncForThisItem = ',
          newTotalEncForThisItem,
          ' deltaEnc = ',
          deltaEnc,
        )

        // if ENC is being reduced or unchanged, no capacity issue anywhere
        if (deltaEnc <= 0) {
          return true
        }

        // 1) check immediate container capacity using contents_enc + delta
        const newContentsEncRaw = contentsEnc + deltaEnc
        const newContentsEnc =
          Math.round((newContentsEncRaw + Number.EPSILON) * 100) / 100
        const availableBeforeRaw = capacity - contentsEnc
        const availableBefore =
          Math.round((availableBeforeRaw + Number.EPSILON) * 100) / 100

        console.log(
          'onCounterChanged: immediate container check: capacity = ',
          capacity,
          ' contentsEnc = ',
          contentsEnc,
          ' deltaEnc = ',
          deltaEnc,
          ' newContentsEnc = ',
          newContentsEnc,
        )

        if (newContentsEnc - capacity > 0) {
          this.clearInputError('tally_qty')

          this.errors.push(
            window.bbUiErrorMsg(
              `Cannot change tally qty to ${count}: ${container.name} capacity (${this.formatEnc(
                capacity,
              )}), available enc capacity (${this.formatEnc(
                availableBefore,
              )}).\nRequired extra (${this.formatEnc(deltaEnc)})`,
            ),
          )
          this.errorFields.push('tally_qty')

          this.local_artefact[ref] = prevCount
          if (
            this.$refs.tallyQtyCounter &&
            typeof this.$refs.tallyQtyCounter.setCount === 'function'
          ) {
            this.$refs.tallyQtyCounter.setCount(prevCount)
          }
          availability = false
        }

        // 2) if immediate container ok, ensure all parent containers
        //    up the chain also have enough spare capacity for deltaEnc
        if (availability) {
          const parentChain = this.getParentContainerChain(
            this.activeArtefact.container,
          )
          for (const parent of parentChain) {
            const parentCapacity = parent.capacity
            const parentContentsEnc = parent.contents_enc
            const parentNewContentsRaw = parentContentsEnc + deltaEnc
            const parentNewContents =
              Math.round((parentNewContentsRaw + Number.EPSILON) * 100) / 100
            const parentAvailableBeforeRaw = parentCapacity - parentContentsEnc
            const parentAvailableBefore =
              Math.round((parentAvailableBeforeRaw + Number.EPSILON) * 100) /
              100

            console.log(
              'onCounterChanged: parent container check: ',
              parent.name,
              ' capacity = ',
              parentCapacity,
              ' contentsEnc = ',
              parentContentsEnc,
              ' deltaEnc = ',
              deltaEnc,
              ' newContentsEnc = ',
              parentNewContents,
            )

            if (parentNewContents - parentCapacity > 0) {
              this.clearInputError('tally_qty')

              // build container chain string from the immediate container
              // up to the parent that exceeds capacity
              const immediateName = container.name || 'Container'
              const parentIndex = parentChain.findIndex(
                (p) => p.id === parent.id,
              )
              const chainUpToParent =
                parentIndex === -1
                  ? parentChain
                  : parentChain.slice(0, parentIndex + 1)
              const chainNames = [
                immediateName,
                ...chainUpToParent.map((p) => p.name).filter(Boolean),
              ]
              const chainLabel = chainNames.join(' -> ')

              this.errors.push(
                window.bbUiErrorMsg(
                  `Cannot change tally qty to ${count}: Container chain: ${chainLabel}.\nAvailable enc capacity of parent container (${this.formatEnc(
                    parentAvailableBefore,
                  )}). Required (${this.formatEnc(deltaEnc)})`,
                ),
              )
              this.errorFields.push('tally_qty')

              this.local_artefact[ref] = prevCount
              if (
                this.$refs.tallyQtyCounter &&
                typeof this.$refs.tallyQtyCounter.setCount === 'function'
              ) {
                this.$refs.tallyQtyCounter.setCount(prevCount)
              }
              availability = false
              break
            }
          }
        }
      }
      return availability
    },
    //========================================================
    // on healing field changed
    //---------------------------
    onHealingFieldChanged(field, value) {
      this.local_artefact = this.local_artefact || {}
      this.local_artefact[field] = value
      const hasValue =
        value !== null &&
        value !== undefined &&
        (typeof value !== 'string' || value.trim() !== '')

      if (field === 'pot') {
        const potency = Number(value)
        const isValidPotency =
          hasValue && Number.isFinite(potency) && potency >= 1 && potency <= 99

        if (isValidPotency) {
          this.clearInputError(field)
        }
        return
      }

      if (hasValue) {
        this.clearInputError(field)
      }
    },
    //========================================================
    // on counter changed
    //--------------------
    async onCounterChanged({ ref, count }) {
      console.log(
        'onCounterChanged ref = ',
        ref,
        ' count = ',
        count,
        ' isNewArtefact = ',
        this.isNewArtefact,
      )
      //--------------------------
      // ensure local item exists
      //--------------------------
      this.local_artefact = this.local_artefact || {}

      this.local_artefact[ref] = count
      if (this.activeArtefact && this.activeArtefact !== this.local_artefact) {
        this.activeArtefact[ref] = count
      }

      this.clearInputError(ref)

      // When AP changes for armour, recompute dex_pen based on the
      // campaign-artefact AP while preserving the original static ratio.
      if (
        ref === 'ap' &&
        this.resolvedItemType === 'Armour' &&
        this.armour_base_ap > 0
      ) {
        const ratio = Number(count) / this.armour_base_ap
        if (!Number.isNaN(ratio) && this.armour_base_dex_pen >= 0) {
          const newDexPen = this.armour_base_dex_pen * ratio
          this.local_artefact.dex_pen = newDexPen
          if (!this.isNewArtefact) {
            this.emitUpdate('dex_pen', newDexPen)
          }
        }
      }

      if (!this.isNewArtefact) {
        this.emitUpdate(ref, count)
      }
    },
    //========================================================
    // on item select
    //-----------------
    onArtefactselect(name) {
      console.log('onArtefactselect name = ', name)

      //---------------------------
      // ensure local item exists

      this.local_artefact = this.local_artefact || {}
      this.local_artefact.name = name || ''

      // changing the base item should clear any previous
      // validation errors (including equip-layer conflicts)
      this.errors = []
      this.errorFields = []

      if (!name) return

      //--------------------------
      // get specific static item

      const getItemByName =
        this.$store.getters['characters/getStaticItemByName']
      const getStaticItemById =
        this.$store.getters['characters/getStaticItemById']
      const item =
        typeof getItemByName === 'function' ? getItemByName(name) : null
      const propertyItem = getItemPropertySource(item, getStaticItemById)
      const effectiveType = getEffectiveItemType(item, getStaticItemById)

      console.log('onArtefactselect item = ', item)

      if (!item) {
        console.error('selected item not found in getStaticItemByName:', name)
        return
      } else {
        //------------------------------------
        // got static item - set local values

        const hasStaticShortDescription =
          typeof item.short_description === 'string' &&
          item.short_description.trim() !== ''

        this.save_item_id = item.id
        console.log('save_item_id = ', this.save_item_id)
        this.local_artefact.type = item.type ?? ''
        this.local_artefact.actual_type = item.type ?? ''
        this.local_artefact.effective_type = effectiveType || item.type || ''
        this.local_artefact.enc = item.enc ?? ''
        this.local_artefact.currency_cost = item.currency_cost ?? ''
        this.local_artefact.equipment_type = getNormalisedEquipmentType(
          item.equipment_type,
        )

        this.local_artefact.short_description = item.short_description ?? ''
        this.local_artefact.description = item.description ?? ''
        this.local_artefact.pwr_storage = item.pwr_storage ?? 0
        this.local_artefact.trait = item.trait ?? null
        this.local_artefact.item_traits = Array.isArray(item.item_traits)
          ? [...item.item_traits]
          : []

        // -------------------------------------------------------
        // flags
        this.local_artefact.active = item.active ?? false
        this.local_artefact.campaign = true
        this.local_artefact.starter = item.starter ?? false
        const resolvedTallyable = this.toBooleanFlag(
          item.tallyable ??
            propertyItem?.tallyable ??
            this.basePropertyItem?.tallyable ??
            this.baseStaticItem?.tallyable ??
            false,
        )
        this.local_artefact.tallyable = resolvedTallyable
        this.local_artefact.wearable = item.wearable ?? false
        this.local_artefact.storeable = item.storeable ?? false
        this.local_artefact.storage = item.storage ?? false
        this.local_artefact.attach_point = item.attach_point ?? false
        this.local_artefact.attachable = item.attachable ?? false
        const resolvedCountable = this.toBooleanFlag(
          item.countable ??
            propertyItem?.countable ??
            this.basePropertyItem?.countable ??
            this.baseStaticItem?.countable ??
            false,
        )
        this.local_artefact.countable = resolvedCountable

        if (this.activeArtefact) {
          this.activeArtefact.campaign = true
          this.activeArtefact.tallyable = resolvedTallyable
          this.activeArtefact.countable = resolvedCountable
        }

        // -------------------------------------------------------
        // set countable values
        // - when countable=false: unit_name=null and unit counts are 0
        // - when unit_name is null: ensure unit counts are 0

        if (this.local_artefact.countable) {
          const staticUnitName =
            this.basePropertyItem?.unit_name ??
            this.baseStaticItem?.unit_name ??
            null
          const unitName = item.unit_name ?? staticUnitName ?? null
          this.local_artefact.unit_name = unitName

          const baseMax = item.max_unit_count ?? 0
          const initialMax = this.getInitialMaxUnitCount(unitName, baseMax)

          // starting values (can be changed on the tally/healing tabs)
          this.local_artefact.max_unit_count = initialMax
        } else {
          this.local_artefact.unit_name = null
          this.local_artefact.max_unit_count = 0
        }

        // -------------------------------------------------------
        // set container/storage values

        this.local_artefact.container_capacity =
          item.container_size?.capacity ?? null
        this.local_artefact.container_size_short =
          item.container_size?.size_short ?? null
        this.local_artefact.container_size_long =
          item.container_size?.size ?? null
        this.local_artefact.container_size = item.container_size ?? null

        if (this.local_artefact.storage) {
          this.local_artefact.contents_count_direct = 0
          this.local_artefact.contents_count_total = 0
        } else {
          this.local_artefact.contents_count_direct = null
          this.local_artefact.contents_count_total = null
        }

        // -------------------------------------------------------
        // set armour values

        if (effectiveType === 'Armour' && propertyItem?.armour) {
          this.local_artefact.equipped = false
          this.local_artefact.broken = false
          this.local_artefact.coverage = propertyItem.armour.coverage ?? ''
          // API exposes dex_pen on static armour
          this.local_artefact.dex_pen = propertyItem.armour.dex_pen ?? 0
          this.local_artefact.layer =
            propertyItem.armour.armour_material.layer ?? 0
          this.local_artefact.ap = propertyItem.armour.armour_material.ap ?? 0
          this.local_artefact.hp = propertyItem.armour.armour_material.hp ?? 0
          const materialAp = propertyItem.armour.armour_material.ap ?? 0
          this.max_ap = materialAp
          // capture baseline values for later dex-pen recalculation
          this.armour_base_ap = materialAp
          this.armour_base_dex_pen =
            typeof propertyItem.armour.dex_pen === 'number'
              ? propertyItem.armour.dex_pen
              : 0
          this.max_hp = propertyItem.armour.armour_material.hp ?? 0
          this.local_artefact.coverage_locations =
            propertyItem.armour.coverage_locations ?? []

          // armour locations (non-fixed coverage locations i.e. need selection e.g. left/right arm)
          const allCandidateLocs = Array.isArray(
            propertyItem.armour.coverage_locations,
          )
            ? propertyItem.armour.coverage_locations.filter(
                (a) => a && a.fixed == false,
              )
            : []

          const currentItemId = this.local_artefact.id ?? null
          const baseName = propertyItem.name || item.name || ''

          this.armour_locations = allCandidateLocs.filter((loc) => {
            if (!loc || !loc.name) return false
            const cal = Array.isArray(this.relatedArmourLocations)
              ? this.relatedArmourLocations.find(
                  (c) => c && c.armour_location_name === loc.name,
                )
              : null

            if (!cal || !Array.isArray(cal.equipped_armour)) return true

            const conflict = cal.equipped_armour.some((ea) => {
              if (!ea) return false
              if (currentItemId && ea.armour === currentItemId) return false
              return ea.armour_name === baseName
            })

            return !conflict
          })

          // -------------------------------------------------------
          // set weapon values
        } else if (
          effectiveType === 'Melee Weapon' ||
          effectiveType === 'Ranged Weapon'
        ) {
          const resolvedWeaponItem = propertyItem ?? this.local_artefact ?? item

          this.local_artefact.broken = false
          this.local_artefact.worn = false
          this.local_artefact.worn_location = null
          this.local_artefact.equipped = false
          const weaponAp = resolvedWeaponItem?.ap ?? 0
          const weaponHp = resolvedWeaponItem?.hp ?? 0
          const resolvedWeaponAp = item?.ap ?? weaponAp
          const resolvedWeaponHp = item?.hp ?? weaponHp
          this.local_artefact.ap = resolvedWeaponAp
          this.local_artefact.hp = resolvedWeaponHp
          this.max_ap = Math.max(weaponAp, resolvedWeaponAp)
          this.max_hp = Math.max(weaponHp, resolvedWeaponHp)
          this.local_artefact.impale = resolvedWeaponItem?.impale ? 'Yes' : 'No'
          this.local_artefact.set_charge = resolvedWeaponItem?.set_charge
            ? 'Yes'
            : 'No'
          this.local_artefact.load = resolvedWeaponItem?.load ?? 0
          this.local_artefact.range = resolvedWeaponItem?.range ?? 0
          this.local_artefact.skills =
            resolvedWeaponItem?.skills ?? this.local_artefact.skills ?? []
          this.syncWeaponDamageDiceSummary()
          if (this.activeArtefact) {
            this.activeArtefact.damage_dice = this.local_artefact.damage_dice
            this.activeArtefact.ap = this.local_artefact.ap
            this.activeArtefact.hp = this.local_artefact.hp
          }
          // get cur str/dex chars
          this.local_artefact.str_cur =
            this.relatedCharacteristics.find((c) => c.name === 'str')?.cur ?? 0
          this.local_artefact.dex_cur =
            this.relatedCharacteristics.find((c) => c.name === 'dex')?.cur ?? 0

          // -------------------------------------------------------
          // set healing values
        } else if (item.type === 'Healing') {
          this.local_artefact.healing_type = item.healing_type ?? null
          this.local_artefact.recovers = item.recovers ?? null
          this.local_artefact.delay = item.delay ?? null
          if (!hasStaticShortDescription) {
            this.local_artefact.short_description =
              this.buildHealingShortDescription(this.local_artefact.recovers)
          }

          if (this.activeArtefact) {
            this.activeArtefact.short_description =
              this.local_artefact.short_description
          }
        } else if (item.type === 'Poison') {
          this.local_artefact.poison_type = item.poison_type ?? null
          this.local_artefact.pot = this.isNewArtefact
            ? item.pot || 10
            : (item.pot ?? null)
          this.local_artefact.effect = item.effect ?? null
          this.local_artefact.delay = item.delay ?? null
          this.local_artefact.damage_dice = item.damage_dice ?? null
          this.local_artefact.duration = item.duration ?? null
          this.local_artefact.resist = item.resist ?? null
          if (!hasStaticShortDescription) {
            this.local_artefact.short_description =
              this.buildPoisonShortDescription(
                this.local_artefact.effect,
                this.local_artefact.damage_dice,
                this.local_artefact.duration,
              )
          }

          if (this.activeArtefact) {
            this.activeArtefact.resist = this.local_artefact.resist
            this.activeArtefact.short_description =
              this.local_artefact.short_description
          }
        } else if (item.type === 'Specialist') {
          this.local_artefact.effect = item.effect ?? null
          this.local_artefact.delay = item.delay ?? null
          this.local_artefact.damage_dice = item.damage_dice ?? null
          this.local_artefact.duration = item.duration ?? null
          this.local_artefact.resist = item.resist ?? null

          if (this.activeArtefact) {
            this.activeArtefact.effect = this.local_artefact.effect
            this.activeArtefact.delay = this.local_artefact.delay
            this.activeArtefact.damage_dice = this.local_artefact.damage_dice
            this.activeArtefact.duration = this.local_artefact.duration
            this.activeArtefact.resist = this.local_artefact.resist
          }
        }

        // -------------------------------------------------------
        // set artefact values
        if (item.type === 'Artefact') {
          const staticArtefactType = item.artefact_type ?? ''
          const staticMaterial = item.material ?? 'Unknown'
          const staticQuality = item.quality ?? 'Unknown'
          const staticAge = item.age ?? 'Unknown'
          const staticArtefact = item.artefact ?? 'Unknown'
          const staticCurrencyRaw = item.currency ?? null
          const staticEnc = item.enc
          const staticCountable = item.countable
          const staticUnitName = item.unit_name ?? null
          const staticMaxUnitCount = item.max_unit_count ?? null
          const rawValueLower = item.value_lower
          const rawValueUpper = item.value_upper
          const staticValueLower = rawValueLower ?? 0
          const staticValueUpper = rawValueUpper ?? 0
          const estimatedValue = item.estimated_value ?? false
          const image = item.image ?? null

          // normalise various currency representations (object/id/short code)
          // to a clean currency id so the v-select with :reduce="reduceObject"
          // can display the correct option.
          const normaliseCurrencyId = (raw) => {
            if (raw === null || raw === undefined || raw === '') return null
            if (typeof raw === 'object') return raw.id ?? null

            const list = Array.isArray(this.currencies) ? this.currencies : []
            const byId = list.find((c) => c && c.id === raw)
            if (byId) return byId.id

            const byCode = list.find(
              (c) => c && (c.short_name === raw || c.name === raw),
            )
            return byCode ? byCode.id : null
          }

          const staticCurrencyId = normaliseCurrencyId(staticCurrencyRaw)

          this.local_artefact.artefact_type = staticArtefactType
          this.local_artefact.material = staticMaterial
          this.local_artefact.quality = staticQuality
          this.local_artefact.age = staticAge
          this.local_artefact.artefact = staticArtefact
          this.local_artefact.currency =
            staticCurrencyId ?? this.local_artefact.currency ?? null
          this.local_artefact.value_lower = staticValueLower
          this.local_artefact.value_upper = staticValueUpper
          this.local_artefact.estimated_value = estimatedValue
          this.local_artefact.image = image

          // For NEW artefacts, remember which lookup
          // values came from an "Unknown" static default so the
          // Artefact tab can allow those specific fields to be
          // edited (and re-edited) until the item is saved.
          if (this.isNewArtefact) {
            const hasStaticLower =
              rawValueLower !== null &&
              rawValueLower !== undefined &&
              rawValueLower !== 0
            const hasStaticUpper =
              rawValueUpper !== null &&
              rawValueUpper !== undefined &&
              rawValueUpper !== 0
            const hasStaticEnc = staticEnc !== null && staticEnc !== undefined
            const hasStaticMaxUnitCount =
              staticMaxUnitCount !== null && staticMaxUnitCount !== undefined
            this.local_artefact._static_material_unknown =
              !staticMaterial || staticMaterial === 'Unknown'
            this.local_artefact._static_quality_unknown =
              !staticQuality || staticQuality === 'Unknown'
            this.local_artefact._static_age_unknown =
              !staticAge || staticAge === 'Unknown'
            this.local_artefact._static_artefact_unknown =
              !staticArtefact || staticArtefact === 'Unknown'
            this.local_artefact._session_material_editable =
              this.local_artefact._static_material_unknown
            this.local_artefact._session_quality_editable =
              this.local_artefact._static_quality_unknown
            this.local_artefact._session_age_editable =
              this.local_artefact._static_age_unknown
            this.local_artefact._session_artefact_editable =
              this.local_artefact._static_artefact_unknown
            this.local_artefact._static_currency_unknown = !staticCurrencyId
            // per-field flags so lower/upper can be independently
            // editable when their static value is zero/blank
            this.local_artefact._static_value_lower_unknown = !hasStaticLower
            this.local_artefact._static_value_upper_unknown = !hasStaticUpper
            // keep aggregate flag for any-static-value cases (backwards compat)
            this.local_artefact._static_value_unknown =
              !hasStaticLower && !hasStaticUpper
            // static-driven fields: when the
            // static item defines these, treat them as read-only on
            // the Tally tab for new artefacts.
            this.local_artefact._static_enc_readonly = !!hasStaticEnc
            this.local_artefact._static_countable_readonly =
              staticCountable === true || staticCountable === false
            this.local_artefact._static_unit_name_readonly = !!staticUnitName
            this.local_artefact._static_max_unit_count_readonly =
              !!hasStaticMaxUnitCount
            // estimated flag from the static item: when present, the
            // Estimated/Actual toggle becomes read-only for new
            // new artefacts.
            this.local_artefact._static_estimated_value_readonly =
              !!estimatedValue
          }
        }
      }
    },
    //========================================================
    // on container select
    //---------------------
    onContainerSelect(containerId) {
      console.log('onContainerSelect containerId = ', containerId)

      const selected = this.containers.find((c) => c.id === containerId) || null
      const name = selected?.name ?? null
      console.log('onContainerSelect id = ', containerId, ' name = ', name)
      //--------------------------
      // ensure local item exists
      //--------------------------
      this.local_artefact = this.local_artefact || {}

      // last valid container selection before this change
      const prevContainer = this.previousContainerId ?? null

      // allow clearing container without capacity checks
      if (!containerId) {
        this.local_artefact.container = null
        if (!this.isNewArtefact) {
          this.emitUpdate('container', this.local_artefact.container)
        }
        this.previousContainerId = null
        this.clearInputError('container')
        return
      }

      console.log('onContainerSelect prevContainer = ', prevContainer)

      // if selecting the same container, just keep it and clear any error
      if (containerId === prevContainer) {
        this.local_artefact.container = containerId
        this.clearInputError('container')
        return
      }

      //--------------------------
      // check container capacity
      //--------------------------
      const container = calcContainerAvailability({
        container_id: containerId,
        character: { artefacts: this.relatedArtefacts },
      })

      if (isEmpty(container)) {
        console.error(
          'Unable to calculate container availability for container id ',
          containerId,
        )
        // revert selection back to previous container
        this.local_artefact.container = prevContainer
        return
      }

      // total enc required for this item (tally_qty * enc per item)
      const encRequired = this.calcQtyTotEnc
      const roundedRequired =
        Math.round((encRequired + Number.EPSILON) * 100) / 100
      const roundedAvailability =
        Math.round((container.availability + Number.EPSILON) * 100) / 100

      console.log(
        'onContainerSelect: encRequired = ',
        roundedRequired,
        ' roundedAvailability = ',
        roundedAvailability,
      )

      if (roundedRequired - roundedAvailability > 0) {
        // insufficient capacity in the immediate container: revert selection
        this.clearInputError('container')

        this.errors.push(
          window.bbUiErrorMsg(
            `Cannot select ${container.name || 'Selected container'}: Capacity (${this.formatEnc(
              container.capacity,
            )}), available enc capacity (${this.formatEnc(
              container.availability,
            )}).\nRequired (${this.formatEnc(encRequired)})`,
          ),
        )
        this.errorFields.push('container')

        // revert v-model back to previous valid container
        this.local_artefact.container = prevContainer
        return
      }

      //------------------------------
      // check parent container chain
      //------------------------------
      const parentChain = this.getParentContainerChain(containerId)
      for (const parent of parentChain) {
        const parentAvailable =
          Math.round(((parent.availability ?? 0) + Number.EPSILON) * 100) / 100

        console.log(
          'onContainerSelect: parent container check: ',
          parent.name,
          ' available = ',
          parentAvailable,
          ' required = ',
          roundedRequired,
        )

        if (roundedRequired - parentAvailable > 0) {
          this.clearInputError('container')

          // build container chain string from the immediate container
          // up to the parent that exceeds capacity
          const immediateName = container.name || 'Container'
          const parentIndex = parentChain.findIndex((p) => p.id === parent.id)
          const chainUpToParent =
            parentIndex === -1
              ? parentChain
              : parentChain.slice(0, parentIndex + 1)
          const chainNames = [
            immediateName,
            ...chainUpToParent.map((p) => p.name).filter(Boolean),
          ]
          const chainLabel = chainNames.join(' -> ')

          this.errors.push(
            window.bbUiErrorMsg(
              `Cannot select ${container.name || 'Selected container'}: Container chain: ${chainLabel}.\nAvailable enc capacity of parent container (${this.formatEnc(
                parentAvailable,
              )}). Required (${this.formatEnc(encRequired)})`,
            ),
          )
          this.errorFields.push('container')

          // revert v-model back to previous valid container
          this.local_artefact.container = prevContainer
          return
        }
      }

      // capacity ok across chain: apply selection and emit update for existing artefacts
      this.local_artefact.container = containerId
      // business rule: if an armour item is placed in a container,
      // it should no longer be equipped
      if (this.resolvedItemType === 'Armour') {
        this.activeArtefact.equipped = false
        // clear any armour-location validation error and selection when
        // switching from equipped armour to stored-in-container
        this.local_artefact.armour_location = null
        this.clearInputError('armour_location')
        if (!this.isNewArtefact) {
          this.emitUpdate('equipped', false)
          this.emitUpdate('armour_location', null)
        }
      }
      this.previousContainerId = containerId

      if (!this.isNewArtefact) {
        this.emitUpdate('container', this.local_artefact.container)
      }
      this.clearInputError('container')
    },
    //========================================================
    // on worn (item/armour) location select
    //---------------------------------------
    onWornLocationSelect(wornLocationId) {
      const selected =
        this.armour_locations.find((c) => c.id === wornLocationId) || null
      const name = selected?.name ?? null
      console.log(
        'onWornLocationSelect id = ',
        wornLocationId,
        ' name = ',
        name,
      )
      //--------------------------
      // ensure local item exists
      //--------------------------
      this.local_artefact = this.local_artefact || {}
      this.local_artefact.worn_location = wornLocationId || null
      if (this.activeArtefact) {
        this.activeArtefact.worn_location = this.local_artefact.worn_location
      }
      // clear any prior validation error once a valid
      // worn_location has been chosen
      if (this.local_artefact.worn_location) {
        this.clearInputError('worn_location')
      }
      //--------------------------------------
      // real-time changes for existing artefacts
      //--------------------------------------
      if (!this.isNewArtefact) {
        // emit id for processing; name is only for debug/logging
        this.emitUpdate('worn_location', this.local_artefact.worn_location)
      }
    },
    //========================================================
    // on attached location select
    //----------------------------
    onAttachedLocationSelect(attachLocationId) {
      const selected =
        this.attach_locations.find((c) => c.id === attachLocationId) || null
      const name = selected?.name ?? null
      console.log(
        'onAttachedLocationSelect id = ',
        attachLocationId,
        ' name = ',
        name,
      )
      //--------------------------
      // ensure local item exists
      //--------------------------
      this.local_artefact = this.local_artefact || {}
      this.local_artefact.attach_location = attachLocationId || null
      //--------------------------------------
      // real-time changes for existing artefacts
      //--------------------------------------
      if (!this.isNewArtefact) {
        // emit id for processing; name is only for debug/logging
        this.emitUpdate('attach_location', this.local_artefact.attach_location)
      }
    },
    //========================================================
    // on armour location select
    //---------------------------
    onArmourLocationSelect(armourLocationId) {
      const source = Array.isArray(this.armour_locations)
        ? this.armour_locations
        : []
      const selected = source.find((c) => c.id === armourLocationId) || null
      const name = selected?.name ?? null
      console.log(
        'onArmourLocationSelect id = ',
        armourLocationId,
        ' name = ',
        name,
      )
      //--------------------------
      // ensure local item exists
      //--------------------------
      this.local_artefact = this.local_artefact || {}
      this.local_artefact.armour_location = armourLocationId || null

      // clear any transient "select location" error when a valid selection is made
      if (armourLocationId) {
        this.clearInputError('armour_location')
      }
      //--------------------------------------
      // real-time changes for existing artefacts
      //--------------------------------------
      if (!this.isNewArtefact) {
        // emit id for processing; name is only for debug/logging
        this.emitUpdate('armour_location', this.local_artefact.armour_location)

        // For flexible armour, if the user previously toggled
        // equipped=true but we deferred emitting that change
        // (pendingFlexibleEquip), now that a specific
        // armour_location has been chosen we can safely emit
        // the equipped=true update so that downstream mapping
        // (processAddEquippedArmour) uses the correct location.
        const requiresArmourLocation =
          Array.isArray(this.armour_locations) &&
          this.armour_locations.length > 0

        if (
          this.pendingFlexibleEquip &&
          requiresArmourLocation &&
          armourLocationId &&
          this.resolvedItemType === 'Armour'
        ) {
          console.log(
            'onArmourLocationSelect: emitting deferred equipped=true for flexible armour',
          )
          this.pendingFlexibleEquip = false
          this.emitUpdate('equipped', true)
        }
      }
    },
    //========================================================
    // on worn toggle (weapons)
    //---------------------------
    onWornToggle(value) {
      console.log('onWornToggle value = ', value)

      if (!this.activeArtefact) return
      if (!(this.activeArtefact?.wearable ?? this.local_artefact?.wearable))
        return

      this.local_artefact = this.local_artefact || {}
      this.local_artefact.worn = value
      this.activeArtefact.worn = value

      if (!this.isNewArtefact) {
        this.emitUpdate('worn', value)
      }
    },
    //========================================================
    // on held toggle
    //-----------------
    onHeldToggle(value) {
      console.log('onHeldToggle value = ', value)

      this.local_artefact = this.local_artefact || {}
      this.local_artefact.held = value

      // apply held flag locally (v-model already updates activeArtefact.held)
      this.activeArtefact.held = value

      // for armour: held=true implies not equipped
      if (this.resolvedItemType === 'Armour' && value) {
        this.local_artefact.equipped = false
        this.activeArtefact.equipped = false
        // clear any armour-location validation error and selection when
        // switching from equipped armour to held
        this.local_artefact.armour_location = null
        this.clearInputError('armour_location')
        if (!this.isNewArtefact) {
          this.emitUpdate('equipped', false)
          this.emitUpdate('armour_location', null)
        }
      }

      // emit held change for existing artefacts
      if (!this.isNewArtefact) {
        this.emitUpdate('held', value)
      }
    },
    //========================================================
    // ENC input handler (tally tab)
    //--------------------------------
    onEncInput(event) {
      if (!this.activeArtefact || !event || !event.target) return

      const raw = event.target.value ?? ''
      const num = raw === '' ? null : Number(raw)

      this.activeArtefact.enc = Number.isNaN(num) ? null : num
    },
    onEncBlur(value) {
      if (!this.activeArtefact) return

      const num = Number(value)
      if (Number.isNaN(num)) return

      const fixed = Number(num.toFixed(2))
      this.activeArtefact.enc = fixed

      if (this.local_artefact) {
        this.local_artefact.enc = fixed
      }

      if (!this.isNewArtefact) {
        this.delayedEmitUpdate('enc', fixed)
      }
    },
    //========================================================
    // on equipped toggle
    //---------------------
    onEquippedToggle(value) {
      console.log('onEquippedToggle value = ', value)

      // ensure local item exists
      this.local_artefact = this.local_artefact || {}
      this.local_artefact.equipped = value

      // apply equipped flag locally
      this.activeArtefact.equipped = value

      // business rule: if item is equipped, it cannot be in a container or held
      if (value) {
        // ---------------------------------------------
        // front-end validation: prevent overlapping armour
        // with same layer on the same character location
        // ---------------------------------------------
        const conflict = this.checkArmourLayerConflict()
        if (conflict) {
          this.errors.push(conflict.message)
          this.errorFields.push('equipped')

          // revert toggle
          this.local_artefact.equipped = false
          this.activeArtefact.equipped = false
          return
        }

        // clear any container selection
        this.local_artefact.container = null
        this.previousContainerId = null

        if (!this.isNewArtefact) {
          this.emitUpdate('container', null)
        }

        // also clear held status
        this.local_artefact.held = false
        this.activeArtefact.held = false
        if (!this.isNewArtefact) {
          this.emitUpdate('held', false)
        }

        // if this armour requires a specific location but none is yet selected,
        // surface a validation message immediately
        const requiresArmourLocation =
          Array.isArray(this.armour_locations) &&
          this.armour_locations.length > 0
        const hasArmourLocation =
          (this.local_artefact && this.local_artefact.armour_location) ||
          this.activeArtefact?.armour_location

        if (requiresArmourLocation && !hasArmourLocation) {
          this.errors.push(
            window.bbUiErrorMsg(
              'Equipped armour requires a specific armour location to be selected',
            ),
          )
          this.errorFields.push('armour_location')

          // For flexible armour, defer emitting the equipped=true
          // change until the user actually picks a specific
          // armour_location from the dropdown. This mirrors the
          // previous behaviour where equip processing only ran
          // after a location was chosen.
          if (!this.isNewArtefact) {
            this.pendingFlexibleEquip = true
          }
        }
      } else {
        // when unequipped, clear any equipped-related validation error
        this.clearInputError('equipped')
        // when unequipped, clear any selected armour location
        this.local_artefact.armour_location = null
        // clear any "equipped armour requires location" message
        this.clearInputError('armour_location')
        if (!this.isNewArtefact) {
          this.emitUpdate('armour_location', null)
        }
      }

      // emit equipped change for existing artefacts
      if (!this.isNewArtefact) {
        const requiresArmourLocation =
          Array.isArray(this.armour_locations) &&
          this.armour_locations.length > 0
        const hasArmourLocation =
          (this.local_artefact && this.local_artefact.armour_location) ||
          this.activeArtefact?.armour_location

        // For flexible armour with no selected location yet, do not
        // emit equipped=true now; it will be emitted once the
        // armour_location is chosen in onArmourLocationSelect.
        if (value && requiresArmourLocation && !hasArmourLocation) {
          console.log(
            'onEquippedToggle: deferring equipped=true emit for flexible armour',
          )
        } else {
          this.emitUpdate('equipped', value)
        }
      }
    },
    //========================================================
    // get dropdown values
    //---------------------
    async getDropDownValues() {
      console.log('getDropDownValues for artefact card')

      const relatedArtefacts = Array.isArray(this.relatedArtefacts)
        ? this.relatedArtefacts
        : []
      //------------
      // containers
      //------------
      const containers = relatedArtefacts.filter(
        (c) => c && c.storage === true && c.id !== this.activeArtefact.id,
      )
      this.containers = containers.slice().sort((a, b) => {
        const labelA = (a.display_name || a.name || '').toLowerCase()
        const labelB = (b.display_name || b.name || '').toLowerCase()
        if (labelA < labelB) return -1
        if (labelA > labelB) return 1
        return 0
      })
      //------------------------------------
      // load lookups in parallel
      //------------------------------------
      await Promise.all([
        this.$store.dispatch('characters/loadStaticSkills'),
        this.$store.dispatch('characters/loadItemSizes'),
        this.$store.dispatch('characters/loadContainerSizes'),
        this.$store.dispatch('characters/loadArtefactTypes'),
        this.$store.dispatch('characters/loadItemLookups'),
        this.$store.dispatch('characters/loadStaticTraits'),
        this.$store.dispatch('characters/loadStaticCurrencies'),
        this.$store.dispatch('races/loadStaticRaces'),
      ])

      const artefactTypeRecords =
        this.$store.getters['characters/getArtefactTypes'] || []
      this.artefact_type_records = Array.isArray(artefactTypeRecords)
        ? artefactTypeRecords
        : []
      this.artefact_types = this.filteredArtefactTypes

      const itemSizes = this.$store.getters['characters/getItemSizes'] || []
      this.item_sizes = Array.isArray(itemSizes) ? itemSizes : []

      const containerSizes =
        this.$store.getters['characters/getContainerSizes'] || []
      this.container_sizes = Array.isArray(containerSizes) ? containerSizes : []

      // get lookups
      const lookups = this.$store.getters['characters/getItemLookups'] || {}
      this.materials = Array.isArray(lookups.material) ? lookups.material : []
      this.ages = Array.isArray(lookups.age) ? lookups.age : []
      this.qualities = Array.isArray(lookups.quality) ? lookups.quality : []
      this.statuses = Array.isArray(lookups.status) ? lookups.status : []
      this.units = Array.isArray(lookups.count_unit) ? lookups.count_unit : []
      this.poison_types = Array.isArray(lookups.poison_type)
        ? lookups.poison_type
        : []
      this.healing_types = Array.isArray(lookups.healing_type)
        ? lookups.healing_type
        : []

      const getTraits = this.$store.getters['characters/getTraits'] || []
      this.traits = Array.isArray(getTraits) ? getTraits : []

      const getRaces = this.$store.getters['races/getRaces'] || []
      this.races = Array.isArray(getRaces) ? getRaces : []

      // get artefacts
      const getArtefacts = this.$store.getters['artefacts/getArtefacts'] || []
      this.artefacts = Array.isArray(getArtefacts) ? getArtefacts : []
      console.log('artefacts for artefact lookups = ', this.artefacts)

      // get currencies
      const getCurrencies =
        this.$store.getters['characters/getCurrencies'] || []
      this.currencies = Array.isArray(getCurrencies) ? getCurrencies : []
      console.log('currencies for artefact lookups = ', this.currencies)
      // get default currency
      this.default_currency = this.currencies.find((c) => c.default) || null

      this.syncArtefactCurrencySelection()
      // Re-align baseline so post-mount currency sync doesn't create a false diff
      if (this.original_artefact && !this.isNewArtefact) {
        this.original_artefact.currency = this.local_artefact?.currency ?? null
      }

      // for new artefacts with no currency yet, seed from default
      if (this.isNewArtefact) {
        const hasCurrency =
          this.local_artefact && this.local_artefact.currency != null
        if (!hasCurrency && this.default_currency) {
          this.local_artefact = this.local_artefact || {}
          this.local_artefact.currency = this.default_currency.id ?? null
        }
      }

      if (this.local_artefact?.artefact_type) {
        this.applyArtefactSubtypeFromType(this.local_artefact.artefact_type)
        if (this.isNewArtefact) {
          this.applyArtefactTypeFlags(this.local_artefact.artefact_type)
        }

        if (this.local_artefact?.item_size) {
          this.applySelectedItemSize(this.local_artefact.item_size)
        } else {
          this.applyArtefactTypeItemSize(this.local_artefact.artefact_type)
          this.applyArtefactTypeEnc(this.local_artefact.artefact_type)
        }

        if (this.local_artefact?.artefact_base_item) {
          this.applyArtefactBaseItemSelection(
            this.local_artefact.artefact_base_item,
            { preserveExisting: !this.isNewArtefact },
          )
        }

        if (this.local_artefact?.container_size) {
          this.applyContainerSizeSelection(this.local_artefact.container_size)
        }
      }
    },
    //------------------
    // confirm deletion
    //------------------
    async confirmDeletion() {
      console.log('confirmDeletion for item = ', this.activeArtefact)
      //-----------------------------------------------
      // cannot delete item if container with contents
      //-----------------------------------------------
      if (
        this.activeArtefact.storage === true &&
        this.activeArtefact.contents_enc > 0
      ) {
        console.log('cannot delete item - container has contents...')
        await this.$refs.confirmDialog.show({
          title: 'Cannot Delete Container',
          message: 'Move contents before deletion',
          cancelButton: 'Cancel',
        })
        return
      }
      //-----------------------------------------------
      // cannot delete item if linked to a character
      //-----------------------------------------------
      const linkedCharacterName =
        this.activeArtefact.character_name ||
        this.activeArtefact.player_name ||
        null
      if (linkedCharacterName) {
        await this.$refs.confirmDialog.show({
          title: 'Cannot Delete Artefact',
          message: `Character ${linkedCharacterName} owns this artefact.`,
          cancelButton: 'Close',
        })
        return
      }
      //------------------------------------
      // proceed with deletion confirmation
      //------------------------------------
      this.delete = true

      const ok = await this.$refs.confirmDialog.show({
        title: 'Delete Campaign Artefact',
        message: 'Confirm Deletion',
        subject: this.activeArtefact.name,
        okButton: 'Delete',
        cancelButton: 'Cancel',
      })
      if (ok) {
        this.$emit('delete-campaign-artefact', this.activeArtefact)
        this.closeSidePanel()
      } else {
        this.delete = false
      }
    },
    async confirmNewInactiveArtefactActivation() {
      if (!this.isNewArtefact) {
        return
      }

      const isActive = this.toBooleanFlag(
        this.activeArtefact?.active ?? this.local_artefact?.active ?? false,
      )

      if (isActive || !this.$refs?.confirmDialog?.show) {
        return
      }

      const makeActive = await this.$refs.confirmDialog.show({
        title: 'Inactive Campaign Artefact',
        message: 'Make active?',
        subject: this.activeArtefact?.name ?? this.local_artefact?.name ?? '',
        okButton: 'Make Active',
        cancelButton: 'Keep Inactive',
      })

      if (!makeActive) {
        return
      }

      this.local_artefact = this.local_artefact || {}
      this.local_artefact.active = true

      if (this.activeArtefact) {
        this.activeArtefact.active = true
      }

      this.syncInactiveStatusWithActiveState()
    },
    //========================================================
    // on submit
    //-----------
    async onSubmit() {
      if (this.submitCommitted) {
        return
      }

      console.log('---------------------------------')
      console.log('onSubmit')
      let hasErrors = false

      // This card only creates/edits campaign artefacts, so keep the
      // working copy aligned with the persisted campaign-only contract.
      this.local_artefact = this.local_artefact || {}
      this.local_artefact.campaign = true
      if (this.activeArtefact) {
        this.activeArtefact.campaign = true
      }

      // reset validation state for this submit
      this.errors = []
      this.errorFields = []

      // Enforce count/tally invariants for both new and existing artefacts
      // before validation/payload construction.
      this.enforceCountAndTallyRules({ emitUpdates: false })

      // -------------------------------------------------
      // artefact tab: type required for new artefacts
      // (artefactTypeMissingForNew returns false for existing artefacts)
      // -------------------------------------------------
      if (this.artefactTypeMissingForNew) {
        const msg = window.bbUiErrorMsg(
          'Artefact type must be selected for new artefacts',
        )
        if (!this.errors.includes(msg)) {
          this.errors.push(msg)
        }
        if (!this.errorFields.includes('artefact_type')) {
          this.errorFields.push('artefact_type')
        }
        hasErrors = true
      }

      // -------------------------------------------------
      // artefact tab: base item required for certain types
      // -------------------------------------------------
      if (!this.validateRequiredArtefactBaseItem()) {
        hasErrors = true
      }

      if (!this.validateInactiveStatusRequired()) {
        hasErrors = true
      }

      // -------------------------------------------------
      // descriptions tab: short description required for some artefact types
      // -------------------------------------------------
      if (this.shortDescriptionRequired) {
        const shortDescription = this.activeArtefact?.short_description ?? ''
        if (
          typeof shortDescription !== 'string' ||
          shortDescription.trim() === ''
        ) {
          const msg = window.bbUiErrorMsg(
            'Short description is required for healing, poison, and specialist artefacts',
          )
          if (!this.errors.includes(msg)) {
            this.errors.push(msg)
          }
          if (!this.errorFields.includes('short_description')) {
            this.errorFields.push('short_description')
          }
          hasErrors = true
        }
      }

      // -------------------------------------------------
      // count tab: countable item requires unit and max count
      // -------------------------------------------------
      if (this.activeArtefact?.countable ?? false) {
        const unitName =
          this.activeArtefact?.unit_name ??
          this.local_artefact?.unit_name ??
          null
        if (!unitName) {
          const msg = window.bbUiErrorMsg(
            'Countable artefacts require a unit to be selected',
          )
          if (!this.errors.includes(msg)) {
            this.errors.push(msg)
          }
          if (!this.errorFields.includes('unit_name')) {
            this.errorFields.push('unit_name')
          }
          hasErrors = true
        }

        const maxUnits = Number(
          this.activeArtefact?.max_unit_count ??
            this.local_artefact?.max_unit_count ??
            0,
        )
        if (!Number.isFinite(maxUnits) || maxUnits < 1) {
          const msg = window.bbUiErrorMsg(
            'Countable artefacts require max units of at least 1',
          )
          if (!this.errors.includes(msg)) {
            this.errors.push(msg)
          }
          if (!this.errorFields.includes('max_unit_count')) {
            this.errorFields.push('max_unit_count')
          }
          hasErrors = true
        }
      }

      // -------------------------------------------------
      // healing fields required for healing artefacts
      // -------------------------------------------------
      if (this.showHealingTab) {
        const requiredHealingFields = [
          {
            field: 'healing_type',
            value:
              this.local_artefact?.healing_type ??
              this.activeArtefact?.healing_type ??
              null,
            message: 'Healing type is required for healing artefacts',
          },
          {
            field: 'recovers',
            value:
              this.local_artefact?.recovers ??
              this.activeArtefact?.recovers ??
              '',
            message: 'Recovers is required for healing artefacts',
          },
          {
            field: 'delay',
            value:
              this.local_artefact?.delay ?? this.activeArtefact?.delay ?? '',
            message: 'Delay is required for healing artefacts',
          },
        ]

        for (const { field, value, message } of requiredHealingFields) {
          const missingValue =
            value === null ||
            value === undefined ||
            (typeof value === 'string' && value.trim() === '')

          if (!missingValue) continue

          const msg = window.bbUiErrorMsg(message)
          if (!this.errors.includes(msg)) this.errors.push(msg)
          if (!this.errorFields.includes(field)) this.errorFields.push(field)
          hasErrors = true
        }
      }

      // -------------------------------------------------
      // poison fields required for poison artefacts
      // -------------------------------------------------
      if (this.showPoisonTab) {
        if (!this.validatePoisonArtefactFields()) {
          hasErrors = true
        }
      }

      if (this.showWeaponTab) {
        const skillRows = this.weaponSkillRows

        if (skillRows.length > 0) {
          skillRows.forEach((row) => {
            const damageDice =
              typeof row.damage_dice === 'string' ? row.damage_dice.trim() : ''
            const errorField = `skill_damage_dice-${row.skill_id}`

            if (!this.isValidWeaponDamageDiceOverride(damageDice)) {
              const msg = window.bbUiErrorMsg(
                `${row.name} damage dice must be D1-D12 or 2D1-9D12 with an optional +1 to +8 or -1 to -8 modifier`,
              )
              if (!this.errors.includes(msg)) {
                this.errors.push(msg)
              }
              if (!this.errorFields.includes(errorField)) {
                this.errorFields.push(errorField)
              }
              hasErrors = true
            }
          })
        } else {
          const weaponDamageDice =
            this.local_artefact?.damage_dice ??
            this.activeArtefact?.damage_dice ??
            ''
          const trimmedWeaponDamageDice =
            typeof weaponDamageDice === 'string' ? weaponDamageDice.trim() : ''

          if (!this.isValidWeaponDamageDiceOverride(trimmedWeaponDamageDice)) {
            const msg = window.bbUiErrorMsg(
              'Weapon damage dice must be D1-D12 or 2D1-9D12 with an optional +1 to +8 or -1 to -8 modifier',
            )
            if (!this.errors.includes(msg)) {
              this.errors.push(msg)
            }
            if (!this.errorFields.includes('damage_dice')) {
              this.errorFields.push('damage_dice')
            }
            hasErrors = true
          }
        }
      }

      //-----------------------
      // where adding new item
      //-----------------------
      if (this.isNewArtefact) {
        // artefact value/currency validation
        this.validateValueRange()
        if (this.errors.length > 0) {
          hasErrors = true
        }

        //---------------------------------------------------------------------------------
        // armour validation: if equipping and a specific location is required,
        // ensure an armour_location has been selected before save
        const isEquippedArmour =
          this.resolvedItemType === 'Armour' &&
          (this.activeArtefact?.equipped ?? false)
        const requiresArmourLocation =
          Array.isArray(this.armour_locations) &&
          this.armour_locations.length > 0
        const hasArmourLocation =
          (this.local_artefact && this.local_artefact.armour_location) ||
          this.activeArtefact?.armour_location

        if (isEquippedArmour && requiresArmourLocation && !hasArmourLocation) {
          this.errors.push(
            window.bbUiErrorMsg(
              'Equipped armour requires a specific armour location to be selected',
            ),
          )
          this.errorFields.push('armour_location')
          hasErrors = true
        }

        //---------------------------------------------------------------------------------
        // armour validation: prevent overlapping equipped armour at the same
        // character location with the same layer value (mirrors backend rule)
        if (isEquippedArmour) {
          const conflict = this.checkArmourLayerConflict()
          if (conflict) {
            this.errors.push(conflict.message)
            this.errorFields.push('equipped')
            hasErrors = true
          }
        }

        //---------------------------------------------------------------------------------
        // non-armour wearable artefacts: if marked as worn and there are
        // available worn_locations, require the user to pick one
        if (
          this.resolvedItemType !== 'Armour' &&
          (this.activeArtefact?.worn ?? false)
        ) {
          const hasWornLocations =
            Array.isArray(this.armour_locations) &&
            this.armour_locations.length > 0
          const hasWornLocation =
            (this.local_artefact && this.local_artefact.worn_location) ||
            this.activeArtefact?.worn_location

          if (hasWornLocations && !hasWornLocation) {
            const msg = window.bbUiErrorMsg(
              'Worn artefacts require a specific worn location to be selected',
            )
            if (!this.errors.includes(msg)) {
              this.errors.push(msg)
            }
            if (!this.errorFields.includes('worn_location')) {
              this.errorFields.push('worn_location')
            }
            hasErrors = true
          }
        }

        //-----------------------
        // continue if no errors
        //-----------------------
        if (this.errors.length > 0) {
          this.navigateToFirstErrorTab()
          return
        }

        this.submitCommitted = true
        await this.confirmNewInactiveArtefactActivation()

        console.log('onSubmit....')

        // general fields
        const item_id =
          this.activeArtefact?.id ?? this.item?.id ?? createTempId() // temp id for new artefacts (as item object created first in backend then linked campaign-artefact)
        const name = this.activeArtefact?.name ?? '' // name for debug & to artefact parm (indicate static item creation)
        const character = null
        const type = this.activeArtefact?.type
          ? this.activeArtefact.type
          : (this.itemType ?? '') // use itemType as fallback for new artefacts (no static item)
        const tally_qty = this.activeArtefact?.tally_qty ?? 0
        const enc = this.activeArtefact?.enc ?? 0

        // descriptive fields
        const dm_notes = this.activeArtefact?.dm_notes ?? ''
        const description = this.activeArtefact?.description ?? '' // only updatable for artefacts (static item creation)
        const short_description = this.activeArtefact?.short_description ?? ''
        const image = this.activeArtefact?.image ?? null
        const image_file = this.activeArtefact?.image_file ?? null

        // equipment fields
        const equipment_type = getNormalisedEquipmentType(
          this.activeArtefact?.equipment_type,
        )

        // location/storage fields
        const container = this.activeArtefact?.container ?? null
        const worn_location = this.activeArtefact?.worn_location ?? null
        const attach_location = this.activeArtefact?.attach_location ?? null
        const armour_location = this.activeArtefact?.armour_location ?? null
        const coverage_locations = this.activeArtefact?.coverage_locations ?? []

        // container/contents fields
        const container_size = this.activeArtefact?.container_size ?? null
        const container_capacity = this.activeArtefact?.container_capacity ?? ''
        const container_size_short =
          this.activeArtefact?.container_size_short ?? ''
        const container_size_long =
          this.activeArtefact?.container_size_long ?? ''
        const contents_count_direct =
          this.activeArtefact?.contents_count_direct ?? null
        const contents_count_total =
          this.activeArtefact?.contents_count_total ?? null

        // magic fields
        const runes = this.activeArtefact?.runes ?? []
        const spells = this.activeArtefact?.spells ?? []

        // flags
        const starter = this.activeArtefact?.starter ?? false
        const active = this.activeArtefact?.active ?? false
        const item_status = this.getNormalisedInactiveStatusValue()
        const campaign = true
        const tallyable = this.toBooleanFlag(
          this.local_artefact?.tallyable ?? this.activeArtefact?.tallyable,
        )
        const wearable = this.activeArtefact?.wearable ?? false
        // new artefacts should always be storeable so they can go in containers
        const storeable = true
        const storage = this.activeArtefact?.storage ?? false
        const attach_point = this.activeArtefact?.attach_point ?? false
        const attachable = this.activeArtefact?.attachable ?? false
        const equipped = this.activeArtefact?.equipped ?? false
        const worn = this.activeArtefact?.worn ?? false
        const broken = this.activeArtefact?.broken ?? false
        const countable = this.activeArtefact?.countable ?? false

        // armour/weapon fields
        const ap = this.local_artefact?.ap ?? this.activeArtefact?.ap ?? 0
        const hp = this.local_artefact?.hp ?? this.activeArtefact?.hp ?? 0
        const coverage =
          this.local_artefact?.coverage ?? this.activeArtefact?.coverage ?? 0
        const dex_pen =
          this.local_artefact?.dex_pen ?? this.activeArtefact?.dex_pen ?? 0
        const layer =
          this.local_artefact?.layer ?? this.activeArtefact?.layer ?? 0

        // weapon fields
        const damage_dice =
          this.local_artefact?.damage_dice ??
          this.activeArtefact?.damage_dice ??
          null
        const impale =
          this.local_artefact?.impale ?? this.activeArtefact?.impale ?? null
        const set_charge =
          this.local_artefact?.set_charge ??
          this.activeArtefact?.set_charge ??
          null
        const load =
          this.local_artefact?.load ?? this.activeArtefact?.load ?? null
        const range =
          this.local_artefact?.range ?? this.activeArtefact?.range ?? null

        // countables
        const max_unit_count = this.activeArtefact?.max_unit_count ?? null

        // selected artefact lookup values
        const artefact_type = this.activeArtefact?.artefact_type ?? ''
        const artefact = this.activeArtefact?.artefact ?? null
        const material = this.normaliseArtefactDescriptorForSave(
          'material',
          this.activeArtefact?.material,
        )
        const quality = this.normaliseArtefactDescriptorForSave(
          'quality',
          this.activeArtefact?.quality,
        )
        const age = this.normaliseArtefactDescriptorForSave(
          'age',
          this.activeArtefact?.age,
        )
        const currency = this.activeArtefact?.currency ?? ''
        const value_lower = this.activeArtefact?.value_lower ?? null
        const value_upper = this.activeArtefact?.value_upper ?? null
        const estimated_value = this.activeArtefact?.estimated_value ?? false
        const pwr_storage = this.activeArtefact?.pwr_storage ?? 0
        const uniqueArtefactName = this.getUniqueArtefactName(name)

        this.local_artefact.name = uniqueArtefactName
        if (this.activeArtefact) {
          this.activeArtefact.name = uniqueArtefactName
        }

        // calc tot enc
        const tot_enc = parseFloat(enc) * parseInt(tally_qty, 10)

        //-----------------
        // set add payload
        //-----------------
        const add_parms = {
          name: uniqueArtefactName,
          payload: {
            id: createTempId(), // temp id for front-end tracking until backend creates real item and returns id

            // general fields
            item: item_id,
            name: uniqueArtefactName,
            character: character,
            type: type,
            tally_qty: tally_qty,
            enc: enc,
            tot_enc: tot_enc,

            // descriptive fields
            dm_notes: dm_notes,
            description: description,
            short_description: short_description,
            image: image,
            image_file: image_file,

            // equipment fields
            equipment_type: equipment_type,

            // location/storage fields
            container: container,
            worn_location: worn_location,
            attach_location: attach_location,
            armour_location: armour_location,
            coverage_locations: coverage_locations,

            // container/contents fields
            container_size: container_size,
            container_capacity: container_capacity,
            container_size_short: container_size_short,
            container_size_long: container_size_long,
            contents_count_direct: contents_count_direct,
            contents_count_total: contents_count_total,

            // magic fields
            runes: runes,
            spells: spells,

            // flags
            active: active,
            status: item_status,
            campaign: campaign,
            starter: starter,
            tallyable: tallyable,
            wearable: wearable,
            storeable: storeable,
            storage: storage,
            attach_point: attach_point,
            attachable: attachable,
            countable: countable,
            equipped: equipped,
            worn: worn,
            broken: broken,

            // armour/weapon fields
            ap: ap,
            hp: hp,
            coverage: coverage,
            dex_pen: dex_pen,
            layer: layer,

            // weapon fields
            skills:
              this.activeArtefact?.skills ?? this.local_artefact?.skills ?? [],
            damage_dice: damage_dice,
            impale: impale,
            set_charge: set_charge,
            load: load,
            range: range,

            // poison fields
            poison_type: this.activeArtefact?.poison_type ?? null,
            pot: this.activeArtefact?.pot ?? null,
            effect: this.activeArtefact?.effect ?? null,
            delay: this.activeArtefact?.delay ?? null,
            duration: this.activeArtefact?.duration ?? null,
            resist: this.activeArtefact?.resist ?? null,
            healing_type: this.activeArtefact?.healing_type ?? null,
            recovers: this.activeArtefact?.recovers ?? null,

            // countables
            unit_name:
              this.activeArtefact?.unit_name ?? this.local_artefact?.unit_name,
            max_unit_count: max_unit_count,

            // artefact fields
            artefact_type: artefact_type,
            item_size:
              this.activeArtefact?.item_size ?? this.local_artefact?.item_size,
            artefact_base_item:
              this.activeArtefact?.artefact_base_item ??
              this.local_artefact?.artefact_base_item,
            trait:
              this.activeArtefact?.trait ?? this.local_artefact?.trait ?? null,
            race:
              this.activeArtefact?.race ?? this.local_artefact?.race ?? null,
            artefact: artefact,
            material: material,
            quality: quality,
            age: age,
            pwr_storage: pwr_storage,
            currency: currency,
            value_lower: value_lower,
            value_upper: value_upper,
            estimated_value: estimated_value,
            item_traits: Array.isArray(this.local_artefact?.item_traits)
              ? [...this.local_artefact.item_traits]
              : [],

            // UI-only static-derived artefact flags persisted locally so
            // pending campaign artefacts reopen with the same per-field
            // editability before backend commit.
            _static_material_unknown:
              this.local_artefact._static_material_unknown,
            _static_quality_unknown:
              this.local_artefact._static_quality_unknown,
            _static_age_unknown: this.local_artefact._static_age_unknown,
            _static_artefact_unknown:
              this.local_artefact._static_artefact_unknown,
            _session_material_editable:
              this.local_artefact._session_material_editable,
            _session_quality_editable:
              this.local_artefact._session_quality_editable,
            _session_age_editable: this.local_artefact._session_age_editable,
            _session_artefact_editable:
              this.local_artefact._session_artefact_editable,
            _static_currency_unknown:
              this.local_artefact._static_currency_unknown,
            _static_value_unknown: this.local_artefact._static_value_unknown,
            _static_value_lower_unknown:
              this.local_artefact._static_value_lower_unknown,
            _static_value_upper_unknown:
              this.local_artefact._static_value_upper_unknown,
            _static_estimated_value_readonly:
              this.local_artefact._static_estimated_value_readonly,
            _static_enc_readonly: this.local_artefact._static_enc_readonly,
            _static_countable_readonly:
              this.local_artefact._static_countable_readonly,
            _static_unit_name_readonly:
              this.local_artefact._static_unit_name_readonly,
            _static_max_unit_count_readonly:
              this.local_artefact._static_max_unit_count_readonly,
          },
        }
        //----------------
        // emit add event
        //----------------
        console.log('emitting add-campaign-artefact with parms = ', add_parms)
        this.$emit('add-campaign-artefact', add_parms)
      }

      // Existing artefacts: keep manual item-name edits normalised so OK/save
      // does not persist a lowercase first letter after the field loses focus.
      if (!this.isNewArtefact) {
        const currentName = this.activeArtefact?.name ?? ''
        const normalisedName = capitaliseFirstLetter(currentName.trim())

        if (normalisedName && normalisedName !== currentName) {
          this.local_artefact.name = normalisedName
          if (this.activeArtefact) {
            this.activeArtefact.name = normalisedName
          }
        }
      }

      if (!this.isNewArtefact && !hasErrors && this.isChanged) {
        this.submitCommitted = true

        const id = this.activeArtefact?.id ?? this.local_artefact?.id ?? null
        const patchChanges = this.buildExistingArtefactPatchChanges()

        console.log(
          'emitting update-campaign-artefact patch changes = ',
          patchChanges,
        )

        if (patchChanges.length > 0) {
          this.$emit('update-campaign-artefact', {
            id,
            partial: true,
            changes: patchChanges,
            ui_state: this.buildLocalStaticUiState(),
          })
        }
      }
      //----------------------------------
      // where no errors close side panel
      //----------------------------------
      if (!hasErrors) {
        this.submitCommitted = true
        this.closeSidePanel()
      }
    },
    //========================================================
    // clear specific input error
    //----------------------------
    clearInputError(field) {
      if (!this.errorFields || this.errorFields.length === 0) return

      const index = this.errorFields.indexOf(field)
      if (index !== -1) {
        this.errors.splice(index, 1)
        this.errorFields.splice(index, 1)
      }
    },
    isTabAvailable(tab) {
      const tabVisibility = {
        artefact: true,
        descriptions: true,
        count: this.showCountTab,
        armour: this.showArmourTab,
        weapon: this.showWeaponTab,
        healing: this.showHealingTab,
        poison: this.showPoisonTab,
        specialist: this.showSpecialistTab,
        adjs: this.showAdjsTab,
        magic: this.showMagicTab,
        traits: this.showArtefactTraitsTab,
        image: this.showImageTab,
        switches: true,
      }

      return tabVisibility[tab] ?? false
    },
    //========================================================
    // maintain worn-location error for new worn artefacts
    //------------------------------------------------
    updateWornLocationErrorForNew() {
      const msg = window.bbUiErrorMsg(
        'Worn artefacts require a specific worn location to be selected',
      )

      // only applies to new, non-armour artefacts
      if (!this.isNewArtefact || this.resolvedItemType === 'Armour') {
        this.clearInputError('worn_location')
        return
      }

      const worn = this.activeArtefact?.worn ?? false
      if (!worn) {
        this.clearInputError('worn_location')
        return
      }

      const hasWornLocations =
        Array.isArray(this.armour_locations) && this.armour_locations.length > 0
      if (!hasWornLocations) {
        this.clearInputError('worn_location')
        return
      }

      const hasWornLocation =
        (this.local_artefact && this.local_artefact.worn_location) ||
        this.activeArtefact?.worn_location

      if (!hasWornLocation) {
        if (!this.errors.includes(msg)) {
          this.errors.push(msg)
        }
        if (!this.errorFields.includes('worn_location')) {
          this.errorFields.push('worn_location')
        }
      } else {
        this.clearInputError('worn_location')
      }
    },
    //========================================================
    // manage cancel
    //---------------
    manageCancel() {
      this.submitCommitted = false
      this.clearPendingTimers()

      if (!this.isNewArtefact && this.isChanged) {
        const restoredOriginal = this.$deepCopy(this.original_artefact || {})

        Object.entries(restoredOriginal).forEach(([key, value]) => {
          this.local_artefact[key] = value

          if (this.activeArtefact) {
            this.activeArtefact[key] = value
          }
        })

        if (this.local_artefact) {
          this.local_artefact.image_file = null
          this.local_artefact.image_cleared = false
        }

        if (this.activeArtefact) {
          this.activeArtefact.image_file = null
          this.activeArtefact.image_cleared = false
        }
      }
      //------------------
      // close side panel
      //------------------
      this.closeSidePanel()
    },
    //========================================================
    // close side panel
    //------------------
    closeSidePanel() {
      this.$emit('close-artefact-panel')
    },
    buildLocalStaticUiState() {
      return {
        trait: this.local_artefact.trait ?? null,
        item_traits: Array.isArray(this.local_artefact.item_traits)
          ? [...this.local_artefact.item_traits]
          : [],
        _static_material_unknown: this.local_artefact._static_material_unknown,
        _static_quality_unknown: this.local_artefact._static_quality_unknown,
        _static_age_unknown: this.local_artefact._static_age_unknown,
        _static_artefact_unknown: this.local_artefact._static_artefact_unknown,
        _session_material_editable:
          this.local_artefact._session_material_editable,
        _session_quality_editable:
          this.local_artefact._session_quality_editable,
        _session_age_editable: this.local_artefact._session_age_editable,
        _session_artefact_editable:
          this.local_artefact._session_artefact_editable,
        _static_currency_unknown: this.local_artefact._static_currency_unknown,
        _static_value_unknown: this.local_artefact._static_value_unknown,
        _static_value_lower_unknown:
          this.local_artefact._static_value_lower_unknown,
        _static_value_upper_unknown:
          this.local_artefact._static_value_upper_unknown,
        _static_estimated_value_readonly:
          this.local_artefact._static_estimated_value_readonly,
        _static_enc_readonly: this.local_artefact._static_enc_readonly,
        _static_countable_readonly:
          this.local_artefact._static_countable_readonly,
        _static_unit_name_readonly:
          this.local_artefact._static_unit_name_readonly,
        _static_max_unit_count_readonly:
          this.local_artefact._static_max_unit_count_readonly,
      }
    },
    buildExistingArtefactPatchChanges() {
      const current =
        this.local_artefact && Object.keys(this.local_artefact).length > 0
          ? this.local_artefact
          : this.activeArtefact || {}
      const original = this.original_artefact || {}

      const normaliseBooleanValue = (value) => {
        if (typeof value === 'boolean') {
          return value
        }

        if (typeof value === 'number') {
          return value !== 0
        }

        if (typeof value === 'string') {
          const normalised = value.trim().toLowerCase()
          if (['yes', 'true', '1', 'on'].includes(normalised)) {
            return true
          }
          if (['no', 'false', '0', 'off', ''].includes(normalised)) {
            return false
          }
        }

        return !!value
      }

      const getIdValue = (value) => {
        const normaliseScalarId = (raw) => {
          if (typeof raw === 'string') {
            const trimmed = raw.trim()
            if (/^-?\d+$/.test(trimmed)) {
              return Number(trimmed)
            }
            return trimmed
          }

          return raw
        }

        if (value && typeof value === 'object') {
          return normaliseScalarId(value.id ?? null)
        }
        return normaliseScalarId(value ?? null)
      }

      const normaliseValue = (key, value) => {
        if (
          [
            'active',
            'starter',
            'tallyable',
            'wearable',
            'storeable',
            'countable',
            'impale',
            'set_charge',
            'storage',
            'attachable',
            'attach_point',
            'estimated_value',
          ].includes(key)
        ) {
          return normaliseBooleanValue(value)
        }

        if (key === 'unit_name') {
          return value ?? ''
        }

        if (
          [
            'trait',
            'currency',
            'race',
            'item_size',
            'container_size',
            'artefact_base_item',
          ].includes(key)
        ) {
          return getIdValue(value)
        }

        if (key === 'skills') {
          return this.getSelectedSkillIds(value || [])
        }

        return value
      }

      const keyDefs = [
        {
          key: 'name',
          current: current.name ?? '',
          original: original.name ?? '',
        },
        {
          key: 'short_description',
          current: current.short_description ?? '',
          original: original.short_description ?? '',
        },
        {
          key: 'description',
          current: current.description ?? '',
          original: original.description ?? '',
        },
        {
          key: 'active',
          current: current.active ?? true,
          original: original.active ?? true,
        },
        {
          key: 'status',
          current: this.toBooleanFlag(current.active ?? true)
            ? this.getInactiveStatusNaValue()
            : (current.status ?? null),
          original: this.toBooleanFlag(original.active ?? true)
            ? this.getInactiveStatusNaValue()
            : (original.status ?? null),
        },
        {
          key: 'starter',
          current: current.starter ?? false,
          original: original.starter ?? false,
        },
        {
          key: 'tallyable',
          current: current.tallyable ?? false,
          original: original.tallyable ?? false,
        },
        {
          key: 'tally_qty',
          current: current.tally_qty ?? 0,
          original: original.tally_qty ?? 0,
        },
        {
          key: 'wearable',
          current: current.wearable ?? false,
          original: original.wearable ?? false,
        },
        {
          key: 'storeable',
          current: current.storeable ?? true,
          original: original.storeable ?? true,
        },
        {
          key: 'countable',
          current: current.countable ?? false,
          original: original.countable ?? false,
        },
        {
          key: 'unit_name',
          current: current.unit_name ?? '',
          original: original.unit_name ?? '',
        },
        {
          key: 'max_unit_count',
          current: current.max_unit_count ?? 0,
          original: original.max_unit_count ?? 0,
        },
        {
          key: 'enc',
          current: current.enc ?? 0.01,
          original: original.enc ?? 0.01,
        },
        {
          key: 'dm_notes',
          current: current.dm_notes ?? '',
          original: original.dm_notes ?? '',
        },
        {
          key: 'ap',
          current: current.ap ?? 0,
          original: original.ap ?? 0,
        },
        {
          key: 'hp',
          current: current.hp ?? 0,
          original: original.hp ?? 0,
        },
        {
          key: 'range',
          current: current.range ?? '',
          original: original.range ?? '',
        },
        {
          key: 'load',
          current: current.load ?? 0,
          original: original.load ?? 0,
        },
        {
          key: 'damage_dice',
          current: current.damage_dice ?? '',
          original: original.damage_dice ?? '',
        },
        {
          key: 'impale',
          current: current.impale ?? false,
          original: original.impale ?? false,
        },
        {
          key: 'set_charge',
          current: current.set_charge ?? false,
          original: original.set_charge ?? false,
        },
        {
          key: 'poison_type',
          current: current.poison_type ?? null,
          original: original.poison_type ?? null,
        },
        {
          key: 'pot',
          current: current.pot ?? 0,
          original: original.pot ?? 0,
        },
        {
          key: 'effect',
          current: current.effect ?? '',
          original: original.effect ?? '',
        },
        {
          key: 'delay',
          current: current.delay ?? '',
          original: original.delay ?? '',
        },
        {
          key: 'duration',
          current: current.duration ?? '',
          original: original.duration ?? '',
        },
        {
          key: 'resist',
          current: current.resist ?? '',
          original: original.resist ?? '',
        },
        {
          key: 'healing_type',
          current: current.healing_type ?? null,
          original: original.healing_type ?? null,
        },
        {
          key: 'recovers',
          current: current.recovers ?? '',
          original: original.recovers ?? '',
        },
        {
          key: 'storage',
          current: current.storage ?? false,
          original: original.storage ?? false,
        },
        {
          key: 'attachable',
          current: current.attachable ?? false,
          original: original.attachable ?? false,
        },
        {
          key: 'attach_point',
          current: current.attach_point ?? false,
          original: original.attach_point ?? false,
        },
        {
          key: 'value_lower',
          current:
            current.value_lower === '' || current.value_lower == null
              ? 0
              : Number(current.value_lower),
          original:
            original.value_lower === '' || original.value_lower == null
              ? 0
              : Number(original.value_lower),
        },
        {
          key: 'value_upper',
          current:
            current.value_upper === '' || current.value_upper == null
              ? 0
              : Number(current.value_upper),
          original:
            original.value_upper === '' || original.value_upper == null
              ? 0
              : Number(original.value_upper),
        },
        {
          key: 'estimated_value',
          current: current.estimated_value ?? false,
          original: original.estimated_value ?? false,
        },
        {
          key: 'material',
          current: this.normaliseArtefactDescriptorForSave(
            'material',
            current.material,
          ),
          original: this.normaliseArtefactDescriptorForSave(
            'material',
            original.material,
          ),
        },
        {
          key: 'quality',
          current: this.normaliseArtefactDescriptorForSave(
            'quality',
            current.quality,
          ),
          original: this.normaliseArtefactDescriptorForSave(
            'quality',
            original.quality,
          ),
        },
        {
          key: 'age',
          current: this.normaliseArtefactDescriptorForSave('age', current.age),
          original: this.normaliseArtefactDescriptorForSave(
            'age',
            original.age,
          ),
        },
        {
          key: 'pwr_storage',
          current: current.pwr_storage ?? 0,
          original: original.pwr_storage ?? 0,
        },
        {
          key: 'artefact_type',
          current: current.artefact_type ?? null,
          original: original.artefact_type ?? null,
        },
        {
          key: 'trait',
          current: current.trait ?? null,
          original: original.trait ?? null,
        },
        {
          key: 'currency',
          current: current.currency ?? null,
          original: original.currency ?? null,
        },
        {
          key: 'race',
          current: current.race ?? null,
          original: original.race ?? null,
        },
        {
          key: 'item_size',
          current: current.item_size ?? null,
          original: original.item_size ?? null,
        },
        {
          key: 'container_size',
          current: current.container_size ?? null,
          original: original.container_size ?? null,
        },
        {
          key: 'artefact_base_item',
          current: current.artefact_base_item ?? null,
          original: original.artefact_base_item ?? null,
        },
        {
          key: 'skills',
          current: current.skills ?? [],
          original: original.skills ?? [],
        },
        {
          key: 'image_file',
          current: current.image_file ?? null,
          original: null,
        },
      ]

      return keyDefs
        .map(({ key, current: currentRaw, original: originalRaw }) => {
          const currentValue = normaliseValue(key, currentRaw)
          const originalValue = normaliseValue(key, originalRaw)
          const isImageFileField = key === 'image_file'
          const hasImageUpload =
            isImageFileField && currentValue instanceof FormData
          return {
            key,
            value: currentValue,
            changed:
              hasImageUpload ||
              JSON.stringify(currentValue) !== JSON.stringify(originalValue),
          }
        })
        .filter((entry) => entry.changed)
        .map(({ key, value }) => ({ key, value }))
        .concat(
          this.local_artefact?.image_cleared
            ? [
                {
                  key: 'image',
                  value: null,
                },
              ]
            : [],
        )
    },
    clearPendingTimers() {
      Object.values(this.pendingTimers || {}).forEach((timerId) => {
        clearTimeout(timerId)
      })
      this.pendingTimers = {}
    },
    //========================================================
    // delayed emit update
    //---------------------
    delayedEmitUpdate(key, value, delay = 600) {
      void key
      void value
      void delay
    },
    //========================================================
    // emit update
    //-------------
    emitUpdate(key, value) {
      void key
      void value

      // Campaign artefact edits are buffered locally and only
      // committed on OK. Keep this method as a no-op so the
      // existing call sites do not perform in-flight saves.
      return
    },
    initialiseExistingStaticArtefact(staticItem, sourceItem = this.item) {
      const item = sourceItem ?? this.item
      const resolvedTallyable = this.toBooleanFlag(
        item?.tallyable ?? this.basePropertyItem?.tallyable ?? false,
      )
      const resolvedCountable = this.toBooleanFlag(
        item?.countable ?? this.basePropertyItem?.countable ?? false,
      )

      this.local_artefact.active = item?.active ?? false
      this.local_artefact.status =
        item?.status ?? staticItem?.status ?? this.getInactiveStatusNaValue()
      this.local_artefact.campaign = true
      this.local_artefact.tallyable = resolvedTallyable
      this.local_artefact.countable = resolvedCountable

      if (this.activeArtefact) {
        this.activeArtefact.campaign = true
        this.activeArtefact.tallyable = resolvedTallyable
        this.activeArtefact.countable = resolvedCountable
        this.activeArtefact.status = this.local_artefact.status
      }

      this.local_artefact.artefact_type =
        item?.artefact_type ?? staticItem?.artefact_type ?? ''
      this.local_artefact.trait = item?.trait ?? staticItem?.trait ?? null
      this.local_artefact.age = item?.age ?? staticItem?.age ?? ''
      this.local_artefact.quality = item?.quality ?? staticItem?.quality ?? ''
      this.local_artefact.material =
        item?.material ?? staticItem?.material ?? ''
      this.local_artefact.value_lower =
        item?.value_lower ?? staticItem?.value_lower ?? 0
      this.local_artefact.value_upper =
        item?.value_upper ?? staticItem?.value_upper ?? 0
      this.local_artefact.estimated_value =
        item?.estimated_value ?? staticItem?.estimated_value ?? false
      this.local_artefact.description =
        item?.description ?? staticItem?.description ?? ''
      this.local_artefact.image = item?.image ?? staticItem?.image ?? null

      this.syncArtefactCurrencySelection()

      const artefactId =
        staticItem?.artefact?.id ??
        staticItem?.artefact ??
        item?.artefact ??
        null
      const getArtefactById = this.$store.getters['artefacts/getArtefactById']
      const artefactObj =
        typeof getArtefactById === 'function' && artefactId
          ? getArtefactById(artefactId)
          : null

      this.local_artefact.artefact = artefactObj ? artefactObj.id : null

      const isKnownStaticDescriptor = (rawValue) => {
        if (rawValue === null || rawValue === undefined) return false

        if (typeof rawValue === 'string') {
          const trimmed = rawValue.trim()
          return trimmed !== '' && trimmed !== 'Unknown'
        }

        if (typeof rawValue === 'object') {
          if (typeof rawValue.name === 'string') {
            const trimmedName = rawValue.name.trim()
            if (trimmedName !== '' && trimmedName !== 'Unknown') {
              return true
            }
          }

          return rawValue.id !== null && rawValue.id !== undefined
        }

        return true
      }

      const resolveStaticUnknownFlag = (
        persistedUnknown,
        staticValue,
        currentValue,
      ) => {
        const resolvedValue =
          staticValue !== null && staticValue !== undefined
            ? staticValue
            : currentValue

        if (typeof persistedUnknown === 'boolean') {
          return persistedUnknown
        }

        return isKnownStaticDescriptor(resolvedValue) ? false : true
      }

      this.local_artefact._static_material_unknown = resolveStaticUnknownFlag(
        item?._static_material_unknown,
        staticItem?.material,
        item?.material,
      )
      this.local_artefact._static_quality_unknown = resolveStaticUnknownFlag(
        item?._static_quality_unknown,
        staticItem?.quality,
        item?.quality,
      )
      this.local_artefact._static_age_unknown = resolveStaticUnknownFlag(
        item?._static_age_unknown,
        staticItem?.age,
        item?.age,
      )
      this.local_artefact._static_artefact_unknown = resolveStaticUnknownFlag(
        item?._static_artefact_unknown,
        staticItem?.artefact,
        item?.artefact,
      )
      this.local_artefact._session_material_editable =
        item?._session_material_editable ??
        this.local_artefact._static_material_unknown
      this.local_artefact._session_quality_editable =
        item?._session_quality_editable ??
        this.local_artefact._static_quality_unknown
      this.local_artefact._session_age_editable =
        item?._session_age_editable ?? this.local_artefact._static_age_unknown
      this.local_artefact._session_artefact_editable =
        item?._session_artefact_editable ??
        this.local_artefact._static_artefact_unknown

      this.local_artefact._static_currency_unknown =
        item?._static_currency_unknown ??
        this.local_artefact._static_currency_unknown
      this.local_artefact._static_value_unknown =
        item?._static_value_unknown ?? this.local_artefact._static_value_unknown
      this.local_artefact._static_value_lower_unknown =
        item?._static_value_lower_unknown ??
        this.local_artefact._static_value_lower_unknown
      this.local_artefact._static_value_upper_unknown =
        item?._static_value_upper_unknown ??
        this.local_artefact._static_value_upper_unknown
      this.local_artefact._static_estimated_value_readonly =
        item?._static_estimated_value_readonly ??
        this.local_artefact._static_estimated_value_readonly
      this.local_artefact._static_enc_readonly =
        item?._static_enc_readonly ?? this.local_artefact._static_enc_readonly
      this.local_artefact._static_countable_readonly =
        item?._static_countable_readonly ??
        this.local_artefact._static_countable_readonly
      this.local_artefact._static_unit_name_readonly =
        item?._static_unit_name_readonly ??
        this.local_artefact._static_unit_name_readonly
      this.local_artefact._static_max_unit_count_readonly =
        item?._static_max_unit_count_readonly ??
        this.local_artefact._static_max_unit_count_readonly

      const countable =
        this.local_artefact.countable ??
        resolvedCountable ??
        item?.countable ??
        this.basePropertyItem?.countable ??
        this.baseStaticItem?.countable ??
        false
      this.local_artefact.countable = !!countable

      if (!this.local_artefact.countable) {
        this.local_artefact.unit_name = null
        this.local_artefact.max_unit_count = 0
        return
      }

      const staticUnitName =
        this.basePropertyItem?.unit_name ??
        this.baseStaticItem?.unit_name ??
        null
      const unitName =
        this.local_artefact.unit_name ??
        item?.unit_name ??
        staticUnitName ??
        null
      this.local_artefact.unit_name = unitName

      if (!unitName) {
        this.local_artefact.max_unit_count = 0
        return
      }

      const persistedMaxRaw =
        this.local_artefact.max_unit_count ?? item?.max_unit_count ?? null
      const persistedMax = Number(persistedMaxRaw)

      if (Number.isFinite(persistedMax) && persistedMax > 0) {
        this.local_artefact.max_unit_count = persistedMax
      } else {
        const staticBaseMax = Number(
          this.basePropertyItem?.max_unit_count ??
            this.baseStaticItem?.max_unit_count ??
            0,
        )
        const initialMax = this.getInitialMaxUnitCount(unitName, staticBaseMax)
        this.local_artefact.max_unit_count = initialMax
      }
    },
  },
  //========================================================
  // mounted
  //---------
  async mounted() {
    console.log('>>> item_type = ', this.item_type)
    //-----------------------------
    // set initial active data tab
    //-----------------------------
    this.activeTab = 'artefact'
    console.log('>>> activeTab = ', this.activeTab)
    //----------------
    // existing item
    //----------------
    if (this.item) {
      const getStaticItemById =
        this.$store.getters['characters/getStaticItemById']
      const persistedArtefactItem =
        typeof getStaticItemById === 'function' && this.item?.id
          ? getStaticItemById(this.item.id)
          : null
      const existingItem =
        persistedArtefactItem?.type === 'Artefact'
          ? {
              ...this.item,
              ...persistedArtefactItem,
              skills: persistedArtefactItem.skills ?? this.item?.skills ?? [],
            }
          : this.item

      if (Object.prototype.hasOwnProperty.call(this.item, 'character_name')) {
        existingItem.character_name = this.item.character_name ?? null
      }

      if (Object.prototype.hasOwnProperty.call(this.item, 'player_name')) {
        existingItem.player_name = this.item.player_name ?? null
      }

      if (Object.prototype.hasOwnProperty.call(this.item, 'last_owner_name')) {
        existingItem.last_owner_name = this.item.last_owner_name ?? null
      }

      if (
        Object.prototype.hasOwnProperty.call(
          this.item,
          'last_owner_player_name',
        )
      ) {
        existingItem.last_owner_player_name =
          this.item.last_owner_player_name ?? null
      }

      console.log('EXISTING ITEM = ', existingItem)
      this.save_item_id = existingItem?.artefact_base_item ?? null
      const staticItem = this.baseStaticItem
      const existingSubtype = this.resolvedItemType || this.item?.type || ''
      const initialArtefactPwrStorage =
        existingItem.pwr_storage ?? staticItem?.pwr_storage ?? 0
      //----------------------------------------------------------
      // save editable fields snapshot for isChanged (and cancel)
      //----------------------------------------------------------
      this.original_artefact = {
        name: existingItem.name ?? '',
        active: existingItem.active ?? true,
        status: this.toBooleanFlag(existingItem.active ?? true)
          ? this.getInactiveStatusNaValue()
          : (existingItem.status ?? null),
        starter: existingItem.starter ?? false,
        tallyable: existingItem.tallyable ?? false,
        wearable: existingItem.wearable ?? false,
        storeable: existingItem.storeable ?? true,
        tally_qty: existingItem.tally_qty ?? 1,
        dm_notes: existingItem.dm_notes ?? existingItem.notes ?? '',
        short_description: existingItem.short_description ?? '',
        container: existingItem.container ?? null,
        worn_location: existingItem.worn_location ?? null,
        attach_location: existingItem.attach_location ?? null,
        armour_location: existingItem.armour_location ?? null,
        ap: existingItem.ap ?? 0,
        hp: existingItem.hp ?? 0,
        enc: existingItem.enc ?? 0.1,
        range: existingItem.range ?? '',
        load: existingItem.load ?? 0,
        damage_dice: existingItem.damage_dice ?? '',
        impale: existingItem.impale ?? false,
        set_charge: existingItem.set_charge ?? false,
        poison_type: existingItem.poison_type ?? null,
        healing_type: existingItem.healing_type ?? null,
        pot: existingItem.pot ?? 0,
        effect: existingItem.effect ?? '',
        delay: existingItem.delay ?? '',
        duration: existingItem.duration ?? '',
        resist: existingItem.resist ?? '',
        recovers: existingItem.recovers ?? '',
        item_size: existingItem.item_size ?? null,
        container_size: existingItem.container_size ?? null,
        artefact_base_item: existingItem.artefact_base_item ?? null,
        worn: existingItem.worn ?? false,
        held: existingItem.held ?? false,
        equipped: existingItem.equipped ?? false,
        storage: existingItem.storage ?? false,
        attachable: existingItem.attachable ?? false,
        attach_point: existingItem.attach_point ?? false,
        runes: existingItem.runes ?? [],
        spells: existingItem.spells ?? [],
        max_unit_count: existingItem.max_unit_count ?? null,
        value_lower: existingItem.value_lower ?? 0,
        value_upper: existingItem.value_upper ?? 0,
        estimated_value:
          existingItem.estimated_value ?? staticItem?.estimated_value ?? false,
        description: existingItem.description ?? staticItem?.description ?? '',
        artefact_type: existingItem.artefact_type ?? '',
        trait: existingItem.trait ?? staticItem?.trait ?? null,
        material: existingItem.material ?? '',
        quality: existingItem.quality ?? '',
        age: existingItem.age ?? '',
        pwr_storage: initialArtefactPwrStorage,
        image: existingItem.image ?? null,
        countable: existingItem.countable ?? staticItem?.countable ?? false,
        unit_name: existingItem.unit_name ?? staticItem?.unit_name ?? null,
        artefact: existingItem.artefact ?? null,
        currency: existingItem.currency ?? null,
        race: existingItem.race ?? null,
        skills: existingItem.skills ?? [],
      }
      //-------------------------------------------------------
      // copy item to local item for v-model (fields changing)
      //-------------------------------------------------------
      this.local_artefact = copyObjectNoRef(existingItem) || {}
      this.local_artefact.pwr_storage = initialArtefactPwrStorage
      if (
        this.local_artefact.tally_qty === null ||
        this.local_artefact.tally_qty === undefined
      ) {
        this.local_artefact.tally_qty = 1
      }
      console.log('COPIED this.local_artefact = ', this.local_artefact)

      this.initialiseExistingStaticArtefact(staticItem, existingItem)

      // Keep original baseline aligned with one-time hydration normalisation
      // so unchanged fields are not emitted as PATCH deltas.
      this.original_artefact.currency = this.local_artefact.currency ?? null
      this.original_artefact.race = this.local_artefact.race ?? null

      // -----------------------------------------------------------------
      // existing armour item:
      // ensure armour-specific fields used by the ItemCard are populated
      // from static item so armour details & selectable locations
      // are available even when the item is currently unequipped

      if (this.resolvedItemType === 'Armour' && existingItem.name) {
        const staticItem = this.baseStaticItem
        const propertyItem = this.basePropertyItem

        if (this.local_artefact?.artefact_base_item) {
          this.applyArtefactBaseItemSelection(
            this.local_artefact.artefact_base_item,
            { preserveExisting: true },
          )
        }

        if (propertyItem?.armour) {
          // headline armour stats (static, not equip-state dependent)
          this.local_artefact.coverage =
            propertyItem.armour.coverage ?? this.local_artefact.coverage ?? ''
          // API uses dex_pen for static armour dex penalty
          if (
            this.local_artefact.dex_pen === null ||
            this.local_artefact.dex_pen === undefined
          ) {
            this.local_artefact.dex_pen = propertyItem.armour.dex_pen ?? 0
          }
          this.local_artefact.layer =
            propertyItem.armour.armour_material?.layer ??
            this.local_artefact.layer ??
            0
          const materialAp = propertyItem.armour.armour_material?.ap ?? 0
          this.max_ap = materialAp
          // capture baseline armour values once so dex_pen can be
          // recomputed proportionally when campaign-artefact AP changes
          if (!this.armour_base_ap) {
            this.armour_base_ap = materialAp
          }
          if (
            !this.armour_base_dex_pen &&
            typeof propertyItem.armour.dex_pen === 'number'
          ) {
            this.armour_base_dex_pen = propertyItem.armour.dex_pen
          }
          // Only fall back to material AP if the character item
          // doesn't already have its own AP value (null/undefined).
          // A value of 0 is a valid per-character AP (e.g. fully destroyed armour)
          // and should be preserved.
          if (
            this.local_artefact.ap === null ||
            this.local_artefact.ap === undefined
          ) {
            this.local_artefact.ap = materialAp
          }

          // existing artefacts - use campaign-artefact hp
          this.local_artefact.hp = this.local_artefact.hp ?? 0

          // set max hp for validation of HP changes against max HP based on material (for existing artefacts, assume backend has set this.item.hp to the correct current value)
          this.max_hp = propertyItem.armour.armour_material?.hp ?? 0

          // store full coverage_locations on the item for conflict checks
          if (!Array.isArray(this.local_artefact.coverage_locations)) {
            this.local_artefact.coverage_locations =
              propertyItem.armour.coverage_locations ?? []
          }

          // dropdown options for flexible (non-fixed) coverage locations,
          // excluding any locations already occupied by the same named
          // armour item on this character (except this item itself).
          const allCandidateLocs = Array.isArray(
            propertyItem.armour.coverage_locations,
          )
            ? propertyItem.armour.coverage_locations.filter(
                (loc) => loc && loc.fixed === false,
              )
            : []

          const currentItemId = this.local_artefact.id ?? this.item?.id ?? null
          const baseName =
            propertyItem.name || staticItem?.name || this.item?.name || ''

          this.armour_locations = allCandidateLocs.filter((loc) => {
            if (!loc || !loc.name) return false
            // find matching character armour location
            const cal = Array.isArray(this.relatedArmourLocations)
              ? this.relatedArmourLocations.find(
                  (c) => c && c.armour_location_name === loc.name,
                )
              : null

            if (!cal || !Array.isArray(cal.equipped_armour)) return true

            // is this location already used by the same named armour item
            // on this character (ignoring this item itself when editing)?
            const conflict = cal.equipped_armour.some((ea) => {
              if (!ea) return false
              if (currentItemId && ea.armour === currentItemId) return false
              return ea.armour_name === baseName
            })

            return !conflict
          })
        }

        // -----------------------------------------------------------------
        // existing weapon item
      } else if (
        this.resolvedItemType === 'Melee Weapon' ||
        this.resolvedItemType === 'Ranged Weapon'
      ) {
        const propertyItem = this.basePropertyItem
        const hasArtefactSkills =
          Array.isArray(this.local_artefact?.skills) &&
          this.local_artefact.skills.length > 0
        const resolvedWeaponFieldSource = propertyItem ?? this.local_artefact
        const resolvedWeaponSkills = hasArtefactSkills
          ? this.local_artefact.skills
          : (propertyItem?.skills ?? this.local_artefact.skills ?? [])
        const hadPersistedDamageDice =
          typeof this.local_artefact?.damage_dice === 'string'
            ? this.local_artefact.damage_dice.trim() !== ''
            : this.local_artefact?.damage_dice !== null &&
              this.local_artefact?.damage_dice !== undefined

        this.applyWeaponArtefactFieldsFromSource(resolvedWeaponFieldSource, {
          preserveExisting: true,
        })
        this.local_artefact.skills = resolvedWeaponSkills

        if (hadPersistedDamageDice) {
          this.hydrateWeaponSkillDamageDiceFromPersistedSummary()
        }

        if (!hadPersistedDamageDice) {
          this.syncWeaponDamageDiceSummary()
        }

        // Keep the existing-item baseline aligned with the hydrated weapon
        // values shown in the editor so legacy blank/null stored values do
        // not make the form appear dirty on open.
        this.original_artefact.ap = this.local_artefact.ap ?? 0
        this.original_artefact.hp = this.local_artefact.hp ?? 0
        this.original_artefact.damage_dice =
          this.local_artefact.damage_dice ?? ''

        // get cur str/dex chars
        this.local_artefact.str_cur =
          this.relatedCharacteristics.find((c) => c.name === 'str')?.cur ?? 0
        this.local_artefact.dex_cur =
          this.relatedCharacteristics.find((c) => c.name === 'dex')?.cur ?? 0

        // -----------------------------------------------------------------
        // existing healing item
      } else if (existingSubtype === 'Healing') {
        console.log('EXISTING HEALING ITEM = ', this.item.name)

        const getItemByName =
          this.$store.getters['characters/getStaticItemByName']
        const staticItem =
          this.basePropertyItem ||
          this.baseStaticItem ||
          (typeof getItemByName === 'function'
            ? getItemByName(this.item.name)
            : null)

        // from static item
        this.local_artefact.healing_type = staticItem?.healing_type ?? ''
        this.local_artefact.recovers = staticItem?.recovers ?? ''
        this.local_artefact.delay = staticItem?.delay ?? ''
        if (this.local_artefact.countable && !this.local_artefact.unit_name) {
          this.local_artefact.unit_name = staticItem?.unit_name ?? null
        }

        // -----------------------------------------------------------------
        // existing poison item
      } else if (existingSubtype === 'Poison') {
        console.log('EXISTING POISON ITEM = ', this.item.name)

        const getItemByName =
          this.$store.getters['characters/getStaticItemByName']
        const staticItem =
          this.basePropertyItem ||
          this.baseStaticItem ||
          (typeof getItemByName === 'function'
            ? getItemByName(this.item.name)
            : null)

        this.local_artefact.poison_type = staticItem?.poison_type ?? ''
        this.local_artefact.pot = staticItem?.pot ?? ''
        this.local_artefact.effect = staticItem?.effect ?? ''
        this.local_artefact.delay = staticItem?.delay ?? ''
        this.local_artefact.damage_dice = staticItem?.damage_dice ?? ''
        this.local_artefact.duration = staticItem?.duration ?? ''
        this.local_artefact.resist = staticItem?.resist ?? ''
        if (this.local_artefact.countable && !this.local_artefact.unit_name) {
          this.local_artefact.unit_name = staticItem?.unit_name ?? null
        }

        // -----------------------------------------------------------------
        // existing specialist item
      } else if (existingSubtype === 'Specialist') {
        console.log('EXISTING SPECIALIST ITEM = ', this.item.name)

        const getItemByName =
          this.$store.getters['characters/getStaticItemByName']
        const staticItem =
          this.basePropertyItem ||
          this.baseStaticItem ||
          (typeof getItemByName === 'function'
            ? getItemByName(this.item.name)
            : null)

        this.local_artefact.effect = staticItem?.effect ?? ''
        this.local_artefact.delay = staticItem?.delay ?? ''
        this.local_artefact.damage_dice = staticItem?.damage_dice ?? ''
        this.local_artefact.duration = staticItem?.duration ?? ''
        this.local_artefact.resist = staticItem?.resist ?? ''
        if (this.local_artefact.countable && !this.local_artefact.unit_name) {
          this.local_artefact.unit_name = staticItem?.unit_name ?? null
        }
      }

      this.original_artefact.ap = this.local_artefact.ap ?? 0
      this.original_artefact.hp = this.local_artefact.hp ?? 0
      this.original_artefact.damage_dice = this.local_artefact.damage_dice ?? ''
      this.original_artefact.skills = copyObjectNoRef(
        this.local_artefact.skills ?? [],
      )

      // -----------------------------------------------------------------
      // new item
    } else {
      this.local_artefact = setItemValues()
      console.log('NEW ITEM...')

      if (
        this.local_artefact.status === null ||
        this.local_artefact.status === undefined ||
        this.local_artefact.status === ''
      ) {
        this.local_artefact.status = this.getStatusNotApplicableValue()
      }

      if (
        this.local_artefact.tally_qty === null ||
        this.local_artefact.tally_qty === undefined
      ) {
        this.local_artefact.tally_qty = 1
      }

      this.previousContainerId = this.local_artefact.container ?? null

      this.local_artefact.campaign = true
      this.local_artefact.estimated_value = true

      // for new artefacts, seed currency from the default
      if (this.default_currency) {
        this.local_artefact.currency = this.default_currency.id ?? null
      }

      // for new weapon artefacts, seed current Str/Dex so that
      // weapon usability and mins-met UI have values
      if (
        this.resolvedItemType === 'Melee Weapon' ||
        this.resolvedItemType === 'Ranged Weapon'
      ) {
        const chars = this.relatedCharacteristics
        this.local_artefact.str_cur =
          chars.find((c) => c && c.name === 'str')?.cur ?? 0
        this.local_artefact.dex_cur =
          chars.find((c) => c && c.name === 'dex')?.cur ?? 0
      }

      // new poison artefacts start at potency 10 unless already seeded.
      if (
        this.resolvedItemType === 'Poison' &&
        (this.local_artefact.pot === null ||
          this.local_artefact.pot === undefined ||
          this.local_artefact.pot === '')
      ) {
        this.local_artefact.pot = 10
      }
    }

    // Render immediately, then hydrate dropdown lookups in the background.
    // This avoids blocking the whole panel on slow lookup endpoints.
    this.getDropDownValues()
      .catch((err) => {
        console.error('Failed to load artefact dropdown lookups', err)
      })
      .finally(() => {
        this.lookupsLoaded = true
      })
  },
  //========================================================
  // before unmount
  //----------------
  beforeUnmount() {
    this.clearPendingTimers()
  },
}
</script>

<style scoped lang="css">
.bb-artefact-card {
  height: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
}

.bb-artefact-card__body {
  flex: 1 1 auto;
  min-height: 0;
  align-content: flex-start;
}

.bbActionIcon {
  opacity: 0.5;
  vertical-align: middle;
  /* constrain to a square icon that scales with font size */
  width: 5rem;
  height: 5rem;
  object-fit: contain;
  display: inline-block;
}

.bb-item-warning {
  /* match the orange warning/cancel tone */
  background-color: #cc7a00 !important;
  color: white !important;
}

.bb-item-warning-label {
  color: white !important;
}

/* Allow the right-hand data column to shrink inside the
   Bootstrap row so long, no-wrap headings don't force
   the entire column to wrap below the left button column. */
.bb-tooltip-wrapper {
  min-width: 0;
}

/* Ensure long item headings stay on one line and
   truncate with an ellipsis instead of wrapping. */
.bb-hdg-lg.no-wrap {
  display: block;
  width: 100%;
  white-space: nowrap !important;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bb-item-tabs {
  gap: 0.1rem;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
}

.bb-item-tabs .nav-link {
  margin-right: 0 !important;
  padding: 0.9rem 1rem;
  white-space: nowrap;
}
</style>

<style scoped>
/* Currency select (id=currency) - keep short_name lowercase */
:deep(#currency .vs__search),
:deep(#currency .vs__dropdown-menu),
:deep(#currency .vs__dropdown-option),
:deep(#currency .vs__selected) {
  text-transform: none !important;
}
</style>
